import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ClipboardEvent, type ReactNode, type RefObject } from "react";
import { Link } from "wouter";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowLeft, Bot, Check, ChevronDown, ChevronLeft, ChevronUp, GripVertical, Maximize2, Minimize2, PinOff, Copy, ExternalLink, FileText, ListChecks, Loader2, LogIn, MessageSquareDot, MessageSquareText, Mic, Pencil, Square, Pin, Reply, RotateCcw, Search, SendHorizontal, SmilePlus, Tag, Trash2, X } from "lucide-react";
import { customFetch, getGetMeQueryKey } from "@workspace/api-client-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { CanalDaConversa, canalDoExterno, ListaPessoais, SeletorDoModo, telefoneDoJid, useExternos, type ConversaExterna } from "@/pages/admin/AtendimentoPessoais";
import { extractApiErrorMessage } from "@/lib/api-error";
import { useCorrecao } from "@/lib/correcao";
import { useDitadoNoCampo, type EstadoDoDitado } from "@/lib/ditado";
import { SortableList, type DragHandleProps } from "@/components/SortableList";
import { BotaoDeImagem, MiniaturasColadas, semLinhasDeImagem, TextoComImagens, useAnexosColados } from "@/components/AnexosColados";
import { Avatar, copiar, dia, hora, horaDaLista } from "@/components/admin/VerAgenteModal";

/**
 * Central de Atendimento (/atendimento, só o suporte): as conversas dos Números 1, 2 e 3 num lugar só, com envio de
 * texto, áudio gravado e imagem colada (Pedro, 30/10/2026: «juntar as conversas dos 3 números de atendimento em 1 local
 * só… enviar mensagens e gravar e enviar áudios, assim como no WhatsApp Web»). Visual e mecanismo do «Ver Agente». Os
 * dados vêm das rotas /api/admin/atendimento/* (role dev); tudo que sai daqui vai pelo número da conversa, e a
 * Atendente trata como gente (se cala na conversa).
 */

type Quem = "cliente" | "atendente" | "automacao" | "manual";

interface Conversa {
  jid: string;
  telefone: string;
  nome: string | null;
  loja: string | null;
  numero: { n: number; nome: string } | null;
  ultima: { quem: Quem; tipo: string | null; texto: string | null; em: string };
  nao_lidas: number;
  atendente_parada: boolean;
  /** Resposta da sessão «Atendente 2.0» que o Pedro ainda não abriu (o robozinho verde na lista). */
  assistente_nao_lido?: boolean;
  /** A conversa tem assunto com a «Atendente 2.0» (o robozinho cinza na lista). Sem o campo, conta como não. */
  tem_assunto?: boolean;
  /** O robozinho do card (Backend, 31/10/2026); null = sem ícone. Sem o campo (a API de antes), vale o de antes: verde com
   *  resposta não lida, cinza com assunto. */
  robo?: EstadoDoRobo | null;
  /** Ids das etiquetas do WhatsApp da conversa. */
  etiquetas?: string[];
  /** As lojas do dono do contato, na ordem de criação ([] sem loja). */
  lojas?: LojaDoContato[];
  /** Fixada no topo da lista da Central (só aqui, não no WhatsApp). */
  fixada?: boolean;
  /** A foto do contato (só nos WhatsApps pessoais). */
  imagem?: string | null;
}

/** Uma mensagem achada pela busca nas mensagens (GET /buscar-mensagens). */
interface Achada {
  jid: string;
  messageid: string;
  quem: Quem;
  /** O pedaço do texto (ou da transcrição) em volta do termo, com «…» onde foi cortado. */
  trecho: string;
  em: string;
  conversa: { nome: string; loja: string | null; numero: { n: number; nome: string } | null };
}

type EstadoDoRobo = "nao_lida" | "atrasado" | "andamento" | "fila" | "parado";
// As cores do robozinho do card (Pedro, 31/10/2026: «Gostei das suas sugestões, incluindo o vermelho»): fundo claro e o
// ícone na cor, nos dois temas, como o verde de antes. Os testids de antes ficam (o verde e o cinza).
const ROBO_DO_CARD: Record<EstadoDoRobo, { cor: string; titulo: string; testid: string }> = {
  nao_lida: { cor: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300", titulo: "Resposta não lida", testid: "atendimento-robo-nao-lido" },
  atrasado: { cor: "bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-300", titulo: "Sem resposta há mais de 30 min", testid: "atendimento-robo-atrasado" },
  andamento: { cor: "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-300", titulo: "A sessão está respondendo", testid: "atendimento-robo-andamento" },
  fila: { cor: "bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-300", titulo: "Na fila da sessão", testid: "atendimento-robo-fila" },
  parado: { cor: "bg-muted text-muted-foreground", titulo: "Parado", testid: "atendimento-robo-assunto" },
};
// A sessão já está com a pergunta: o azul de quem acabou de perguntar aqui dá lugar ao da lista.
const ROBO_COM_A_SESSAO: ReadonlySet<EstadoDoRobo | null | undefined> = new Set<EstadoDoRobo>(["fila", "andamento", "atrasado", "nao_lida"]);

interface LojaDoContato {
  id: string;
  nome: string;
  slug: string;
  tipo: "produtos" | "servicos" | "ambos" | null;
  /** gratis | profissional | ultra (ausente na API de antes de 31/10). */
  plano?: string;
}

// O plano da loja na conversa (Pedro, 31/10/2026, A-0047): olhando a conversa, ver se a Atendente devia ter usado ou
// oferecido um recurso do plano (ex.: o Pacote de escolhas do Profissional).
const PLANOS: Record<string, { nome: string; cor: string }> = {
  gratis: { nome: "Grátis", cor: "border-border bg-muted text-muted-foreground" },
  profissional: { nome: "Profissional", cor: "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300" },
  ultra: { nome: "Ultra", cor: "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300" },
};
const nomeDoPlano = (p: string) => PLANOS[p]?.nome ?? p;

// Um selo por plano. Dono com várias lojas: lojas do mesmo plano dividem o selo (o nome delas no title), e o selo
// mostra «2 lojas» quando há mais de um plano entre elas.
function SelosDoPlano({ lojas }: { lojas: LojaDoContato[] }) {
  const porPlano = new Map<string, string[]>();
  for (const l of lojas) if (l.plano) porPlano.set(l.plano, [...(porPlano.get(l.plano) ?? []), l.nome]);
  if (porPlano.size === 0) return null;
  const varios = porPlano.size > 1;
  return (
    <>
      {[...porPlano].map(([plano, nomes]) => (
        <span
          key={plano}
          title={lojas.length > 1 ? `${nomeDoPlano(plano)}: ${nomes.join(", ")}` : undefined}
          className={`shrink-0 whitespace-nowrap rounded-full border px-1.5 py-px text-[11px] font-semibold leading-4 ${PLANOS[plano]?.cor ?? PLANOS.gratis!.cor}`}
          data-testid={`atendimento-plano-${plano}`}
        >
          {nomeDoPlano(plano)}
          {varios && nomes.length > 0 ? ` · ${nomes.length === 1 ? "1 loja" : `${nomes.length} lojas`}` : ""}
        </span>
      ))}
    </>
  );
}

// As etiquetas do robozinho (Geral OZ, 30/10/2026, nos 3 números): a conversa em que o Pedro perguntou à Atendente 2.0
// fica com a 55 «RobôZinho»; o «Resolvido» do painel troca pela 56 «RobôZinho Resolveu».
const ETIQUETA_ROBOZINHO = "55";
const ETIQUETA_ROBOZINHO_RESOLVEU = "56";

/** Etiqueta mudada na Central que a lista ainda não mostra: `antes` é o que a lista mostrava; vale até ela mudar (ou 2 min). */
interface Mudanca {
  tem: boolean;
  antes: boolean;
  em: number;
}
const MUDANCA_MAX_MS = 120_000;
const etiquetasDe = (c: Conversa, ms: Record<string, Mudanca> | undefined): string[] => {
  const s = new Set(c.etiquetas ?? []);
  for (const [id, m] of Object.entries(ms ?? {})) if (m.tem) s.add(id);
  else s.delete(id);
  return [...s];
};
// Marcada como não lida aqui: o selo vale até a lista mostrar (ou 30 s).
const MARCADA_MAX_MS = 30_000;
// A barrinha de reações (Pedro, 30/10/2026, pela Geral OZ; os 5 últimos pedidos depois, «Tá ótimo»).
const REACOES = ["👍", "❤️", "😂", "😮", "😢", "🙏", "🥰", "🥳", "🎉", "🤝", "😊"];
// Apagar para todos: o WhatsApp deixa até uns 2 dias e meio depois do envio.
const APAGAR_ATE_MS = 60 * 3_600_000;

// O que fica neste aparelho para o recarregar não perder nada (Pedro, 30/10/2026): os rascunhos (o da conversa, o do
// «Perguntar» por mensagem e o do «Continuar…» por assunto), a conversa aberta, o painel do robozinho com o assunto e o
// «Perguntar» aberto. Sem armazenamento (aba anônima, bloqueado), vale só até recarregar.
const lerTexto = (chave: string): string => {
  try {
    return localStorage.getItem(chave) ?? "";
  } catch {
    return "";
  }
};
const guardarTexto = (chave: string, texto: string) => {
  try {
    if (texto.trim()) localStorage.setItem(chave, texto);
    else localStorage.removeItem(chave);
  } catch {
    // sem armazenamento
  }
};
const lerGuardado = <T,>(chave: string): T | null => {
  try {
    const v = localStorage.getItem(chave);
    return v ? (JSON.parse(v) as T) : null;
  } catch {
    return null;
  }
};
const guardar = (chave: string, valor: unknown) => {
  try {
    if (valor === null) localStorage.removeItem(chave);
    else localStorage.setItem(chave, JSON.stringify(valor));
  } catch {
    // sem armazenamento
  }
};
const CHAVE_DA_ABERTA = "central-aberta";
const CHAVE_DO_PAINEL = "central-painel";
const CHAVE_DA_PERGUNTA = "central-pergunta";
// A aba do TO DO aberta (e a tarefa aberta nela), na conversa em que está.
const CHAVE_DO_TODO = "central-todo";
// O botão do To Do no topo da página, com uma conversa aberta (computador): abre ou fecha a aba dessa conversa.
const EVENTO_DO_TODO = "central-alternar-todo";
// A aba da lista: os números do VnZ ou os WhatsApps pessoais (03/11/2026).
const CHAVE_DO_MODO = "central-modo";
const CHAVE_DO_TODO_EXPANDIDO = "central-todo-expandido";
// O rascunho da conversa (Pedro, 30/10/2026, como no WhatsApp): o texto digitado e não enviado, por conversa.
const chaveDoRascunho = (jid: string) => `central-rascunho:${jid}`;
const lerRascunho = (jid: string): string => lerTexto(chaveDoRascunho(jid));
const guardarRascunho = (jid: string, texto: string) => guardarTexto(chaveDoRascunho(jid), texto);
// A lista relê os rascunhos quando a conversa fecha ou o rascunho some.
const AVISO_DE_RASCUNHO = "central-rascunho";
// Responder citando (Pedro, 30/10/2026: «colocar a função de responder mensagem pelo painel»): a mensagem escolhida fica
// guardada junto com o rascunho da conversa, até sair a resposta ou cancelar. O WhatsApp só deixa responder às dos
// últimos 7 dias.
const chaveDaCitacao = (jid: string) => `central-rascunho-citacao:${jid}`;
const RESPONDER_ATE_MS = 7 * 24 * 3_600_000;

// Sem resposta do servidor (Pedro, 30/10/2026, depois de ver «Failed to fetch» no reinício da API: «não quero essa
// mensagem de erro»): as cargas automáticas falham em silêncio e tentam de novo na próxima vez; o que ele fez avisa com
// uma frase só, nunca a do navegador. A página de erro do nginx ou da Cloudflare (502/503/504/52x sem a mensagem da API)
// conta como sem resposta; o aviso da API (409, 503 com mensagem…) continua o dela.
const SEM_SERVIDOR = "Não consegui falar com o servidor. Tente de novo.";
const semResposta = (err: unknown): boolean => {
  if (err instanceof TypeError) return /fetch|network|load failed|internet/i.test(err.message);
  const e = err as { name?: unknown; status?: unknown; data?: { error?: { message?: unknown } } | null } | null;
  if (!e || typeof e !== "object") return false;
  if (e.name === "ResponseParseError") return true;
  const status = typeof e.status === "number" ? e.status : 0;
  return (status === 502 || status === 503 || status === 504 || (status >= 520 && status <= 527)) && typeof e.data?.error?.message !== "string";
};
const mensagemDoErro = (err: unknown, padrao: string) => (semResposta(err) ? SEM_SERVIDOR : extractApiErrorMessage(err, padrao));

/** Etiqueta do WhatsApp (as mesmas nos 3 números, pela sincronia), com quantas conversas da lista a têm. */
interface Etiqueta {
  id: string;
  nome: string;
  cor: string | null;
  conversas: number;
}

// As etiquetas dos 3 números: os chips do filtro e o dropdown da conversa. No mesmo passo da lista, para a contagem do
// chip não ficar atrás das conversas. Na conversa de um WhatsApp pessoal, as do número dela (da UAZAPI, de 30 em 30 s).
function useEtiquetas() {
  const canal = useContext(CanalDaConversa);
  return useQuery({
    queryKey: canal.etiquetas,
    queryFn: () => customFetch<Etiqueta[]>(`${canal.base}/etiquetas`),
    refetchInterval: canal.pessoal ? 30_000 : 10_000,
    retry: false,
  });
}

/** A respondida: achada, com quem escreveu e o conteúdo; não achada, `achada: false`. */
interface Citada {
  messageid: string;
  quem?: Quem;
  tipo?: string | null;
  texto?: string | null;
  transcricao?: string | null;
  achada?: false;
}

interface Mensagem {
  messageid: string;
  quem: Quem;
  tipo: string | null;
  texto: string | null;
  transcricao: string | null;
  /** O id da mensagem respondida: a página não mostra esse código em lugar nenhum. */
  citada: string | null;
  /** A respondida (Backend, 30/10/2026), para mostrar como no WhatsApp. */
  citacao?: Citada | null;
  reacao_a: string | null;
  apagada: boolean;
  em: string;
  midia: boolean;
  /** A execução do n8n que recebeu ou respondeu a mensagem (só a da Atendente e a do cliente). */
  execucao?: string | null;
  /** A última edição (Backend): a página mostra o texto dela no lugar do original, com «Editada». */
  editada?: { texto: string | null; transcricao?: string | null; em: string } | null;
  /** Nos WhatsApps pessoais a edição chega como mensagem à parte, com o id da original: vira o texto dela. */
  edita?: string | null;
  /** No grupo (só nos WhatsApps pessoais), quem escreveu. */
  autor?: string | null;
}

interface Enviada {
  ok: true;
  messageid: string | null;
}

// O que acabou de sair daqui e ainda não voltou pelo servidor (o eco chega pelo webhook, depois do «digitando»).
interface Pendente {
  id: string;
  messageid: string | null;
  // As mensagens que já estavam na conversa no envio: sem messageid, o eco é a primeira «manual» nova que bate.
  conhecidas: Set<string>;
  tipo: "texto" | "audio" | "imagem";
  texto: string | null;
  previa: string | null;
  duracaoMs: number | null;
  desde: number;
}

const LIMITE_LISTA = 50;
const LIMITE_MENSAGENS = 60;
const PENDENTE_MAX_MS = 90_000;

const COR_DO_NUMERO: Record<number, string> = { 1: "bg-orange-600", 2: "bg-sky-700", 3: "bg-purple-600" };
const ROTULO_DE_QUEM: Partial<Record<Quem, string>> = { atendente: "Atendente", automacao: "Automação", manual: "Você" };

const ehImagem = (tipo: string | null) => tipo === "ImageMessage" || tipo === "StickerMessage";
const rotuloDaMidia = (tipo: string | null): string | null =>
  tipo === "AudioMessage"
    ? "🎤 Áudio"
    : tipo === "ImageMessage"
      ? "📷 Foto"
      : tipo === "VideoMessage"
        ? "🎬 Vídeo"
        : tipo === "DocumentMessage"
          ? "📄 Documento"
          : tipo === "StickerMessage"
            ? "Figurinha"
            : null;

// Mensagem que o WhatsApp não conseguiu decifrar chega como «[Undecryptable] [text] Não foi possível d…» (Geral OZ,
// 30/10/2026: na prévia e no balão vira «Mensagem não recebida»).
const NAO_RECEBIDA = "Mensagem não recebida";
const naoRecebida = (t: string | null | undefined) => !!t && /^\s*\[undecryptable\]/i.test(t);

// A UAZAPI pode devolver «<telefone>:<id>»; na base da Atendente o messageid é só o id.
const idDaMensagem = (id: string | null | undefined) => (id ? (id.split(":").pop() ?? null) : null);

// O mesmo número com e sem o 9 do celular, como a API compara as conversas.
const chaveDoJid = (jid: string) => {
  const d = jid.split("@")[0]!.replace(/\D/g, "");
  return d.startsWith("55") && d.length === 13 && d[4] === "9" ? d.slice(0, 4) + d.slice(5) : d;
};

const tempo = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

function useAtrasado<T>(valor: T, ms: number): T {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

// Mídia só baixa quando o balão aparece na tela: a conversa pode ter dezenas de fotos e áudios.
function useVisivel<T extends Element>(): [RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [visivel, setVisivel] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || visivel) return;
    const io = new IntersectionObserver((e) => {
      if (e.some((x) => x.isIntersecting)) setVisivel(true);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [visivel]);
  return [ref, visivel];
}

function Selo({ n }: { n: number }) {
  return (
    <span className={`shrink-0 rounded px-1.5 py-px text-[10px] font-bold text-white ${COR_DO_NUMERO[n] ?? "bg-slate-500"}`} data-testid={`atendimento-selo-${n}`}>
      Nº {n}
    </span>
  );
}

// Contato sem nome salvo chega com o número no lugar do nome: aí vale o telefone formatado.
const nomeDe = (c: Conversa) => (c.nome && /\p{L}/u.test(c.nome) ? c.nome : null);
const tituloDa = (c: Conversa) => c.loja || nomeDe(c) || c.telefone;
// A conversa de um WhatsApp pessoal no formato da Central (sem loja, sem número do VnZ).
const conversaDoPessoal = (p: ConversaExterna): Conversa => ({
  jid: p.chatid,
  telefone: p.grupo ? "Grupo" : telefoneDoJid(p.chatid),
  nome: p.nome,
  loja: null,
  numero: null,
  ultima: { quem: "cliente", tipo: p.ultima.tipo || null, texto: p.ultima.texto || null, em: p.ultima.em ?? new Date(0).toISOString() },
  nao_lidas: p.naoLidas,
  atendente_parada: false,
  etiquetas: p.etiquetas,
  fixada: p.fixada,
  imagem: p.imagem,
});
const nada = () => {};

// Os filtros voltam ao recarregar (Pedro, 30/10/2026): o número, o «Não lidas» (com as conversas que ficam nele) e a
// etiqueta, neste aparelho. A busca não. Etiqueta guardada que sumiu do WhatsApp sai sozinha (o efeito do filtro de
// etiqueta, abaixo).
const CHAVE_DOS_FILTROS = "central-filtros";
// Passou disso, saem as mais antigas das que ficam no «Não lidas»: o localStorage tem uns 5 MB para a Central toda.
const FICAM_MAX = 500;
function filtrosGuardados(): { numero: number | null; naoLidas: boolean; etiqueta: string | null; ficam: Conversa[] } {
  try {
    const x = JSON.parse(localStorage.getItem(CHAVE_DOS_FILTROS) ?? "{}") as Record<string, unknown> | null;
    const naoLidas = x?.naoLidas === true;
    const ficam = x?.ficam;
    return {
      numero: x && [1, 2, 3].includes(x.numero as number) ? (x.numero as number) : null,
      naoLidas,
      etiqueta: typeof x?.etiqueta === "string" && x.etiqueta ? x.etiqueta : null,
      ficam: naoLidas && Array.isArray(ficam) ? (ficam as Conversa[]).filter((c) => typeof c?.jid === "string" && typeof c.ultima?.em === "string") : [],
    };
  } catch {
    return { numero: null, naoLidas: false, etiqueta: null, ficam: [] };
  }
}

// Junta (ou atualiza) conversas às que ficam no «Não lidas»; sem mudança, devolve o mesmo Map.
function juntarFicam(f: Map<string, Conversa>, cs: Conversa[]): Map<string, Conversa> {
  let n: Map<string, Conversa> | null = null;
  for (const c of cs) if (f.get(c.jid) !== c) (n ??= new Map(f)).set(c.jid, c);
  if (!n) return f;
  return n.size > FICAM_MAX ? new Map([...n].sort(([, a], [, b]) => (a.ultima.em < b.ultima.em ? 1 : -1)).slice(0, FICAM_MAX)) : n;
}

// O mesmo critério da busca do /conversas: nome, loja ou o número (com 4+ dígitos), sem diferenciar maiúscula.
const casaComBusca = (c: Conversa, busca: string) => {
  if (!busca) return true;
  const alvo = `${c.nome ?? ""} ${c.loja ?? ""} ${c.jid.split("@")[0]!.replace(/\D/g, "")}`.toLowerCase();
  const digitos = busca.replace(/\D/g, "");
  return alvo.includes(busca.toLowerCase()) || (digitos.length >= 4 && alvo.includes(digitos));
};

// ─── O voltar do celular na Central (Pedro, 31/10/2026, T-0027) ─────────────────────────────────────────────────────────
// «não permitir que o voltar do telefone volte da página /atendimento para a /results. Quando estou no robozinho o voltar
// quero que volte para a conversa aberta e o voltar numa conversa aberta volte para a lista.» Só no celular: cada camada
// aberta (conversa, robô, tarefas, pergunta) põe uma entrada no histórico, e o voltar fecha a de cima; sem camada, o
// voltar não sai da Central. As entradas têm a mesma URL e valem pela contagem: fechar pela tela tira UMA entrada,
// qualquer que seja; várias camadas fechando juntas viram um history.go(-n) só (dois back() seguidos não são confiáveis).
const naCentralDoCelular = () => !window.matchMedia("(min-width: 768px)").matches;
const camadasDaCentral: Array<{ fechar: () => void }> = [];
let popsParaIgnorar = 0;
let entradasParaTirar = 0;
let baseDaCentral = false;
function voltarNaCentral() {
  if (popsParaIgnorar > 0) {
    popsParaIgnorar--;
    return;
  }
  const topo = camadasDaCentral.pop();
  if (topo) {
    topo.fechar();
    return;
  }
  // Na lista: o voltar não sai para o /results — a entrada volta.
  if (baseDaCentral) window.history.pushState({ central: "base" }, "");
}
function tirarEntradaDoHistorico() {
  entradasParaTirar++;
  if (entradasParaTirar > 1) return;
  queueMicrotask(() => {
    const n = entradasParaTirar;
    entradasParaTirar = 0;
    popsParaIgnorar++;
    window.history.go(-n);
  });
}
function useCentralNaoSaiPeloVoltar() {
  useEffect(() => {
    if (!naCentralDoCelular()) return;
    baseDaCentral = true;
    window.history.pushState({ central: "base" }, "");
    window.addEventListener("popstate", voltarNaCentral);
    return () => {
      baseDaCentral = false;
      camadasDaCentral.length = 0;
      window.removeEventListener("popstate", voltarNaCentral);
    };
  }, []);
}
function useVoltarFecha(aberto: boolean, fechar: () => void) {
  const fecharRef = useRef(fechar);
  fecharRef.current = fechar;
  useEffect(() => {
    if (!aberto || !naCentralDoCelular()) return;
    const camada = { fechar: () => fecharRef.current() };
    camadasDaCentral.push(camada);
    window.history.pushState({ central: "camada" }, "");
    return () => {
      const i = camadasDaCentral.indexOf(camada);
      // Ainda na pilha = fechou pela tela (não pelo voltar): a entrada dela sai do histórico.
      if (i >= 0) {
        camadasDaCentral.splice(i, 1);
        tirarEntradaDoHistorico();
      }
    };
  }, [aberto]);
}

export default function Atendimento() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [buscaDigitada, setBuscaDigitada] = useState("");
  const busca = useAtrasado(buscaDigitada.trim(), 350);
  // Busca nas mensagens (Pedro, 30/10/2026: «quando eu selecionar ele a busca fica por mensagens momentaneamente. e o
  // ícone fica com uma cor mostrando aceso»): o balão dentro do campo liga e desliga; limpar a busca também desliga; não
  // fica guardada. Nela, a lista mostra as mensagens achadas em todas as conversas, sem os filtros.
  const [nasMensagens, setNasMensagens] = useState(false);
  const campoDaBusca = useRef<HTMLInputElement>(null);
  // O X do campo (Pedro, 31/10/2026: «colocar X para limpar busca»): o mesmo que apagar o texto todo (sai também da busca
  // nas mensagens); o foco fica no campo.
  const limparBusca = () => {
    setBuscaDigitada("");
    setNasMensagens(false);
    campoDaBusca.current?.focus();
  };
  const buscaDaLista = nasMensagens ? "" : busca;
  const termo = nasMensagens ? busca : "";
  const [guardados] = useState(filtrosGuardados);
  const [numero, setNumero] = useState<number | null>(guardados.numero);
  const [naoLidas, setNaoLidas] = useState(guardados.naoLidas);
  const [etiqueta, setEtiqueta] = useState<string | null>(guardados.etiqueta);
  // «Não lidas» (Pedro, 30/10/2026: «enquanto eu estiver com o filtro não lidas ligado as mensagens ficam lá»): toda
  // conversa que passou pela lista com ele ligado continua nela, já lida e sem o selo, na ordem da hora. O número, a
  // etiqueta e a busca só escolhem quais delas aparecem; saem todas ao desligar o «Não lidas» ou ao clicar em «Todos».
  const [ficam, setFicam] = useState<Map<string, Conversa>>(() => new Map(guardados.ficam.map((c) => [c.jid, c])));
  // Só entra a lista buscada depois de ligar o filtro (ou de abrir a página): a do cache pode ter a que já foi lida.
  const ficamDesde = useRef(Date.now());
  const mudarQueFica = (jid: string, f: (c: Conversa) => Conversa) =>
    setFicam((m) => {
      const c = m.get(jid);
      return c ? new Map(m).set(jid, f(c)) : m;
    });
  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_DOS_FILTROS, JSON.stringify({ numero, naoLidas, etiqueta, ficam: [...ficam.values()] }));
    } catch {
      // sem armazenamento: os filtros valem só até recarregar
    }
  }, [numero, naoLidas, etiqueta, ficam]);
  const [aberta, setAberta] = useState<Conversa | null>(null);
  // Etiquetas mudadas na Central (dropdown, «Resolvido», pergunta nova, chave «Atendente») que a lista ainda não mostra:
  // ela vem do webhook, segundos depois, e tem cache de 15 s. Guardadas por conversa na página: o card que perdeu a
  // etiqueta do filtro sai na hora e não volta ao abrir outra conversa; o número do chip acompanha.
  const [mudadas, setMudadas] = useState<Record<string, Record<string, Mudanca>>>({});
  // A conversa de cada mudança: no filtro de uma etiqueta que ela acabou de ganhar aqui, o card entra na hora.
  const mudadasDe = useRef(new Map<string, Conversa>());
  useEffect(() => {
    for (const jid of [...mudadasDe.current.keys()]) if (!mudadas[jid]) mudadasDe.current.delete(jid);
  }, [mudadas]);
  // Marcadas como não lidas aqui (o card mostra o selo e o «Não lidas» conta antes de a lista mostrar).
  const [marcadas, setMarcadas] = useState<Map<string, { c: Conversa; em: number }>>(() => new Map());
  // Resposta da 2.0 vista aqui (o /assuntos/:id/lido): o robozinho verde do card sai na hora, até a lista mostrar (ou 30 s).
  const [robosLidos, setRobosLidos] = useState<Map<string, number>>(() => new Map());
  // Perguntou ou continuou aqui: o card fica azul («Na fila da sessão») na hora, até a lista mostrar a sessão com a pergunta
  // (ou 30 s). Só com o `robo` na lista.
  const [robosNaFila, setRobosNaFila] = useState<Map<string, number>>(() => new Map());
  // Fixadas e desafixadas aqui: a lista se reordena na hora, até o servidor mostrar (ou 30 s).
  const [fixadasAqui, setFixadasAqui] = useState<Map<string, { fixa: boolean; em: number }>>(() => new Map());
  // Os rascunhos dos cards: relidos quando uma conversa fecha ou o rascunho dela some.
  const [, setVersaoDosRascunhos] = useState(0);
  useEffect(() => {
    const reler = () => setVersaoDosRascunhos((v) => v + 1);
    window.addEventListener(AVISO_DE_RASCUNHO, reler);
    return () => window.removeEventListener(AVISO_DE_RASCUNHO, reler);
  }, []);

  // Um filtro por etiqueta (Pedro, 30/10/2026), com a contagem de conversas; combina com o Nº e o Não lidas.
  const etiquetas = useEtiquetas();
  // Etiqueta apagada no WhatsApp enquanto estava escolhida: o filtro sai junto (senão fica preso, sem chip para tirar).
  useEffect(() => {
    if (etiqueta && etiquetas.data && !etiquetas.data.some((e) => e.id === etiqueta)) setEtiqueta(null);
  }, [etiqueta, etiquetas.data]);

  const lista = useInfiniteQuery({
    queryKey: ["atendimento-conversas", buscaDaLista, numero, naoLidas, etiqueta],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limite: String(LIMITE_LISTA) });
      if (buscaDaLista) p.set("busca", buscaDaLista);
      if (numero) p.set("numero", String(numero));
      if (naoLidas) p.set("nao_lidas", "1");
      if (etiqueta) p.set("etiqueta", etiqueta);
      if (pageParam) p.set("antes", pageParam);
      return customFetch<{ conversas: Conversa[]; proximo: string | null; nao_lidas_total?: number }>(`/api/admin/atendimento/conversas?${p}`);
    },
    getNextPageParam: (ultima) => ultima.proximo ?? undefined,
    refetchInterval: 10_000,
    retry: false,
  });
  const conversas = useMemo(() => {
    const vistas = new Set<string>();
    const todas: Conversa[] = [];
    for (const pagina of lista.data?.pages ?? []) {
      for (const c of pagina.conversas) {
        if (vistas.has(c.jid)) continue;
        vistas.add(c.jid);
        todas.push(c);
      }
    }
    return todas;
  }, [lista.data]);
  const achadas = useInfiniteQuery({
    queryKey: ["atendimento-buscar-mensagens", termo],
    enabled: termo.length >= 3,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ q: termo, limite: "30" });
      if (pageParam) p.set("antes", pageParam);
      return customFetch<{ resultados: Achada[]; proximo: string | null }>(`/api/admin/atendimento/buscar-mensagens?${p}`);
    },
    getNextPageParam: (ultima) => ultima.proximo ?? undefined,
    retry: false,
  });
  const resultados = useMemo(() => (achadas.data?.pages ?? []).flatMap((p) => p.resultados), [achadas.data]);
  // A conversa aberta acompanha a lista (contador, Atendente, etiquetas); fora do filtro, fica a última versão vista.
  const naLista = aberta ? conversas.find((c) => c.jid === aberta.jid) : undefined;
  useEffect(() => {
    if (naLista) setAberta(naLista);
  }, [naLista]);
  const atual = aberta ? (naLista ?? ficam.get(aberta.jid) ?? aberta) : null;
  useEffect(() => {
    if (naoLidas && lista.dataUpdatedAt >= ficamDesde.current) setFicam((f) => juntarFicam(f, conversas));
  }, [naoLidas, conversas, lista.dataUpdatedAt]);
  // Desligar o «Não lidas» ou clicar em «Todos» (mesmo já marcado) tira da lista as que ficaram.
  const soltarAsQueFicam = () => {
    setFicam(new Map());
    ficamDesde.current = Date.now();
  };
  // A mensagem em que a conversa abre, quando veio de um resultado da busca nas mensagens (`vez` repete a mesma).
  const [ancora, setAncora] = useState<{ jid: string; messageid: string; vez: number } | null>(null);
  // A aba de tarefas expandida (Pedro, 31/10/2026, T-0027: «expandir a tela de notas… cobrir a tela de mensagem aberta e
  // aparecer só a lista de mensagens»): fica guardada no aparelho (recarregar mantém); abrir uma conversa pela lista, pela
  // tarefa ou pela busca a devolve ao lado do chat (quem toca na conversa quer ver as mensagens).
  const [todoExpandido, setTodoExpandido] = useState(() => lerGuardado<boolean>(CHAVE_DO_TODO_EXPANDIDO) === true);
  useEffect(() => guardar(CHAVE_DO_TODO_EXPANDIDO, todoExpandido ? true : null), [todoExpandido]);
  // As tarefas pelo botão do topo, sem conversa aberta (Pedro, 01/11/2026, T-0027): no celular a aba ocupa a tela; no
  // computador, a coluna da conversa. Com uma conversa aberta, o botão do topo abre e fecha a aba dela.
  const [todoDaLista, setTodoDaLista] = useState<{ tarefa: string | null } | null>(null);
  const [todoNaConversa, setTodoNaConversa] = useState(false);
  const ehCelular = useIsMobile();
  // Os WhatsApps pessoais (Pedro, 03/11/2026): a aba «Pessoais», separada dos números do VnZ, só quando há algum.
  const pessoaisQ = useExternos();
  const externos = pessoaisQ.data?.externos ?? [];
  const [modo, setModo] = useState<"vnz" | "pessoais">(() => (lerGuardado<string>(CHAVE_DO_MODO) === "pessoais" ? "pessoais" : "vnz"));
  useEffect(() => guardar(CHAVE_DO_MODO, modo === "pessoais" ? "pessoais" : null), [modo]);
  const emPessoais = modo === "pessoais" && externos.length > 0;
  const [pessoal, setPessoal] = useState<ConversaExterna | null>(null);
  // A conversa pessoal aberta é a mesma da Central, falando pelo canal do número dela (Pedro, 04/11/2026: «copiar outras
  // funções… que já existem na Central»).
  const canalPessoal = useMemo(() => (pessoal ? canalDoExterno(externos, pessoal.externo) : null), [externos, pessoal?.externo]);
  const conversaPessoal = useMemo(() => (pessoal ? conversaDoPessoal(pessoal) : null), [pessoal]);
  const abrirPessoal = (c: ConversaExterna | null) => {
    setPessoal(c);
    if (c) setTodoDaLista(null);
  };
  // As etiquetas mudadas na conversa pessoal que a lista ainda não mostra (como as `mudadas` dos números do VnZ).
  const [mudadasPessoal, setMudadasPessoal] = useState<Record<string, Mudanca>>({});
  useEffect(() => setMudadasPessoal({}), [pessoal?.externo, pessoal?.chatid]);
  useEffect(() => {
    setMudadasPessoal((ms) => {
      const servidor = new Set(pessoal?.etiquetas ?? []);
      const valem = Object.entries(ms).filter(([id, m]) => Date.now() - m.em < MUDANCA_MAX_MS && servidor.has(id) === m.antes);
      return valem.length === Object.keys(ms).length ? ms : Object.fromEntries(valem);
    });
  }, [pessoal]);
  const mudarEtiquetasPessoal = (novas: Record<string, boolean>) =>
    setMudadasPessoal((ms) => {
      const servidor = new Set(pessoal?.etiquetas ?? []);
      const n = { ...ms };
      for (const [id, tem] of Object.entries(novas)) n[id] = { tem, antes: ms[id]?.antes ?? servidor.has(id), em: Date.now() };
      return n;
    });
  const abrir = (c: Conversa) => {
    setAberta(c);
    setAncora(null);
    setRestaurar(null);
    guardar(CHAVE_DA_ABERTA, c.jid);
    // Abrir marca como lida (no WhatsApp também).
    setMarcadas((ms) => {
      if (!ms.has(c.jid)) return ms;
      const n = new Map(ms);
      n.delete(c.jid);
      return n;
    });
  };
  // Aberta pela lista: no «Não lidas», fica nele (mesmo se a lista ainda não contou).
  const abrirDaLista = (c: Conversa) => {
    if (naoLidas) setFicam((f) => juntarFicam(f, [c]));
    setTodoExpandido(false);
    setTodoDaLista(null);
    abrir(c);
  };
  // A conversa pelo jid (com ou sem o 9): a da lista, a que ficou no «Não lidas», a aberta ou a do /conversas pelo número.
  const acharConversa = async (jid: string): Promise<Conversa | undefined> => {
    const k = chaveDoJid(jid);
    const c = conversas.find((x) => chaveDoJid(x.jid) === k) ?? [...ficam.values()].find((x) => chaveDoJid(x.jid) === k) ?? (aberta && chaveDoJid(aberta.jid) === k ? aberta : undefined);
    if (c) return c;
    try {
      const x = await customFetch<{ conversas: Conversa[] }>(`/api/admin/atendimento/conversas?${new URLSearchParams({ limite: "5", busca: jid.split("@")[0]! })}`);
      return x.conversas.find((y) => chaveDoJid(y.jid) === k);
    } catch {
      return undefined; // abre com o que se sabe
    }
  };
  // A conversa de uma tarefa do TO DO (clicada na aba).
  const abrirDaTarefa = async (t: Tarefa) => {
    if (!t.jid) return;
    setTodoExpandido(false);
    const c = await acharConversa(t.jid);
    abrir(
      c ?? {
        jid: t.jid,
        telefone: t.jid.split("@")[0]!,
        nome: t.conversa?.nome ?? null,
        loja: t.conversa?.loja ?? null,
        numero: t.conversa?.numero ?? null,
        ultima: { quem: "cliente", tipo: null, texto: null, em: t.criado_em },
        nao_lidas: 0,
        atendente_parada: false,
      },
    );
  };
  // Aberta por um resultado da busca nas mensagens: com os dados da lista (ou do /conversas pelo número) e rolada até a
  // mensagem achada.
  const abrirAchada = async (r: Achada) => {
    setTodoExpandido(false);
    const c = await acharConversa(r.jid);
    abrir(
      c ?? {
        jid: r.jid,
        telefone: r.jid.split("@")[0]!,
        nome: r.conversa.nome,
        loja: r.conversa.loja,
        numero: r.conversa.numero,
        ultima: { quem: r.quem, tipo: null, texto: r.trecho, em: r.em },
        nao_lidas: 0,
        atendente_parada: false,
      },
    );
    setAncora({ jid: c?.jid ?? r.jid, messageid: r.messageid, vez: Date.now() });
  };
  const fechar = () => {
    setAberta(null);
    setAncora(null);
    setRestaurar(null);
    guardar(CHAVE_DA_ABERTA, null);
  };
  useCentralNaoSaiPeloVoltar();
  useVoltarFecha(!!aberta, fechar);
  // O voltar do celular fecha as tarefas abertas pelo botão da lista.
  useVoltarFecha(!!todoDaLista, () => setTodoDaLista(null));
  useVoltarFecha(!!pessoal && emPessoais, () => setPessoal(null));
  // A conversa da aba que está na tela (no celular, ela esconde a lista).
  const algoAberto = emPessoais ? !!pessoal : !!aberta;
  // Com uma tarefa aberta, o voltar volta para a lista de tarefas (Pedro, 01/11/2026, T-0027: «está voltando para a lista
  // de mensagens e não para a lista das tarefas»); o seguinte fecha a aba. Vem depois da camada da aba: fica por cima.
  useVoltarFecha(!!todoDaLista?.tarefa, () => setTodoDaLista((x) => (x ? { tarefa: null } : x)));
  const tarefasDaLista = useTarefas(!!todoDaLista);
  const tarefaRespondidaNaLista = !!tarefasDaLista.data?.tarefas.some((t) => t.nao_lida);
  // Recarregou com uma conversa aberta: ela volta. Fora da 1ª página, busca pelo número e abre assim mesmo.
  const [restaurar, setRestaurar] = useState<string | null>(() => lerGuardado<string>(CHAVE_DA_ABERTA));
  useEffect(() => {
    if (!restaurar || aberta || !lista.data) return;
    const naPagina = conversas.find((x) => x.jid === restaurar);
    if (naPagina) {
      abrir(naPagina);
      return;
    }
    let vale = true;
    customFetch<{ conversas: Conversa[] }>(`/api/admin/atendimento/conversas?${new URLSearchParams({ limite: "5", busca: restaurar.split("@")[0] })}`)
      .then((r) => {
        const c = r.conversas.find((x) => x.jid === restaurar);
        if (vale && c) abrir(c);
      })
      .catch(() => {})
      .finally(() => {
        if (vale) setRestaurar(null);
      });
    return () => {
      vale = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurar, aberta, lista.data, conversas]);
  // Cada mudança de etiqueta vale até a lista mudar aquela etiqueta da conversa (fora da lista, até vencer).
  useEffect(() => {
    setMudadas((todas) => {
      const agora = Date.now();
      let mexeu = false;
      const ficam: typeof todas = {};
      for (const [jid, ms] of Object.entries(todas)) {
        const naLista = conversas.find((x) => x.jid === jid);
        const servidor = naLista ? new Set(naLista.etiquetas ?? []) : null;
        const valem = Object.entries(ms).filter(([id, m]) => agora - m.em < MUDANCA_MAX_MS && (!servidor || servidor.has(id) === m.antes));
        if (valem.length !== Object.keys(ms).length) mexeu = true;
        if (valem.length) ficam[jid] = Object.fromEntries(valem);
      }
      return mexeu ? ficam : todas;
    });
    setMarcadas((ms) => {
      const agora = Date.now();
      const ficam = [...ms].filter(([jid, x]) => agora - x.em < MARCADA_MAX_MS && !conversas.some((c) => c.jid === jid && c.nao_lidas > 0));
      return ficam.length === ms.size ? ms : new Map(ficam);
    });
    setRobosLidos((rs) => {
      const agora = Date.now();
      const ficam = [...rs].filter(([jid, em]) => agora - em < MARCADA_MAX_MS && conversas.some((c) => c.jid === jid && (c.assistente_nao_lido || c.robo === "nao_lida")));
      return ficam.length === rs.size ? rs : new Map(ficam);
    });
    setRobosNaFila((rs) => {
      const agora = Date.now();
      const ficam = [...rs].filter(([jid, em]) => agora - em < MARCADA_MAX_MS && !conversas.some((c) => c.jid === jid && ROBO_COM_A_SESSAO.has(c.robo)));
      return ficam.length === rs.size ? rs : new Map(ficam);
    });
    setFixadasAqui((fs) => {
      const agora = Date.now();
      const ficam = [...fs].filter(([jid, x]) => agora - x.em < MARCADA_MAX_MS && !conversas.some((c) => c.jid === jid && !!c.fixada === x.fixa));
      return ficam.length === fs.size ? fs : new Map(ficam);
    });
  }, [conversas]);
  const mudarEtiquetas = (c: Conversa, novas: Record<string, boolean>) => {
    mudadasDe.current.set(c.jid, c);
    setMudadas((todas) => {
      const servidor = new Set(c.etiquetas ?? []);
      const ms = { ...(todas[c.jid] ?? {}) };
      for (const [id, tem] of Object.entries(novas)) ms[id] = { tem, antes: ms[id]?.antes ?? servidor.has(id), em: Date.now() };
      return { ...todas, [c.jid]: ms };
    });
    // A que ficou no «Não lidas» já lida não volta na lista do servidor: a mudança vai nela, senão vence e desfaz.
    mudarQueFica(c.jid, (x) => {
      const s = new Set(x.etiquetas ?? []);
      for (const [id, tem] of Object.entries(novas)) (tem ? s.add(id) : s.delete(id));
      return { ...x, etiquetas: [...s] };
    });
  };
  // O robozinho do card: o `robo` da lista, com o que já se fez aqui (a resposta vista tira o verde; a pergunta nova põe o
  // azul); sem o campo, o de antes.
  const roboDoCard = (c: Conversa): EstadoDoRobo | null => {
    const lida = robosLidos.has(c.jid);
    if (c.robo === undefined) return c.assistente_nao_lido && !lida ? "nao_lida" : c.tem_assunto || c.assistente_nao_lido ? "parado" : null;
    let r = c.robo;
    if (lida && r === "nao_lida") r = "parado";
    if (robosNaFila.has(c.jid) && !ROBO_COM_A_SESSAO.has(r)) r = "fila";
    return r;
  };
  // O que a lista mostra: sem o card que perdeu aqui a etiqueta do filtro; no «Não lidas», com as que ficam (as lidas sem
  // o selo) e as marcadas como não lidas que a lista ainda não trouxe; a aberta sem selo, a marcada com.
  const exibidas = useMemo(() => {
    const perdeu = (jid: string) => !!etiqueta && mudadas[jid]?.[etiqueta]?.tem === false;
    let l = conversas.filter((c) => !perdeu(c.jid));
    const encaixar = (x: Conversa) => {
      if (l.some((c) => c.jid === x.jid) || perdeu(x.jid)) return;
      const i = l.findIndex((c) => c.ultima.em < x.ultima.em);
      l = i === -1 ? [...l, x] : [...l.slice(0, i), x, ...l.slice(i)];
    };
    // Fixadas no topo (o servidor já manda assim); a fixada aqui vai para o alto delas, a desafixada volta para a hora dela.
    const fixa = (c: Conversa) => fixadasAqui.get(c.jid)?.fixa ?? !!c.fixada;
    if (naoLidas) {
      for (const { c } of marcadas.values()) encaixar(c);
      // As que ficam e casam com o número, a etiqueta e a busca; fora da lista do servidor, já foram lidas. As mais antigas
      // que a última página carregada esperam a rolagem chegar à hora delas.
      const paginas = lista.data?.pages ?? [];
      const ate = paginas[paginas.length - 1]?.proximo ?? null;
      for (const c of ficam.values()) {
        const casa = (!numero || c.numero?.n === numero) && (!etiqueta || etiquetasDe(c, mudadas[c.jid]).includes(etiqueta)) && casaComBusca(c, buscaDaLista);
        if (casa && (fixa(c) || !ate || c.ultima.em >= ate)) encaixar({ ...c, nao_lidas: 0 });
      }
    } else if (etiqueta) {
      // Ganhou aqui a etiqueta do filtro (a lista do servidor só traz depois do webhook): entra na hora.
      for (const [jid, ms] of Object.entries(mudadas)) {
        const x = ms[etiqueta]?.tem === true ? (atual?.jid === jid ? atual : mudadasDe.current.get(jid)) : undefined;
        if (x && (!numero || x.numero?.n === numero) && casaComBusca(x, buscaDaLista)) encaixar(x);
      }
    }
    const novaFixa = (c: Conversa) => (fixadasAqui.get(c.jid)?.fixa === true && !c.fixada ? 1 : 0);
    const fixadas = l.filter(fixa).sort((a, b) => novaFixa(b) - novaFixa(a));
    const outras = l.filter((c) => !fixa(c)).sort((a, b) => (a.ultima.em < b.ultima.em ? 1 : a.ultima.em > b.ultima.em ? -1 : 0));
    return [...fixadas, ...outras].map((c) => ({
      ...c,
      fixada: fixa(c),
      nao_lidas: c.jid === atual?.jid ? 0 : marcadas.has(c.jid) && c.nao_lidas === 0 ? 1 : c.nao_lidas,
      assistente_nao_lido: robosLidos.has(c.jid) ? false : c.assistente_nao_lido,
      robo: roboDoCard(c),
    }));
  }, [conversas, etiqueta, mudadas, naoLidas, ficam, numero, buscaDaLista, lista.data, atual, marcadas, fixadasAqui, robosLidos, robosNaFila]);
  // O chip conta o que a Central mudou nas conversas que a lista ainda mostra do jeito antigo (e na que entrou aqui).
  const contagem = (e: Etiqueta) => {
    let n = e.conversas;
    for (const [jid, ms] of Object.entries(mudadas)) {
      const m = ms[e.id];
      if (m && m.tem !== m.antes && (conversas.some((c) => c.jid === jid) || exibidas.some((c) => c.jid === jid))) n += m.tem ? 1 : -1;
    }
    return Math.max(0, n);
  };
  // «Marcar como não lida» (Pedro, 30/10/2026): da conversa aberta, fecha a conversa, senão a abertura marcaria como lida
  // de novo (como no WhatsApp Web).
  // «Fixar conversa» (Pedro, 30/10/2026): só no topo da lista da Central, como o «Fixar conversa» do WhatsApp.
  const fixar = async (c: Conversa, fixa: boolean) => {
    try {
      await customFetch("/api/admin/atendimento/fixar", { method: "POST", body: JSON.stringify({ jid: c.jid, fixar: fixa }) });
      setFixadasAqui((fs) => new Map(fs).set(c.jid, { fixa, em: Date.now() }));
      mudarQueFica(c.jid, (x) => ({ ...x, fixada: fixa }));
      qc.invalidateQueries({ queryKey: ["atendimento-conversas"] });
    } catch (err) {
      toast({ title: mensagemDoErro(err, fixa ? "Não foi possível fixar a conversa." : "Não foi possível desafixar a conversa."), variant: "destructive" });
    }
  };
  const marcarNaoLida = async (c: Conversa) => {
    try {
      await customFetch("/api/admin/atendimento/nao-lida", { method: "POST", body: JSON.stringify({ jid: c.jid }) });
      setMarcadas((ms) => new Map(ms).set(c.jid, { c, em: Date.now() }));
      if (atual?.jid === c.jid) fechar();
      qc.invalidateQueries({ queryKey: ["atendimento-conversas"] });
    } catch (err) {
      toast({ title: mensagemDoErro(err, "Não foi possível marcar como não lida."), variant: "destructive" });
    }
  };
  // O contador do «Não lidas» (Pedro, 30/10/2026: «como tem nas etiquetas»): o total sem filtro vem em toda página do
  // /conversas. Guarda o último para o número não sumir enquanto a lista de outro filtro carrega.
  const totalNaoLidas = lista.data?.pages[0]?.nao_lidas_total;
  const [naoLidasTotal, setNaoLidasTotal] = useState<number | null>(null);
  useEffect(() => {
    if (typeof totalNaoLidas === "number") setNaoLidasTotal(totalNaoLidas);
  }, [totalNaoLidas]);

  // Rolagem infinita: o fim da lista à vista pede a próxima página (das conversas ou das mensagens achadas).
  const fimDaLista = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = nasMensagens ? achadas : lista;
  useEffect(() => {
    const el = fimDaLista.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => {
      if (e.some((x) => x.isIntersecting) && hasNextPage && !isFetchingNextPage) void fetchNextPage();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, conversas.length, resultados.length, nasMensagens]);

  // Com o painel da Atendente 2.0 aberto, quem encolhe é o chat do meio; abaixo de 70rem = 1120 px (lista 340 + chat 360 +
  // painel 420) a lista sai, senão o painel saía cortado da tela (Pedro, 30/10/2026).
  const [painelAberto, setPainelAberto] = useState(false);

  // A página ocupa a altura que sobra da tela: com a faixa «Visualizando a loja» em cima, o campo de mensagem saía da
  // tela no celular.
  const raiz = useRef<HTMLDivElement>(null);
  const [acima, setAcima] = useState(0);
  useLayoutEffect(() => {
    const medir = () => setAcima(Math.max(0, Math.round((raiz.current?.getBoundingClientRect().top ?? 0) + window.scrollY)));
    medir();
    window.addEventListener("resize", medir);
    return () => window.removeEventListener("resize", medir);
  }, []);

  useEffect(() => {
    const antes = document.title;
    document.title = "Atendimento";
    return () => {
      document.title = antes;
    };
  }, []);

  const chip = (ativo: boolean) =>
    `rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${ativo ? "border-foreground bg-foreground text-background" : "bg-background text-muted-foreground hover:bg-muted"}`;

  return (
    <div ref={raiz} className="flex flex-col bg-background" style={{ height: `calc(100svh - ${acima}px)` }} data-testid="atendimento">
      <div className="flex items-center gap-3 border-b px-4 py-3">
        {/* Sem o «←» para o /results (Pedro, 31/10/2026, T-0027). */}
        <h1 className="min-w-0 truncate text-base font-semibold">Atendimento · Números 1, 2 e 3</h1>
        {/* No canto direito do topo (Pedro, 01/11/2026, T-0027): no celular só na lista; no computador sempre. */}
        <button
          type="button"
          onClick={() => (aberta && !emPessoais ? window.dispatchEvent(new Event(EVENTO_DO_TODO)) : setTodoDaLista((x) => (x ? null : { tarefa: null })))}
          title="To Do"
          aria-label="To Do"
          aria-pressed={aberta && !emPessoais ? todoNaConversa : !!todoDaLista}
          className={`relative ml-auto h-8 w-8 shrink-0 items-center justify-center rounded-lg border hover:bg-muted ${algoAberto ? "hidden md:flex" : "flex"} ${(aberta && !emPessoais ? todoNaConversa : !!todoDaLista) ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground"}`}
          data-testid="atendimento-lista-todo"
        >
          <ListChecks className="h-4 w-4" />
          {tarefaRespondidaNaLista && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary" data-testid="atendimento-lista-todo-nao-lida" />}
        </button>
      </div>
      {/* Duas colunas que rolam sozinhas; no celular (< md), uma por vez. A coluna única é minmax(0,1fr): com auto, a linha
          das etiquetas (que não quebra) alargava a página no celular em vez de rolar sozinha. */}
      <div
        className={`grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)] ${painelAberto ? "min-[70rem]:grid-cols-[minmax(260px,340px)_minmax(0,1fr)]" : "md:grid-cols-[minmax(260px,340px)_minmax(0,1fr)]"}`}
      >
        <div className={`flex min-h-0 flex-col border-r ${algoAberto ? (!emPessoais && painelAberto ? "hidden min-[70rem]:flex" : "hidden md:flex") : "flex"}`}>
          {externos.length > 0 && (
            <div className="border-b px-3 py-2">
              <SeletorDoModo modo={emPessoais ? "pessoais" : "vnz"} onMudar={setModo} />
            </div>
          )}
          {emPessoais ? (
            <ListaPessoais
              externos={externos}
              aberta={pessoal}
              onAbrir={abrirPessoal}
              onAtualizar={setPessoal}
              rascunhoDe={(c) => lerRascunho(canalDoExterno(externos, c.externo).chave + c.chatid)}
            />
          ) : (
          <>
          <div className="space-y-2.5 border-b p-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={campoDaBusca}
                value={buscaDigitada}
                onChange={(e) => {
                  setBuscaDigitada(e.target.value);
                  if (!e.target.value) setNasMensagens(false);
                }}
                placeholder={nasMensagens ? "Buscar nas mensagens" : "Buscar por nome, loja ou telefone"}
                inputMode="search"
                className={`h-9 w-full rounded-lg border bg-background pl-8 text-sm outline-none focus:ring-2 focus:ring-ring ${buscaDigitada ? "pr-16" : "pr-10"}`}
                data-testid="atendimento-busca"
              />
              {buscaDigitada && (
                <button
                  type="button"
                  onClick={limparBusca}
                  aria-label="Limpar a busca"
                  title="Limpar a busca"
                  className="absolute right-9 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  data-testid="atendimento-busca-limpar"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setNasMensagens((v) => !v);
                  campoDaBusca.current?.focus();
                }}
                aria-pressed={nasMensagens}
                aria-label="Buscar nas mensagens"
                title={nasMensagens ? "Buscando nas mensagens (clique para buscar conversas)" : "Buscar nas mensagens"}
                className={`absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md transition-colors ${nasMensagens ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                data-testid="atendimento-busca-mensagens"
              >
                <MessageSquareText className="h-4 w-4" />
              </button>
            </div>
            {!!etiquetas.data?.length && (
              <div
                className={`-mx-3 flex gap-1.5 overflow-x-auto px-3 pb-1 hover-scrollbar ${nasMensagens ? "pointer-events-none opacity-40" : ""}`}
                role="group"
                aria-label="Filtrar por etiqueta"
                aria-disabled={nasMensagens || undefined}
                data-testid="atendimento-etiquetas"
              >
                {etiquetas.data.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap ${chip(etiqueta === e.id)}`}
                    aria-pressed={etiqueta === e.id}
                    onClick={() => setEtiqueta(etiqueta === e.id ? null : e.id)}
                    data-testid={`atendimento-etiqueta-${e.id}`}
                  >
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: e.cor ?? "currentColor" }} />
                    {e.nome}
                    <span className="opacity-70">{contagem(e)}</span>
                  </button>
                ))}
              </div>
            )}
            {/* Na busca nas mensagens os filtros não valem: ficam apagados. */}
            <div className={`flex flex-wrap gap-1.5 ${nasMensagens ? "pointer-events-none opacity-40" : ""}`} role="group" aria-label="Filtrar conversas" aria-disabled={nasMensagens || undefined}>
              <button
                type="button"
                className={chip(numero === null)}
                aria-pressed={numero === null}
                onClick={() => {
                  soltarAsQueFicam();
                  if (numero === null) void lista.refetch();
                  else setNumero(null);
                }}
                data-testid="atendimento-filtro-todos"
              >
                Todos
              </button>
              {[1, 2, 3].map((n) => (
                <button key={n} type="button" className={chip(numero === n)} aria-pressed={numero === n} onClick={() => setNumero(numero === n ? null : n)} data-testid={`atendimento-filtro-${n}`}>
                  Nº {n}
                </button>
              ))}
              <button type="button" className={`flex items-center gap-1.5 ${chip(naoLidas)}`} aria-pressed={naoLidas}
                onClick={() => {
                  soltarAsQueFicam();
                  setNaoLidas((v) => !v);
                }}
                data-testid="atendimento-filtro-nao-lidas"
              >
                Não lidas
                {naoLidasTotal !== null && <span className="opacity-70">{naoLidasTotal + marcadas.size}</span>}
              </button>
            </div>
          </div>
          {/* A lista se atualiza sozinha: falha dela não avisa (tenta de novo em 10 s). A busca nas mensagens ele pediu. */}
          {nasMensagens && achadas.isError && (
            <div className="border-b bg-destructive/10 px-4 py-2 text-sm text-destructive" role="alert">
              {mensagemDoErro(achadas.error, "Não foi possível buscar nas mensagens.")}
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto" data-testid="atendimento-lista">
            {nasMensagens ? (
              termo.length < 3 ? (
                <div className="p-6 text-center" data-testid="atendimento-achadas-dica">
                  <p className="text-sm font-medium">Buscar nas mensagens</p>
                  <p className="mt-1 text-xs text-muted-foreground">Digite pelo menos 3 letras. Vale para todas as conversas, sem os filtros.</p>
                </div>
              ) : achadas.isLoading ? (
                <p className="p-4 text-sm text-muted-foreground">Buscando…</p>
              ) : resultados.length === 0 ? (
                !achadas.isError && (
                  <div className="p-6 text-center" data-testid="atendimento-achadas-vazio">
                    <p className="text-sm font-medium">Nenhuma mensagem</p>
                    <p className="mt-1 text-xs text-muted-foreground">Nada com «{termo}» nas conversas.</p>
                  </div>
                )
              ) : (
                resultados.map((r) => (
                  <ResultadoDaBusca key={`${r.jid}:${r.messageid}`} r={r} termo={termo} ativa={ancora?.messageid === r.messageid && atual?.jid === r.jid} onClick={() => void abrirAchada(r)} />
                ))
              )
            ) : lista.isLoading || (lista.isError && !lista.data) ? (
              <p className="p-4 text-sm text-muted-foreground">Carregando…</p>
            ) : exibidas.length === 0 ? (
              <div className="p-6 text-center" data-testid="atendimento-vazio">
                <p className="text-sm font-medium">Nenhuma conversa</p>
                <p className="mt-1 text-xs text-muted-foreground">{buscaDaLista || numero || naoLidas || etiqueta ? "Nada com esse filtro." : "As conversas dos 3 números aparecem aqui."}</p>
              </div>
            ) : (
              exibidas.map((c) => (
                <ItemDaLista
                  key={c.jid}
                  c={c}
                  ativa={c.jid === atual?.jid}
                  rascunho={c.jid === atual?.jid ? "" : lerRascunho(c.jid)}
                  onClick={() => abrirDaLista(c)}
                  onNaoLida={() => void marcarNaoLida(c)}
                  onFixar={(fixa) => void fixar(c, fixa)}
                />
              ))
            )}
            <div ref={fimDaLista} className="h-6">
              {isFetchingNextPage && <Loader2 className="mx-auto h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
          </div>
          </>
          )}
        </div>
        <div className={`min-h-0 min-w-0 flex-col ${algoAberto ? "flex" : "hidden md:flex"}`}>
          {emPessoais && !(todoDaLista && !ehCelular) ? (
            pessoal ? (
              <CanalDaConversa.Provider value={canalPessoal!}>
                <ConversaAberta
                  key={`${pessoal.externo}:${pessoal.chatid}`}
                  c={conversaPessoal!}
                  mudadas={mudadasPessoal}
                  ancora={null}
                  onMudar={mudarEtiquetasPessoal}
                  onPerguntou={nada}
                  onRoboLido={nada}
                  onVoltar={() => setPessoal(null)}
                  onPainel={nada}
                  todoExpandido={false}
                  onTodoExpandido={nada}
                  onConversaDaTarefa={nada}
                  onTodo={nada}
                />
              </CanalDaConversa.Provider>
            ) : (
              <div className="m-auto p-6 text-center" data-testid="atendimento-pessoais-escolha">
                <p className="text-sm font-medium">Escolha uma conversa</p>
                <p className="mt-1 text-xs text-muted-foreground">A resposta sai pelo WhatsApp pessoal da conversa.</p>
              </div>
            )
          ) : atual && !emPessoais ? (
            <ConversaAberta
              key={atual.jid}
              c={atual}
              mudadas={mudadas[atual.jid]}
              ancora={ancora?.jid === atual.jid ? ancora : null}
              onMudar={(novas) => mudarEtiquetas(atual, novas)}
              onPerguntou={() => setRobosNaFila((rs) => new Map(rs).set(atual.jid, Date.now()))}
              onRoboLido={() => {
                setRobosLidos((rs) => new Map(rs).set(atual.jid, Date.now()));
                mudarQueFica(atual.jid, (x) => ({ ...x, assistente_nao_lido: false }));
              }}
              onVoltar={fechar}
              onPainel={setPainelAberto}
              todoExpandido={todoExpandido}
              onTodoExpandido={setTodoExpandido}
              onConversaDaTarefa={(t) => void abrirDaTarefa(t)}
              onTodo={setTodoNaConversa}
            />
          ) : todoDaLista && !ehCelular ? (
            <PainelDeTarefas
              jid={null}
              expandido
              onExpandir={() => {}}
              aberta={todoDaLista.tarefa}
              onAbrir={(tarefa) => setTodoDaLista({ tarefa })}
              onConversa={(t) => {
                if (!t.jid) return;
                // No computador a aba segue aberta, ao lado da conversa da tarefa (como na aba da conversa).
                guardar(CHAVE_DO_TODO, { jid: t.jid, tarefa: todoDaLista.tarefa });
                setTodoDaLista(null);
                setModo("vnz");
                void abrirDaTarefa(t);
              }}
              onFechar={() => setTodoDaLista(null)}
            />
          ) : (
            <div className="m-auto p-6 text-center" data-testid="atendimento-escolha">
              <p className="text-sm font-medium">Escolha uma conversa</p>
              <p className="mt-1 text-xs text-muted-foreground">A resposta sai pelo número da conversa.</p>
            </div>
          )}
        </div>
      </div>
      {todoDaLista && !algoAberto && ehCelular && (
        <div>
          <PainelDeTarefas
            jid={null}
            expandido={false}
            onExpandir={() => {}}
            aberta={todoDaLista.tarefa}
            onAbrir={(tarefa) => setTodoDaLista({ tarefa })}
            onConversa={(t) => {
              if (!t.jid) return;
              // Como na aba da conversa: a aba fecha para a conversa aparecer, e o robozinho não abre por cima dela.
              setTodoDaLista(null);
              setModo("vnz");
              guardar(CHAVE_DO_PAINEL, { jid: t.jid, aberto: false, assunto: null });
              void abrirDaTarefa(t);
            }}
            onFechar={() => setTodoDaLista(null)}
          />
        </div>
      )}
    </div>
  );
}

// A setinha do card (Pedro, 30/10/2026, como no WhatsApp Web): aparece ao passar o mouse (sempre, na tela de toque), com
// «Fixar conversa» e, na conversa já lida, «Marcar como não lida».
function ItemDaLista({
  c,
  ativa,
  rascunho,
  onClick,
  onNaoLida,
  onFixar,
}: {
  c: Conversa;
  ativa: boolean;
  /** O texto não enviado desta conversa (vazio = nenhum). */
  rascunho: string;
  onClick: () => void;
  onNaoLida: () => void;
  onFixar: (fixa: boolean) => void;
}) {
  const midia = rotuloDaMidia(c.ultima.tipo);
  const corpo = naoRecebida(c.ultima.texto) ? NAO_RECEBIDA : [midia, c.ultima.texto].filter(Boolean).join(" · ");
  const quem = ROTULO_DE_QUEM[c.ultima.quem];
  const tel = c.jid.split("@")[0];
  const lida = c.nao_lidas === 0;
  return (
    <div className="group relative">
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 border-b px-3 py-2.5 text-left hover:bg-muted/60 ${ativa ? "bg-muted" : ""}`}
      data-testid={`atendimento-conversa-${tel}`}
    >
      <Avatar nome={c.loja || nomeDe(c)} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="min-w-0 truncate text-sm font-medium">{tituloDa(c)}</span>
          {c.numero && <Selo n={c.numero.n} />}
          {c.fixada && <Pin className="ml-auto h-3 w-3 shrink-0 rotate-45 text-muted-foreground" aria-label="Fixada" data-testid="atendimento-fixada" />}
          <span className={`${c.fixada ? "" : "ml-auto "}shrink-0 pl-1 text-[11px] text-muted-foreground`}>{horaDaLista(c.ultima.em)}</span>
        </div>
        <div className="flex items-center gap-2 group-hover:pr-7 [@media(hover:none)]:pr-7">
          {rascunho ? (
            <span className="min-w-0 flex-1 truncate text-xs text-green-700 dark:text-green-400" data-testid="atendimento-rascunho">
              <span className="font-semibold">Rascunho:</span> {rascunho.replace(/\s+/g, " ")}
            </span>
          ) : (
            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
              {quem ? `${quem}: ` : ""}
              {corpo}
            </span>
          )}
          {/* O robozinho (Pedro, 30/10 e 31/10/2026): verde com resposta não lida, vermelho com mais de 30 min sem resposta,
              amarelo respondendo, azul na fila, cinza parado. */}
          {c.robo && (
            <span
              className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full ${ROBO_DO_CARD[c.robo].cor}`}
              title={ROBO_DO_CARD[c.robo].titulo}
              aria-label={ROBO_DO_CARD[c.robo].titulo}
              data-testid={ROBO_DO_CARD[c.robo].testid}
            >
              <Bot className="h-3 w-3" />
            </span>
          )}
          {c.nao_lidas > 0 && (
            <span className="flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground" data-testid="atendimento-nao-lidas">
              {c.nao_lidas}
            </span>
          )}
        </div>
      </div>
    </button>
    <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            title="Mais opções"
            aria-label="Mais opções da conversa"
            className="absolute bottom-2 right-2 flex h-6 w-6 items-center justify-center rounded-md border bg-background text-muted-foreground opacity-0 shadow-sm hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100"
            data-testid={`atendimento-conversa-menu-${tel}`}
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" collisionPadding={16}>
          <DropdownMenuItem onSelect={() => onFixar(!c.fixada)} data-testid="atendimento-fixar">
            {c.fixada ? "Desafixar conversa" : "Fixar conversa"}
          </DropdownMenuItem>
          {lida && (
            <DropdownMenuItem onSelect={onNaoLida} data-testid="atendimento-marcar-nao-lida">
              Marcar como não lida
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// A data e a hora de um resultado da busca: só a hora se for de hoje; o ano só se for de outro.
const dataEHora = (iso: string) => {
  const d = new Date(iso);
  const hoje = new Date();
  if (d.toDateString() === hoje.toDateString()) return hora(iso);
  return `${d.getFullYear() === hoje.getFullYear() ? dia(iso).slice(0, 5) : dia(iso)} ${hora(iso)}`;
};

// Minúscula e sem acento, letra a letra (o mesmo tamanho do texto, para o destaque cair no lugar certo).
const comoNaBusca = (s: string) => {
  let r = "";
  for (let i = 0; i < s.length; i++) r += s[i]!.normalize("NFD")[0]!.toLowerCase()[0]!;
  return r;
};

// O termo em destaque no trecho, sem diferenciar maiúscula nem acento («cardapio» acha «Cardápio»), como a busca acha.
function Destaque({ texto, termo }: { texto: string; termo: string }) {
  const t = comoNaBusca(termo);
  if (!t) return <>{texto}</>;
  const base = comoNaBusca(texto);
  const partes: ReactNode[] = [];
  let i = 0;
  for (let j = base.indexOf(t); j >= 0; j = base.indexOf(t, i)) {
    if (j > i) partes.push(texto.slice(i, j));
    partes.push(
      <mark key={j} className="rounded-sm bg-amber-200 px-0.5 text-foreground dark:bg-amber-400/30" data-testid="atendimento-achada-termo">
        {texto.slice(j, j + t.length)}
      </mark>,
    );
    i = j + t.length;
  }
  partes.push(texto.slice(i));
  return <>{partes}</>;
}

// Um resultado da busca nas mensagens: como o card da conversa, com o trecho achado no lugar da última mensagem.
function ResultadoDaBusca({ r, termo, ativa, onClick }: { r: Achada; termo: string; ativa: boolean; onClick: () => void }) {
  const nome = r.conversa.nome && /\p{L}/u.test(r.conversa.nome) ? r.conversa.nome : null;
  const quem = ROTULO_DE_QUEM[r.quem];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start gap-3 border-b px-3 py-2.5 text-left hover:bg-muted/60 ${ativa ? "bg-muted" : ""}`}
      data-testid={`atendimento-achada-${r.messageid}`}
    >
      <Avatar nome={r.conversa.loja || nome} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="min-w-0 truncate text-sm font-medium">{r.conversa.loja || nome || r.conversa.nome}</span>
          {r.conversa.numero && <Selo n={r.conversa.numero.n} />}
          <span className="ml-auto shrink-0 pl-1 text-[11px] text-muted-foreground">{dataEHora(r.em)}</span>
        </div>
        <p className="line-clamp-2 break-words text-xs text-muted-foreground">
          {quem ? `${quem}: ` : ""}
          <Destaque texto={r.trecho} termo={termo} />
        </p>
      </div>
    </button>
  );
}

function ConversaAberta({
  c,
  mudadas,
  ancora,
  onMudar,
  onPerguntou,
  onRoboLido,
  onVoltar,
  onPainel,
  todoExpandido,
  onTodoExpandido,
  onConversaDaTarefa,
  onTodo,
}: {
  c: Conversa;
  mudadas: Record<string, Mudanca> | undefined;
  /** Aberta por um resultado da busca nas mensagens: a mensagem em que a conversa abre. */
  ancora: { messageid: string; vez: number } | null;
  onMudar: (novas: Record<string, boolean>) => void;
  /** Perguntou (ou continuou) à 2.0 daqui: o card fica azul na hora. */
  onPerguntou: () => void;
  /** A resposta da 2.0 foi vista no painel: o card tira o robozinho verde na hora. */
  onRoboLido: () => void;
  onVoltar: () => void;
  onPainel: (aberto: boolean) => void;
  /** A aba de tarefas cobrindo o chat (só tablet e computador). */
  todoExpandido: boolean;
  onTodoExpandido: (expandido: boolean) => void;
  /** Clicou na conversa (outra) de uma tarefa do TO DO. */
  onConversaDaTarefa: (t: Tarefa) => void;
  /** A aba de tarefas desta conversa abriu ou fechou (o botão do topo da página acende junto). */
  onTodo: (aberto: boolean) => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const jid = c.jid;
  // Por onde a conversa fala: os números do VnZ, ou um WhatsApp pessoal (Pedro, 04/11/2026), sem o que é do VnZ
  // (Atendente, robozinho, To Do, loja, referências para as sessões).
  const canal = useContext(CanalDaConversa);
  const pessoal = canal.pessoal;
  // O que fica guardado no aparelho por conversa (citação, consultas): a do número pessoal não se mistura com a do VnZ.
  const guardada = canal.chave + jid;
  const rolagem = useRef<HTMLDivElement>(null);
  const [vistas, setVistas] = useState<Map<string, Mensagem>>(() => new Map());
  // `proximo` da página mais antiga carregada: undefined = ainda não carregou nada; null = não há mais antigas.
  const [maisAntigas, setMaisAntigas] = useState<string | null | undefined>(undefined);
  const [carregandoAntigas, setCarregandoAntigas] = useState(false);
  const [pendentes, setPendentes] = useState<Pendente[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [atendenteLigada, setAtendenteLigada] = useState(!c.atendente_parada);
  // O robozinho: o popup da pergunta sobre uma mensagem e o painel dos assuntos. Conversa com assunto da 2.0 abre com o
  // painel aberto (Pedro, 30/10/2026: «eu fecho se eu precisar»); o X fecha só nesta visita. Com resposta não lida, o
  // painel abre nela.
  const [perguntaDe, setPerguntaDe] = useState<Mensagem | null>(null);
  // O TO DO (Pedro, 31/10/2026): a aba fica no lugar da do robozinho, uma das duas por vez. Volta ao recarregar nesta
  // conversa; no computador, segue aberta ao ir para a conversa de uma tarefa.
  const [todo, setTodo] = useState<{ tarefa: string | null } | null>(() => {
    if (pessoal) return null;
    const g = lerGuardado<{ jid: string; tarefa: string | null }>(CHAVE_DO_TODO);
    return g && chaveDoJid(g.jid) === chaveDoJid(c.jid) ? { tarefa: g.tarefa } : null;
  });
  useEffect(() => {
    if (pessoal) return;
    guardar(CHAVE_DO_TODO, todo ? { jid, tarefa: todo.tarefa } : null);
  }, [jid, todo, pessoal]);
  // O ponto do botão: alguma tarefa com resposta não lida.
  const tarefas = useTarefas(!!todo);
  const tarefaRespondida = !!tarefas.data?.tarefas.some((t) => t.nao_lida);
  const irParaConversaDaTarefa = (t: Tarefa) => {
    if (!t.jid) return;
    // Expandida, a aba recolhe: a conversa da tarefa (mesmo a já aberta) aparece ao lado.
    onTodoExpandido(false);
    const noCelular = !window.matchMedia("(min-width: 768px)").matches;
    if (noCelular) {
      // No celular a aba fecha para a conversa aparecer, e o robozinho não abre por cima dela.
      setTodo(null);
      guardar(CHAVE_DO_PAINEL, { jid: t.jid, aberto: false, assunto: null });
    }
    if (chaveDoJid(t.jid) === chaveDoJid(jid)) return;
    guardar(CHAVE_DO_TODO, noCelular ? null : { jid: t.jid, tarefa: todo?.tarefa ?? null });
    onConversaDaTarefa(t);
  };
  // Recarregou nesta conversa: o painel volta como estava (aberto no assunto, ou fechado); senão, a regra de sempre.
  const [painel, setPainel] = useState<{ assunto: string | null } | null>(() => {
    if (todo || pessoal) return null;
    const g = lerGuardado<{ jid: string; aberto: boolean; assunto: string | null }>(CHAVE_DO_PAINEL);
    if (g && chaveDoJid(g.jid) === chaveDoJid(c.jid)) return g.aberto ? { assunto: g.assunto } : null;
    // No celular o painel não abre sozinho ao entrar na conversa (Pedro, 31/10/2026): fica no ícone do robozinho.
    if (!window.matchMedia("(min-width: 768px)").matches) return null;
    return c.tem_assunto || c.assistente_nao_lido ? { assunto: null } : null;
  });
  // O botão do topo da página (computador) faz o mesmo que o da conversa; e acende quando a aba está aberta.
  const todoAberto = !!todo;
  useEffect(() => {
    onTodo(todoAberto);
    return () => onTodo(false);
  }, [todoAberto, onTodo]);
  useEffect(() => {
    if (pessoal) return;
    const alternar = () => {
      setTodo((x) => (x ? null : { tarefa: null }));
      setPainel(null);
    };
    window.addEventListener(EVENTO_DO_TODO, alternar);
    return () => window.removeEventListener(EVENTO_DO_TODO, alternar);
  }, [pessoal]);
  // O voltar do celular: no robô, nas tarefas ou na pergunta, volta para a conversa (T-0027).
  useVoltarFecha(!!painel, () => setPainel(null));
  useVoltarFecha(!!todo, () => setTodo(null));
  useVoltarFecha(!!todo?.tarefa, () => setTodo((x) => (x ? { tarefa: null } : x)));
  useVoltarFecha(!!perguntaDe, () => abrirPerguntaRef.current(null));
  const [assuntoAberto, setAssuntoAberto] = useState<string | null>(painel?.assunto ?? null);
  useEffect(() => {
    if (pessoal) return;
    guardar(CHAVE_DO_PAINEL, { jid, aberto: !!painel, assunto: painel ? assuntoAberto : null });
  }, [jid, painel, assuntoAberto, pessoal]);
  // O «Perguntar» aberto volta ao recarregar, na mesma mensagem (assim que ela carregar).
  const abrirPerguntaRef = useRef<(m: Mensagem | null) => void>(() => {});
  const abrirPergunta = (m: Mensagem | null) => {
    setPerguntaDe(m);
    guardar(CHAVE_DA_PERGUNTA, m ? { jid, messageid: m.messageid } : null);
  };
  abrirPerguntaRef.current = abrirPergunta;
  const perguntaGuardada = useRef(pessoal ? null : lerGuardado<{ jid: string; messageid: string }>(CHAVE_DA_PERGUNTA));
  // Etiquetas mudadas aqui (dropdown, «Resolvido», pergunta nova, chave «Atendente») que a lista ainda não mostra: quem
  // guarda é a página (por conversa), para o filtro da lista valer na hora. Uma a uma, e não pela resposta do POST: a
  // resposta parte do que a lista sabe, e duas seguidas apagariam a primeira.
  const etiquetas = useMemo(() => etiquetasDe(c, mudadas), [c, mudadas]);
  const mudar = onMudar;
  // O dropdown de etiquetas (Pedro, 30/10/2026: «escolher a etiqueta que vou inserir ou retirar da conversa»): clicar
  // alterna; fica aberto para várias seguidas; a que está indo mostra «…».
  const todasAsEtiquetas = useEtiquetas().data ?? [];
  const [indo, setIndo] = useState<Set<string>>(() => new Set());
  const alternarEtiqueta = async (e: Etiqueta, tem: boolean) => {
    setIndo((s) => new Set(s).add(e.id));
    setErro(null);
    try {
      await customFetch(`${canal.base}/etiqueta`, { method: "POST", body: JSON.stringify({ jid, labelid: e.id, acao: tem ? "tirar" : "por" }) });
      mudar({ [e.id]: !tem });
      qc.invalidateQueries({ queryKey: canal.lista });
      qc.invalidateQueries({ queryKey: canal.etiquetas });
    } catch (err) {
      setErro(mensagemDoErro(err, "Não foi possível mudar a etiqueta."));
    } finally {
      setIndo((s) => {
        const n = new Set(s);
        n.delete(e.id);
        return n;
      });
    }
  };
  const robozinho = etiquetas.includes(ETIQUETA_ROBOZINHO) ? "pendente" : etiquetas.includes(ETIQUETA_ROBOZINHO_RESOLVEU) ? "resolvido" : null;
  // A conversa voltou ao RobôZinho (pergunta nova, ou «Continuar…» desde 30/10 ~23h50, com `etiquetada`): ganha a
  // «RobôZinho» e perde a «RobôZinho Resolveu» na hora; o «Resolvido» aparece e o filtro da lista acompanha.
  const voltouAoRobozinho = () => {
    mudar({ [ETIQUETA_ROBOZINHO]: true, [ETIQUETA_ROBOZINHO_RESOLVEU]: false });
    qc.invalidateQueries({ queryKey: ["atendimento-conversas"] });
    qc.invalidateQueries({ queryKey: ["atendimento-etiquetas"] });
  };
  // Expandida, a aba de tarefas cobre o chat: a lista de conversas volta a caber ao lado.
  const todoCheio = !!todo && todoExpandido;
  const comPainel = (!!painel || !!todo) && !todoCheio;
  useEffect(() => {
    onPainel(comPainel);
    return () => onPainel(false);
  }, [comPainel, onPainel]);
  const segurar = useRef<{ altura: number; topo: number } | null>(null);
  const topo = useRef<HTMLDivElement>(null);
  const noFim = useRef(true);
  const jaRolou = useRef(false);

  // Abrir marca como lida (no WhatsApp também).
  useEffect(() => {
    customFetch(`${canal.base}/lida`, { method: "POST", body: JSON.stringify({ jid }) })
      .then(() => qc.invalidateQueries({ queryKey: canal.lista }))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jid, qc, canal.base]);

  // Mensagem editada (Pedro, 30/10/2026): um balão só, com o texto da última edição em todo lugar (balão, citação,
  // «Perguntar»), como no WhatsApp; o «Editada» fica ao lado da hora. Nos WhatsApps pessoais a edição chega como
  // mensagem à parte (`edita` = a original): ela não vira balão, e o texto dela vai para a original; a que chega antes
  // da original fica guardada até ela carregar.
  const edicoes = useRef(new Map<string, { texto: string | null; em: string }>());
  const juntar = (lista: Mensagem[]) =>
    setVistas((antes) => {
      const m = new Map(antes);
      const editadas = new Set<string>();
      for (const x of lista) {
        if (!x.edita) continue;
        const e = edicoes.current.get(x.edita);
        if (!e || e.em <= x.em) edicoes.current.set(x.edita, { texto: x.texto, em: x.em });
        editadas.add(x.edita);
      }
      const comEdicao = (x: Mensagem): Mensagem => {
        const e = edicoes.current.get(x.messageid);
        if (e?.texto) return { ...x, texto: e.texto, editada: { texto: e.texto, em: e.em } };
        return x.editada?.texto ? { ...x, texto: x.editada.texto } : x;
      };
      for (const x of lista) if (!x.edita) m.set(x.messageid, comEdicao(x));
      for (const id of editadas) {
        const o = m.get(id);
        if (o) m.set(id, comEdicao(o));
      }
      return m;
    });

  // A página mais nova, de 4 em 4 s; o que já foi visto fica (a janela anda e as do meio não somem).
  const recentes = useQuery({
    queryKey: ["atendimento-mensagens", guardada],
    queryFn: () => customFetch<{ mensagens: Mensagem[]; proximo: string | null }>(`${canal.base}/mensagens?jid=${encodeURIComponent(jid)}&limite=${LIMITE_MENSAGENS}`),
    refetchInterval: 4_000,
    retry: false,
  });
  useEffect(() => {
    if (!recentes.data) return;
    const el = rolagem.current;
    noFim.current = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 150;
    juntar(recentes.data.mensagens);
    setMaisAntigas((v) => (v === undefined ? recentes.data!.proximo : v));
  }, [recentes.data]);

  // Falhou: o topo à vista não tenta de novo na hora (sem servidor, seria um aviso atrás do outro); rolar de novo tenta.
  const antigasFalhou = useRef(0);
  const carregarAntigas = async (limite = LIMITE_MENSAGENS) => {
    if (!maisAntigas || carregandoAntigas || Date.now() - antigasFalhou.current < 5_000) return;
    setCarregandoAntigas(true);
    try {
      const r = await customFetch<{ mensagens: Mensagem[]; proximo: string | null }>(
        `${canal.base}/mensagens?jid=${encodeURIComponent(jid)}&limite=${limite}&antes=${encodeURIComponent(maisAntigas)}`,
      );
      const el = rolagem.current;
      if (el) segurar.current = { altura: el.scrollHeight, topo: el.scrollTop };
      noFim.current = false;
      juntar(r.mensagens);
      setMaisAntigas(r.proximo);
    } catch (err) {
      antigasFalhou.current = Date.now();
      toast({ title: mensagemDoErro(err, "Não foi possível carregar as mensagens anteriores."), variant: "destructive" });
    } finally {
      setCarregandoAntigas(false);
    }
  };

  // Aberta por um resultado da busca nas mensagens (Pedro, 30/10/2026): a página vem da achada até a mais nova (`desde`),
  // a conversa rola até ela e ela pisca. Antes das 500 mais novas, as anteriores vêm (de 200 em 200) até chegar nela.
  const [alvo, setAlvo] = useState<{ messageid: string; pagina: boolean } | null>(null);
  const [piscando, setPiscando] = useState<string | null>(null);
  // Responder citando: a escolhida vai na faixa em cima do campo e no próximo envio (texto, áudio ou imagem).
  const [citando, setCitando] = useState<{ citada: Citada; em: string } | null>(() => {
    const g = lerGuardado<{ citada: Citada; em: string }>(chaveDaCitacao(guardada));
    return g?.citada?.messageid && Date.now() - Date.parse(g.em) < RESPONDER_ATE_MS ? g : null;
  });
  const citar = (x: { citada: Citada; em: string } | null) => {
    setCitando(x);
    guardar(chaveDaCitacao(guardada), x);
  };
  // A faixa da citação encolhe a conversa: quem estava no fim continua vendo o fim.
  useLayoutEffect(() => {
    const el = rolagem.current;
    if (el && noFim.current) el.scrollTop = el.scrollHeight;
  }, [citando]);
  useEffect(() => {
    if (!ancora) return;
    let vale = true;
    setAlvo({ messageid: ancora.messageid, pagina: false });
    customFetch<{ mensagens: Mensagem[]; proximo: string | null }>(
      `${canal.base}/mensagens?jid=${encodeURIComponent(jid)}&limite=${LIMITE_MENSAGENS}&desde=${encodeURIComponent(ancora.messageid)}`,
    )
      .then((r) => {
        if (!vale) return;
        juntar(r.mensagens);
        // A página vai da achada até a mais nova: as anteriores seguem da mais antiga das duas (null = não há mais).
        setMaisAntigas((v) => (v === undefined ? r.proximo : v === null || r.proximo === null ? null : r.proximo < v ? r.proximo : v));
        setAlvo((a) => (a?.messageid === ancora.messageid ? { ...a, pagina: true } : a));
      })
      .catch((err) => {
        if (!vale) return;
        setAlvo(null);
        setErro(mensagemDoErro(err, "Não foi possível abrir a conversa nessa mensagem."));
      });
    return () => {
      vale = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ancora?.vez]);
  useEffect(() => {
    if (!alvo?.pagina || vistas.has(alvo.messageid) || carregandoAntigas) return;
    if (maisAntigas) void carregarAntigas(200);
    else setAlvo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alvo, vistas, maisAntigas, carregandoAntigas]);
  useEffect(() => {
    if (!piscando) return;
    const t = setTimeout(() => setPiscando(null), 2500);
    return () => clearTimeout(t);
  }, [piscando]);

  const mensagens = useMemo(() => [...vistas.values()].sort((a, b) => (a.em < b.em ? -1 : a.em > b.em ? 1 : 0)), [vistas]);
  useEffect(() => {
    const g = perguntaGuardada.current;
    if (!g || g.jid !== jid || perguntaDe) return;
    const m = vistas.get(g.messageid);
    if (!m) return;
    perguntaGuardada.current = null;
    setPerguntaDe(m);
  }, [vistas, jid, perguntaDe]);
  // Reação vira emoji pequeno no balão de que ela fala; sem o balão carregado, fica sozinha no lugar dela.
  // De cada lado (o cliente e o nosso número) vale a última reação; vazia = tirou.
  const reacoes = useMemo(() => {
    const r = new Map<string, { cliente: Mensagem | null; nossa: Mensagem | null }>();
    for (const m of mensagens) {
      if (!m.reacao_a) continue;
      const x = r.get(m.reacao_a) ?? { cliente: null, nossa: null };
      const lado = m.quem === "cliente" ? "cliente" : "nossa";
      if (!x[lado] || x[lado]!.em <= m.em) x[lado] = m;
      r.set(m.reacao_a, x);
    }
    return r;
  }, [mensagens]);
  // Reagir (Pedro, 30/10/2026): a reação daqui aparece na hora e vale até o /mensagens trazer a dela (ou 1 min).
  const [minhas, setMinhas] = useState<Map<string, { emoji: string; em: number }>>(() => new Map());
  useEffect(() => {
    if (minhas.size === 0) return;
    const ficam = [...minhas].filter(([id, x]) => Date.now() - x.em < 60_000 && (reacoes.get(id)?.nossa?.texto ?? "") !== x.emoji);
    if (ficam.length !== minhas.size) setMinhas(new Map(ficam));
  }, [reacoes, minhas]);
  // Apagar para todos (Pedro, 30/10/2026): só as nossas, até ~60 h; o balão fica riscado na hora, como a Central mostra.
  const [apagadas, setApagadas] = useState<Set<string>>(() => new Set());
  const apagar = async (m: Mensagem): Promise<boolean> => {
    setErro(null);
    try {
      await customFetch(`${canal.base}/apagar`, { method: "POST", body: JSON.stringify({ jid, messageid: m.messageid }) });
      setApagadas((s) => new Set(s).add(m.messageid));
      void recentes.refetch();
      return true;
    } catch (err) {
      setErro(mensagemDoErro(err, "Não foi possível apagar a mensagem."));
      return false;
    }
  };
  const reagir = async (m: Mensagem, emoji: string) => {
    setErro(null);
    const antes = minhas.get(m.messageid);
    setMinhas((x) => new Map(x).set(m.messageid, { emoji, em: Date.now() }));
    try {
      await customFetch(`${canal.base}/reagir`, { method: "POST", body: JSON.stringify({ jid, messageid: m.messageid, emoji }) });
      void recentes.refetch();
    } catch (err) {
      setMinhas((x) => {
        const n = new Map(x);
        if (antes) n.set(m.messageid, antes);
        else n.delete(m.messageid);
        return n;
      });
      const naoAchou = (err as { status?: number } | null)?.status === 404;
      setErro(mensagemDoErro(err, naoAchou ? "Só dá para reagir a mensagens dos últimos 7 dias." : "Não foi possível reagir à mensagem."));
    }
  };

  // O que voltou pelo servidor sai dos «enviando…» (pelo messageid ou, sem ele, pela primeira «manual» nova com o mesmo
  // texto ou do mesmo tipo); o que passou do tempo também.
  useEffect(() => {
    setPendentes((ps) => {
      const agora = Date.now();
      const usadas = new Set<string>();
      const ficam = ps.filter((p) => {
        if (agora - p.desde >= PENDENTE_MAX_MS) return false;
        if (p.messageid) return !vistas.has(p.messageid);
        const eco = [...vistas.values()].find(
          (m) =>
            m.quem === "manual" &&
            !p.conhecidas.has(m.messageid) &&
            !usadas.has(m.messageid) &&
            (p.tipo === "texto"
              ? (m.texto ?? "").trim() === (p.texto ?? "").trim()
              : m.tipo === (p.tipo === "audio" ? "AudioMessage" : "ImageMessage")),
        );
        if (!eco) return true;
        usadas.add(eco.messageid);
        return false;
      });
      return ficam.length === ps.length ? ps : ficam;
    });
  }, [vistas]);
  useEffect(() => {
    if (pendentes.length === 0) return;
    const t = setInterval(() => setPendentes((ps) => ps.filter((p) => Date.now() - p.desde < PENDENTE_MAX_MS)), 5_000);
    return () => clearInterval(t);
  }, [pendentes.length]);

  // Ao abrir, desce ao fim (ou até a mensagem achada pela busca); mensagem nova desce só para quem está no fim; as
  // anteriores não mexem no que se lê.
  useLayoutEffect(() => {
    const el = rolagem.current;
    if (!el || mensagens.length === 0) return;
    if (segurar.current) {
      el.scrollTop = el.scrollHeight - segurar.current.altura + segurar.current.topo;
      segurar.current = null;
      if (!alvo) return;
    }
    if (alvo) {
      const b = vistas.has(alvo.messageid) ? el.querySelector<HTMLElement>(`[data-messageid="${CSS.escape(alvo.messageid)}"]`) : null;
      if (!b) return; // ainda vindo
      el.scrollTop += b.getBoundingClientRect().top - el.getBoundingClientRect().top - Math.max(0, (el.clientHeight - b.offsetHeight) / 2);
      jaRolou.current = true;
      noFim.current = false;
      setPiscando(alvo.messageid);
      setAlvo(null);
      return;
    }
    if (!jaRolou.current || noFim.current) {
      el.scrollTop = el.scrollHeight;
      jaRolou.current = true;
    }
  }, [mensagens, pendentes.length, alvo]);

  const aoRolar = () => {
    const el = rolagem.current;
    if (el) noFim.current = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  };
  // O topo à vista pede as anteriores: rolando para cima, ou de cara quando a conversa cabe na tela.
  useEffect(() => {
    const el = topo.current;
    if (!el || !maisAntigas || carregandoAntigas || mensagens.length === 0) return;
    const io = new IntersectionObserver((e) => {
      if (e.some((x) => x.isIntersecting) && jaRolou.current) void carregarAntigas();
    }, { root: rolagem.current });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maisAntigas, carregandoAntigas, mensagens.length]);

  const mudarAtendente = async (ligada: boolean) => {
    setAtendenteLigada(ligada);
    try {
      await customFetch("/api/admin/atendimento/atendente", { method: "POST", body: JSON.stringify({ jid, ligada }) });
      // A chave é a etiqueta PARAR ROBÔ na conversa: com o filtro dela, o card sai (ou volta) na hora.
      const parar = todasAsEtiquetas.find((e) => e.nome === "PARAR ROBÔ");
      if (parar) mudar({ [parar.id]: !ligada });
      qc.invalidateQueries({ queryKey: ["atendimento-conversas"] });
    } catch (err) {
      setAtendenteLigada(!ligada);
      toast({ title: mensagemDoErro(err, "Não foi possível mudar a Atendente nesta conversa."), variant: "destructive" });
    }
  };

  // Embaixo do título: o nome do contato (quando o título é a loja) e o telefone (quando o título não é ele).
  const detalhe = [c.loja && nomeDe(c) ? nomeDe(c) : null, tituloDa(c) === c.telefone ? null : c.telefone].filter(Boolean).join(" · ");

  const enviou = (p: Omit<Pendente, "id" | "desde" | "conhecidas">) => {
    setPendentes((ps) => [
      ...ps,
      { ...p, messageid: idDaMensagem(p.messageid), conhecidas: new Set(vistas.keys()), id: `${Date.now()}-${Math.random()}`, desde: Date.now() },
    ]);
    noFim.current = true;
    qc.invalidateQueries({ queryKey: canal.lista });
  };

  return (
    <div className="flex min-h-0 flex-1">
      {/* @container: no chat estreito (celular, ou o painel da 2.0 aberto), os botões do topo descem para uma linha só
          deles, e o nome, a loja e o telefone ficam com a largura toda. */}
      <div className={`@container flex min-h-0 min-w-0 flex-1 flex-col ${todoCheio ? "md:hidden" : ""}`} data-testid="atendimento-chat" data-canal={pessoal ? "pessoal" : "vnz"}>
      <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-3 py-2.5 ${pessoal ? "bg-violet-50/70 dark:bg-violet-950/30" : ""}`}>
        <button type="button" onClick={onVoltar} className="-ml-1 rounded p-1 text-muted-foreground hover:text-foreground md:hidden" aria-label="Conversas" data-testid="atendimento-voltar">
          <ChevronLeft className="h-5 w-5" />
        </button>
        {pessoal && c.imagem ? <img src={c.imagem} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" /> : <Avatar nome={c.loja || nomeDe(c)} />}
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <div className="truncate text-sm font-semibold" data-testid="atendimento-titulo">
              {tituloDa(c)}
            </div>
            {!!c.lojas?.length && <SelosDoPlano lojas={c.lojas} />}
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            {detalhe && <span className="truncate">{detalhe}</span>}
            {c.numero && (
              <span className="flex shrink-0 items-center gap-1 whitespace-nowrap">
                {detalhe ? "· " : ""}conversa no <Selo n={c.numero.n} />
              </span>
            )}
            {pessoal && canal.rotulo && (
              <span className="flex min-w-0 items-center gap-1 whitespace-nowrap">
                {detalhe ? "· " : ""}conversa no
                <span className={`truncate rounded px-1.5 py-px text-[10px] font-bold ${canal.cor ?? ""}`} data-testid="atendimento-pessoal-numero">
                  {canal.rotulo}
                </span>
              </span>
            )}
          </div>
        </div>
        <div
          className={pessoal ? "flex shrink-0 items-center gap-2" : "flex w-full flex-wrap items-center justify-end gap-x-2 gap-y-2 @md:gap-x-3 @xl:w-auto"}
          data-testid="atendimento-topo-botoes"
        >
        {!pessoal && <CopiarConversa c={c} />}
        {!!c.lojas?.length && (
          <>
            <BotoesDaLoja lojas={c.lojas} onErro={setErro} />
            <span className="h-5 w-px shrink-0 bg-border" aria-hidden="true" data-testid="atendimento-loja-divisor" />
          </>
        )}
        {!pessoal && (
          <label className="flex shrink-0 items-center gap-2 text-sm" data-testid="atendimento-atendente">
            <Switch checked={atendenteLigada} onCheckedChange={(v) => void mudarAtendente(v)} aria-label="Atendente nesta conversa" />
            Atendente
          </label>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              title="Etiquetas"
              aria-label="Etiquetas da conversa"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border text-muted-foreground hover:bg-muted hover:text-foreground"
              data-testid="atendimento-etiquetas-conversa"
            >
              <Tag className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            collisionPadding={16}
            className="max-h-[min(26rem,var(--radix-dropdown-menu-content-available-height))] w-64 max-w-[calc(100vw-2rem)]"
            data-testid="atendimento-etiquetas-menu"
          >
            {todasAsEtiquetas.map((e) => {
              const tem = etiquetas.includes(e.id);
              const vai = indo.has(e.id);
              return (
                <DropdownMenuItem
                  key={e.id}
                  disabled={vai}
                  onSelect={(ev) => {
                    ev.preventDefault();
                    void alternarEtiqueta(e, tem);
                  }}
                  aria-checked={tem}
                  data-testid={`atendimento-etiqueta-item-${e.id}`}
                >
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: e.cor ?? "currentColor" }} />
                  <span className="min-w-0 flex-1 truncate">{e.nome}</span>
                  {vai ? <span className="text-muted-foreground">…</span> : tem && <Check />}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        {!pessoal && (
        <>
        <button
          type="button"
          onClick={() => {
            setPainel((p) => (p ? null : { assunto: null }));
            setTodo(null);
          }}
          aria-pressed={!!painel}
          title="Atendente 2.0"
          aria-label="Atendente 2.0"
          className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border hover:bg-muted ${painel ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground"}`}
          data-testid="atendimento-robo-toggle"
        >
          <Bot className="h-4 w-4" />
          {c.assistente_nao_lido && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary" />}
        </button>
        <button
          type="button"
          onClick={() => {
            setTodo((x) => (x ? null : { tarefa: null }));
            setPainel(null);
          }}
          aria-pressed={!!todo}
          title="To Do"
          aria-label="To Do"
          className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border hover:bg-muted ${todo ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground"}`}
          data-testid="atendimento-todo-toggle"
        >
          <ListChecks className="h-4 w-4" />
          {tarefaRespondida && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary" data-testid="atendimento-todo-nao-lida" />}
        </button>
        </>
        )}
        </div>
      </div>
      {/* Fundo opaco (a mesma cor do muted/30 sobre o fundo): translúcido na área que rola, o Chromium desenhava um
          ladrilho de 512×256 num tom diferente no meio da conversa (Geral OZ, 30/10/2026). */}
      <div
        ref={rolagem}
        onScroll={aoRolar}
        className="min-h-0 flex-1 overflow-y-auto bg-[color-mix(in_oklab,var(--color-muted)_30%,var(--color-background))] px-3 py-3"
        data-testid="atendimento-mensagens"
      >
        <div ref={topo} className="h-px" />
        {(recentes.isLoading || (recentes.isError && !recentes.data)) && <p className="text-center text-sm text-muted-foreground">Carregando…</p>}
        {carregandoAntigas && <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin text-muted-foreground" />}
        {mensagens.map((m, i) => {
          if (m.reacao_a && (vistas.has(m.reacao_a) || !m.texto)) return null;
          const novoDia = i === 0 || dia(mensagens[i - 1]!.em) !== dia(m.em);
          return (
            <div key={m.messageid}>
              {novoDia && (
                <div className="my-3 text-center">
                  <span className="rounded-md bg-background px-2 py-0.5 text-[11px] text-muted-foreground shadow-sm">{dia(m.em)}</span>
                </div>
              )}
              {(() => {
                const r = reacoes.get(m.messageid);
                const local = minhas.get(m.messageid);
                const nossa = local ? local.emoji : (r?.nossa?.texto ?? "");
                const mostrar = [r?.cliente?.texto ?? "", nossa].filter(Boolean);
                const apagadaAqui = apagadas.has(m.messageid);
                const podeApagar = m.quem !== "cliente" && !m.apagada && !apagadaAqui && Date.now() - Date.parse(m.em) < APAGAR_ATE_MS;
                return (
                  <Balao
                    m={m}
                    jid={jid}
                    reacoes={mostrar}
                    nossa={nossa}
                    nomeDoCliente={nomeDe(c) || c.telefone}
                    piscando={piscando === m.messageid}
                    apagadaAqui={apagadaAqui}
                    onResponder={
                      !m.apagada && !apagadaAqui && Date.now() - Date.parse(m.em) < RESPONDER_ATE_MS
                        ? () => citar({ citada: { messageid: m.messageid, quem: m.quem, tipo: m.tipo, texto: m.texto, transcricao: m.transcricao }, em: m.em })
                        : undefined
                    }
                    onApagar={podeApagar ? () => apagar(m) : undefined}
                    onReagir={(e) => void reagir(m, e)}
                    onRobo={pessoal ? undefined : () => abrirPergunta(m)}
                  />
                );
              })()}
            </div>
          );
        })}
        {pendentes.map((p) => (
          <BalaoPendente key={p.id} p={p} />
        ))}
      </div>
      <Compositor
        jid={jid}
        erro={erro}
        setErro={setErro}
        onEnviou={enviou}
        citando={citando?.citada ?? null}
        nomeDoCliente={nomeDe(c) || c.telefone}
        onSemCitacao={() => citar(null)}
      />
      </div>
      {painel && (
        <PainelDoRobo
          jid={jid}
          abrir={painel.assunto}
          abrirNaoLido={!!c.assistente_nao_lido}
          mensagemDe={(id) => vistas.get(id) ?? null}
          robozinho={robozinho}
          onResolveu={() => mudar({ [ETIQUETA_ROBOZINHO]: false, [ETIQUETA_ROBOZINHO_RESOLVEU]: true })}
          onEtiquetada={voltouAoRobozinho}
          onContinuou={onPerguntou}
          onLido={onRoboLido}
          onAssunto={setAssuntoAberto}
          onFechar={() => setPainel(null)}
        />
      )}
      {todo && (
        <PainelDeTarefas
          jid={jid}
          expandido={todoExpandido}
          onExpandir={onTodoExpandido}
          aberta={todo.tarefa}
          onAbrir={(tarefa) => setTodo({ tarefa })}
          onConversa={irParaConversaDaTarefa}
          onFechar={() => setTodo(null)}
        />
      )}
      {perguntaDe && (
        <PerguntaAoRobo
          jid={jid}
          m={perguntaDe}
          onFechar={() => abrirPergunta(null)}
          onCriou={(r) => {
            // O assunto novo entra na lista na hora (sem esperar a próxima leitura de 5 s).
            qc.setQueryData<{ assuntos: Assunto[] }>(["atendimento-assuntos", jid], (antes) => ({
              assuntos: [{ ...r.assunto, mensagens: r.mensagens, nao_lidas: 0 }, ...(antes?.assuntos ?? []).filter((a) => a.id !== r.assunto.id)],
            }));
            abrirPergunta(null);
            setPainel({ assunto: r.assunto.id });
            setTodo(null);
            if (r.etiquetada) voltouAoRobozinho();
            onPerguntou();
          }}
        />
      )}
    </div>
  );
}

function Balao({
  m,
  jid,
  reacoes,
  nossa,
  nomeDoCliente,
  piscando,
  apagadaAqui,
  onResponder,
  onApagar,
  onReagir,
  onRobo,
}: {
  m: Mensagem;
  jid: string;
  /** Quem escreveu a respondida, quando foi o cliente. */
  nomeDoCliente: string;
  /** A mensagem achada pela busca, logo depois de a conversa rolar até ela: um destaque rápido, como no WhatsApp. */
  piscando: boolean;
  /** Só nas que dá para responder: até 7 dias e não apagadas. */
  onResponder?: () => void;
  /** Apagada para todos daqui, antes de o /mensagens mostrar. */
  apagadaAqui: boolean;
  /** Só nas nossas, até ~60 h: devolve se apagou. */
  onApagar?: () => Promise<boolean>;
  /** Os emojis para mostrar embaixo do balão (o do cliente e o nosso). */
  reacoes: string[];
  /** A nossa reação (vazia = nenhuma): tocar nela de novo tira. */
  nossa: string;
  onReagir: (emoji: string) => void;
  /** Perguntar à 2.0 sobre a mensagem (fora nos WhatsApps pessoais). */
  onRobo?: () => void;
}) {
  const canal = useContext(CanalDaConversa);
  const direita = m.quem !== "cliente";
  // No grupo de um WhatsApp pessoal, a de outra pessoa mostra quem escreveu.
  const rotulo = ROTULO_DE_QUEM[m.quem] ?? m.autor ?? undefined;
  // Reação sem o balão de que ela fala: só o emoji, pequeno.
  if (m.reacao_a) {
    return (
      <div className={`my-1 flex ${direita ? "justify-end" : "justify-start"}`} data-testid="atendimento-reacao-sozinha">
        <span className="rounded-full bg-background px-2 py-0.5 text-sm shadow-sm">{m.texto}</span>
      </div>
    );
  }
  const legendaDaMidia = m.transcricao || (m.midia ? m.texto : null);
  const [confirmando, setConfirmando] = useState(false);
  const [apagando, setApagando] = useState(false);
  // Apagada (pelo cliente, ou por nós daqui antes de o /mensagens mostrar): riscada, como a Central sempre mostrou.
  const apagada = m.apagada || apagadaAqui;
  return (
    <div className={`my-1 flex ${direita ? "justify-end" : "justify-start"}`} data-testid={`atendimento-msg-${m.quem}`} data-messageid={m.messageid}>
      <div className="relative max-w-[85%] md:max-w-[70%]">
        <div
          className={`rounded-lg px-3 py-1.5 text-sm shadow-sm transition-shadow duration-500 ${direita ? "rounded-tr-sm bg-emerald-50 dark:bg-emerald-950/40" : "rounded-tl-sm bg-background"} ${piscando ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-background" : ""}`}
          data-piscando={piscando || undefined}
        >
          {rotulo && <div className="mb-0.5 text-[11px] font-semibold text-muted-foreground">{rotulo}</div>}
          {(m.citacao || m.citada) && <BlocoCitado citacao={m.citacao ?? null} nomeDoCliente={nomeDoCliente} />}
          {apagada ? (
            <div className="italic text-muted-foreground" data-testid="atendimento-apagada">
              🚫 <span className="line-through">{m.texto || rotuloDaMidia(m.tipo) || "Mensagem"}</span> <span className="no-underline">(apagada)</span>
            </div>
          ) : m.midia ? (
            <>
              <Midia jid={jid} m={m} />
              {legendaDaMidia && <div className={`mt-0.5 whitespace-pre-wrap break-words ${m.transcricao ? "italic text-muted-foreground" : ""}`}>{legendaDaMidia}</div>}
              {m.transcricao && m.texto && m.texto !== m.transcricao && <div className="mt-0.5 whitespace-pre-wrap break-words">{m.texto}</div>}
            </>
          ) : naoRecebida(m.texto) ? (
            <div className="italic text-muted-foreground" data-testid="atendimento-nao-recebida">
              {NAO_RECEBIDA}
            </div>
          ) : (
            <div className="whitespace-pre-wrap break-words">{m.texto}</div>
          )}
          <div className="mt-0.5 flex items-center justify-end gap-1.5 text-[10px] text-muted-foreground">
            {onResponder && (
              <button type="button" onClick={onResponder} title="Responder" aria-label="Responder a esta mensagem" className="inline-flex items-center rounded px-0.5 hover:text-foreground" data-testid="atendimento-responder">
                <Reply className="h-3 w-3" />
              </button>
            )}
            {!apagada && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" title="Reagir" aria-label="Reagir a esta mensagem" className="inline-flex items-center rounded px-0.5 hover:text-foreground" data-testid="atendimento-reagir">
                  <SmilePlus className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align={direita ? "end" : "start"}
                collisionPadding={16}
                className="flex min-w-0 max-w-[calc(100vw-2rem)] flex-wrap justify-center gap-0.5 rounded-2xl p-1"
                data-testid="atendimento-reagir-barra"
              >
                {REACOES.map((e, i) => (
                  <DropdownMenuItem
                    key={e}
                    onSelect={() => onReagir(e === nossa ? "" : e)}
                    aria-label={e === nossa ? `Tirar a reação ${e}` : `Reagir com ${e}`}
                    className={`h-9 w-9 justify-center rounded-full p-0 text-xl ${e === nossa ? "bg-muted" : ""}`}
                    data-testid={`atendimento-reagir-${i}`}
                  >
                    {e}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            )}
            {onApagar && !apagada && (
              <button
                type="button"
                onClick={() => setConfirmando(true)}
                title="Apagar para todos"
                aria-label="Apagar para todos"
                className="inline-flex items-center rounded px-0.5 hover:text-destructive"
                data-testid="atendimento-apagar"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
            {onRobo && (
              <button type="button" onClick={onRobo} title="Perguntar à Atendente 2.0" aria-label="Perguntar à Atendente 2.0 sobre esta mensagem" className="inline-flex items-center rounded px-0.5 hover:text-foreground" data-testid="atendimento-robo">
                <Bot className="h-3 w-3" />
              </button>
            )}
            {!canal.pessoal && <CopiarReferencia texto={referenciaDaMensagem(jid, m)} />}
            {m.editada && !apagada && (
              <span className="italic" title={`Editada às ${hora(m.editada.em)}`} data-testid="atendimento-editada">
                Editada
              </span>
            )}
            <span>{hora(m.em)}</span>
          </div>
          {confirmando && onApagar && !apagada && (
            <div className="mt-1 flex flex-wrap items-center justify-end gap-2 border-t pt-1.5 text-xs" data-testid="atendimento-apagar-pergunta">
              <span className="font-medium">Apagar para todos?</span>
              <button type="button" onClick={() => setConfirmando(false)} disabled={apagando} className="rounded-md border bg-background px-2 py-0.5 hover:bg-muted" data-testid="atendimento-apagar-cancelar">
                Cancelar
              </button>
              <button
                type="button"
                disabled={apagando}
                onClick={async () => {
                  setApagando(true);
                  const ok = await onApagar();
                  setApagando(false);
                  if (ok) setConfirmando(false);
                }}
                className="inline-flex items-center gap-1 rounded-md bg-destructive px-2 py-0.5 font-semibold text-destructive-foreground hover:bg-destructive/90"
                data-testid="atendimento-apagar-confirmar"
              >
                {apagando && <Loader2 className="h-3 w-3 animate-spin" />}
                Apagar
              </button>
            </div>
          )}
        </div>
        {reacoes.length > 0 && (
          <div className={`-mt-1.5 flex ${direita ? "justify-end pr-2" : "justify-start pl-2"}`} data-testid="atendimento-reacoes">
            <span className="rounded-full border bg-background px-1.5 text-xs shadow-sm">{reacoes.join("")}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// A respondida como no WhatsApp (Pedro, 30/10/2026): quem escreveu e o texto clarinho, em até 2 linhas; nunca o código.
// Áudio mostra a transcrição; foto, a legenda (a descrição da imagem só sem legenda).
// Não achada (ou sem a `citacao`, só com o código no `citada`): só «Mensagem».
function BlocoCitado({ citacao, nomeDoCliente }: { citacao: Citada | null; nomeDoCliente: string }) {
  const c = citacao && citacao.achada !== false && citacao.quem ? citacao : null;
  const nossa = !!c && c.quem !== "cliente";
  const quem = c ? (c.quem === "cliente" ? nomeDoCliente : (ROTULO_DE_QUEM[c.quem!] ?? "Cliente")) : null;
  const corpo = !c
    ? "Mensagem"
    : naoRecebida(c.texto ?? null)
      ? NAO_RECEBIDA
      : [rotuloDaMidia(c.tipo ?? null), c.tipo === "AudioMessage" ? c.transcricao || c.texto : c.texto || c.transcricao].filter(Boolean).join(" · ") || "Mensagem";
  return (
    <div
      className={`mb-1 rounded-md border-l-4 bg-black/5 px-2 py-1 text-xs dark:bg-white/5 ${!c ? "border-muted-foreground/40" : nossa ? "border-emerald-500" : "border-sky-500"}`}
      data-testid="atendimento-citada"
    >
      {quem && <div className={`truncate text-[11px] font-semibold ${nossa ? "text-emerald-700 dark:text-emerald-400" : "text-sky-700 dark:text-sky-400"}`}>{quem}</div>}
      <div className="line-clamp-2 whitespace-pre-wrap break-words text-muted-foreground">{corpo}</div>
    </div>
  );
}

// O link público como no cartão do painel (Dashboard → PublicLinkCard): o da loja para produtos e ambos, o da agenda para
// serviços.
function linkDaLoja(l: LojaDoContato): string {
  const origem = window.location.origin.replace("://www.", "://");
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  return `${origem}${base}/${l.tipo === "servicos" ? "agendar" : "loja"}/${l.slug}`;
}

const BOTAO_DO_TOPO = "flex h-8 min-w-8 shrink-0 items-center justify-center gap-1 rounded-lg border px-2 text-muted-foreground hover:bg-muted hover:text-foreground";

// Os botões da loja do contato (Pedro, 30/10/2026, pela Geral OZ): abrir o link público e entrar na loja (impersonar)
// numa aba nova, com a Central aberta nesta. Dono com mais de uma loja: cada botão abre a lista das lojas. O copiar do
// link saiu em 31/10: no lugar dele, o copiar da conversa (o primeiro do topo, em toda conversa).
function BotoesDaLoja({ lojas, onErro }: { lojas: LojaDoContato[]; onErro: (e: string | null) => void }) {
  const qc = useQueryClient();
  const [entrando, setEntrando] = useState(false);
  const varias = lojas.length > 1;
  const entrar = async (l: LojaDoContato) => {
    // A aba abre no clique (depois do POST, o navegador a bloquearia) e só recebe o endereço com a loja já na sessão.
    const aba = window.open("", "_blank");
    if (!aba) {
      onErro("O navegador bloqueou a aba nova. Libere as janelas deste site e tente de novo.");
      return;
    }
    aba.opener = null;
    setEntrando(true);
    onErro(null);
    try {
      await customFetch("/api/admin/results/impersonate", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ storeId: l.id }) });
      // O mesmo do «Impersonar» do /results: o cache é da loja anterior, e a identidade nova chega antes do painel abrir.
      qc.invalidateQueries();
      await qc.refetchQueries({ queryKey: getGetMeQueryKey() });
      aba.location.href = `${window.location.origin}${import.meta.env.BASE_URL}`;
    } catch (err) {
      aba.close();
      onErro(mensagemDoErro(err, "Não foi possível entrar na loja."));
    } finally {
      setEntrando(false);
    }
  };
  const iconeEntrar = entrando ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />;
  if (!varias) {
    const l = lojas[0]!;
    return (
      <>
        <a href={linkDaLoja(l)} target="_blank" rel="noopener noreferrer" title="Abrir o link da loja" aria-label="Abrir o link da loja" className={BOTAO_DO_TOPO} data-testid="atendimento-loja-abrir">
          <ExternalLink className="h-4 w-4" />
        </a>
        <button type="button" onClick={() => void entrar(l)} disabled={entrando} title="Entrar na loja" aria-label="Entrar na loja" className={BOTAO_DO_TOPO} data-testid="atendimento-loja-entrar">
          {iconeEntrar}
        </button>
      </>
    );
  }
  const lista = (acao: "abrir" | "entrar", titulo: string, icone: ReactNode) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" disabled={acao === "entrar" && entrando} title={titulo} aria-label={titulo} className={BOTAO_DO_TOPO} data-testid={`atendimento-loja-${acao}`}>
          {icone}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" collisionPadding={16} className="w-60 max-w-[calc(100vw-2rem)]" data-testid={`atendimento-loja-menu-${acao}`}>
        {lojas.map((l) =>
          acao === "abrir" ? (
            <DropdownMenuItem key={l.id} asChild data-testid={`atendimento-loja-${acao}-item-${l.id}`}>
              <a href={linkDaLoja(l)} target="_blank" rel="noopener noreferrer">
                <span className="min-w-0 flex-1 truncate">{l.nome}</span>
                {l.plano && <span className="shrink-0 text-xs text-muted-foreground">{nomeDoPlano(l.plano)}</span>}
              </a>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem key={l.id} onSelect={() => void entrar(l)} data-testid={`atendimento-loja-${acao}-item-${l.id}`}>
              <span className="min-w-0 flex-1 truncate">{l.nome}</span>
              {l.plano && <span className="shrink-0 text-xs text-muted-foreground">{nomeDoPlano(l.plano)}</span>}
            </DropdownMenuItem>
          ),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
  return (
    <>
      {lista("abrir", "Abrir o link da loja", <ExternalLink className="h-4 w-4" />)}
      {lista("entrar", "Entrar na loja", iconeEntrar)}
    </>
  );
}

// O botão de copiar do balão (Pedro, 30/10/2026): não copia a mensagem, e sim uma linha de JSON para colar na sessão da
// Atendente e ela achar a mensagem: jid, messageid, a execução do n8n (quando há) e a hora em Brasília, nesta ordem.
// Brasília não tem horário de verão desde 2019: o fuso é sempre -03:00.
function referenciaDaMensagem(jid: string, m: Mensagem): string {
  const em = `${new Date(Date.parse(m.em) - 3 * 3_600_000).toISOString().slice(0, 19)}-03:00`;
  return JSON.stringify({ jid, messageid: m.messageid, ...(m.execucao ? { execucao: m.execucao } : {}), em });
}

// O copiar do topo da conversa (Pedro, 31/10/2026: «para copiar o ID da conversa toda. Igual temos em cada mensagem»):
// a mesma linha de JSON do balão, com o jid, o Nº do número da conversa e a hora da cópia em Brasília, para a sessão saber
// até onde ele tinha visto.
function referenciaDaConversa(c: Conversa): string {
  const copiadoEm = `${new Date(Date.now() - 3 * 3_600_000).toISOString().slice(0, 19)}-03:00`;
  return JSON.stringify({ jid: c.jid, numero: c.numero?.n ?? null, copiado_em: copiadoEm });
}
function CopiarConversa({ c }: { c: Conversa }) {
  const [copiou, setCopiou] = useState<boolean | null>(null);
  return (
    <button
      type="button"
      onClick={async () => {
        const ok = await copiar(referenciaDaConversa(c));
        setCopiou(ok);
        setTimeout(() => setCopiou(null), 1500);
      }}
      title="Copiar a referência da conversa"
      aria-label="Copiar a referência da conversa"
      className={BOTAO_DO_TOPO}
      data-testid="atendimento-conversa-copiar"
    >
      {/* Na coluna estreita (abaixo de 576 px, o mesmo corte do topo) só o ícone, no tamanho do botão: o texto a mais
          alargava o botão e o robozinho descia de linha (Geral OZ, 31/10/2026). O texto fica para o leitor de tela. */}
      {copiou === true ? (
        <>
          <Check className="h-4 w-4" />
          <span className="sr-only text-xs @xl:not-sr-only">Copiado</span>
        </>
      ) : copiou === false ? (
        <>
          <X className="h-4 w-4 text-destructive @xl:hidden" />
          <span className="sr-only text-xs text-destructive @xl:not-sr-only">Não copiou</span>
        </>
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </button>
  );
}

// «Copiado» só quando copiou de verdade (o mesmo `copiar` do Ver Agente), por 1,5 s no lugar do ícone.
function CopiarReferencia({ texto }: { texto: string }) {
  const [copiou, setCopiou] = useState<boolean | null>(null);
  return (
    <button
      type="button"
      title="Copiar a referência desta mensagem"
      aria-label="Copiar a referência desta mensagem"
      onClick={async () => {
        const ok = await copiar(texto);
        setCopiou(ok);
        setTimeout(() => setCopiou(null), 1500);
      }}
      className="inline-flex items-center gap-0.5 rounded px-0.5 hover:text-foreground"
      data-testid="atendimento-copiar"
    >
      {copiou === true ? (
        <>
          <Check className="h-3 w-3" />
          Copiado
        </>
      ) : copiou === false ? (
        <span className="text-destructive">Não copiou</span>
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </button>
  );
}

// ─── O robozinho (Pedro, 30/10/2026): pergunta sobre uma mensagem para a sessão «Atendente 2.0», que responde aqui ────
// As rotas /api/admin/atendimento/assuntos são da Backend; a entrega na sessão e a resposta, da Geral OZ. Cada pergunta
// abre um assunto (A-0042) com a mensagem comentada (o mesmo JSON do botão de copiar); a conversa continua no painel.
// A lista de conversas tem cache de 15 s na rota: o robozinho chega e sai com até 15 s de atraso.

type EstadoDoAssunto = "na_fila" | "entregue" | "respondido";

// Contrato da Backend (30/10/2026). O `id` do assunto é o código (A-0001…); as mensagens vêm da mais antiga à mais nova.
interface FalaDoAssunto {
  id: number;
  autor: "pedro" | "sessao";
  texto: string;
  criado_em: string;
  lido_em: string | null;
}

interface Assunto {
  id: string;
  jid: string;
  mensagem: { jid: string; messageid: string; execucao?: string | null; em?: string } | null;
  status: EstadoDoAssunto;
  /** A sessão que responde (31/10/2026): o `nome` e o rótulo dela. */
  sessao?: string | null;
  sessao_rotulo?: string | null;
  criado_em: string;
  atualizado_em: string;
  mensagens: FalaDoAssunto[];
  /** Respostas da sessão ainda não lidas. */
  nao_lidas: number;
  /** Resolvido só este assunto (T-0027, 31/10/2026); mensagem nova reabre. Ausente na API de antes. */
  resolvido_em?: string | null;
}

const ESTADO_DO_ASSUNTO: Record<EstadoDoAssunto, string> = {
  na_fila: "na fila da sessão",
  entregue: "entregue, a sessão está respondendo",
  respondido: "respondido",
};
const COR_DO_ESTADO: Record<EstadoDoAssunto, string> = {
  na_fila: "bg-muted text-muted-foreground",
  entregue: "bg-amber-100 text-amber-800",
  respondido: "bg-emerald-100 text-emerald-800",
};

// O que o balão mostra, numa linha: o texto, a transcrição ou o tipo da mídia.
const resumoDaMensagem = (m: Mensagem) =>
  (naoRecebida(m.texto) ? NAO_RECEBIDA : m.texto || m.transcricao || rotuloDaMidia(m.tipo) || "Mensagem").replace(/\s+/g, " ");

// A hora da mensagem comentada: a do balão carregado ou a do JSON (que pode não trazer `em`).
const horaDaComentada = (a: Assunto, mensagemDe: (messageid: string) => Mensagem | null): string | null => {
  const em = (a.mensagem && mensagemDe(a.mensagem.messageid)?.em) || a.mensagem?.em;
  return em && !Number.isNaN(Date.parse(em)) ? hora(em) : null;
};

// Ctrl+Enter (ou ⌘+Enter) envia; Enter sozinho quebra a linha.
const ehCtrlEnter = (e: { key: string; ctrlKey: boolean; metaKey: boolean }) => e.key === "Enter" && (e.ctrlKey || e.metaKey);

type AssuntoCriado = { assunto: Omit<Assunto, "mensagens" | "nao_lidas">; mensagens: FalaDoAssunto[]; etiquetada?: boolean };

// As sessões do Claude que respondem (Pedro, 31/10/2026: «seja escolhido qual sessão eu quero mandar o prompt»): a padrão
// vem primeiro; a fechada (o tmux dela parado) também pode ser escolhida, e a mensagem espera ela abrir.
interface Sessao {
  nome: string;
  rotulo: string;
  ativa: boolean;
  padrao: boolean;
}

function useSessoes() {
  return useQuery({
    queryKey: ["atendimento-sessoes"],
    queryFn: () => customFetch<Sessao[]>("/api/admin/atendimento/sessoes"),
    staleTime: 60_000,
    retry: false,
  });
}

// O texto sobre a sessão: a padrão segue como sempre foi («a Atendente 2.0»); as outras, pelo rótulo.
const comASessao = (rotulo: string | null | undefined) => (!rotulo || rotulo === "Atendente 2.0" ? "a Atendente 2.0" : rotulo);

// O dropdown das sessões (o popup do robozinho e a tarefa nova): a escolhida com ✓ e a sugerida marcada.
function MenuDeSessoes({
  sessoes,
  valor,
  sugerida,
  onEscolher,
  testid,
}: {
  sessoes: Sessao[];
  valor: string | null;
  sugerida?: string | null;
  onEscolher: (nome: string) => void;
  testid: string;
}) {
  return (
    <DropdownMenuContent
      align="start"
      collisionPadding={16}
      className="max-h-[min(22rem,var(--radix-dropdown-menu-content-available-height))] w-64 max-w-[calc(100vw-2rem)]"
      onClick={(e) => e.stopPropagation()}
      data-testid={testid}
    >
      {sessoes.map((s) => (
        <DropdownMenuItem key={s.nome} onSelect={() => onEscolher(s.nome)} aria-checked={s.nome === valor} data-testid={`${testid}-item`} data-sessao={s.nome}>
          <span className="min-w-0 flex-1 truncate">
            {s.rotulo}
            {!s.ativa && <span className="text-muted-foreground"> (fechada)</span>}
          </span>
          {s.nome === sugerida && <span className="shrink-0 rounded bg-primary/10 px-1.5 py-px text-[10px] font-medium text-primary">sugerida</span>}
          {s.nome === valor && <Check />}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  );
}

function PerguntaAoRobo({ jid, m, onFechar, onCriou }: { jid: string; m: Mensagem; onFechar: () => void; onCriou: (r: AssuntoCriado) => void }) {
  // O rascunho da pergunta, por mensagem: volta ao recarregar e some ao enviar.
  const chaveDoTexto = `central-pergunta-texto:${jid}:${m.messageid}`;
  const [texto, setTexto] = useState(() => lerTexto(chaveDoTexto));
  useEffect(() => guardarTexto(chaveDoTexto, texto), [chaveDoTexto, texto]);
  const campoDaPergunta = useRef<HTMLTextAreaElement>(null);
  const correcaoDaPergunta = useCorrecao(texto, setTexto, campoDaPergunta);
  // A setinha do título escolhe a sessão; cada vez que o popup abre, volta à padrão (null). Sem escolher, o POST é o de antes.
  const sessoes = useSessoes().data ?? [];
  const [sessao, setSessao] = useState<string | null>(null);
  const escolhida = sessoes.find((s) => s.nome === sessao) ?? sessoes.find((s) => s.padrao) ?? sessoes[0] ?? null;
  const rotulo = escolhida?.rotulo ?? "Atendente 2.0";
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // «Editar mensagem» (Pedro, 02/11/2026, T-0046): o pedido chega à sessão já com a skill /editar-mensagem. Desligada a
  // cada vez que o popup abre. Um lapizinho no canto (Pedro, 18:22: «O toggle ficou muito grande… um lapizinho, um
  // canto»), aceso quando ligado; a dica aparece com o mouse em cima e, no toque, por um instante.
  const [editarMensagem, setEditarMensagem] = useState(false);
  const [dicaDoLapis, setDicaDoLapis] = useState(false);
  const tempoDaDica = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (tempoDaDica.current) clearTimeout(tempoDaDica.current); }, []);
  const alternarEditar = () => {
    setEditarMensagem((v) => !v);
    setDicaDoLapis(true);
    if (tempoDaDica.current) clearTimeout(tempoDaDica.current);
    tempoDaDica.current = setTimeout(() => setDicaDoLapis(false), 1500);
  };
  const ditado = useDitadoNoCampo(texto, setTexto, campoDaPergunta, setErro);
  const anexos = useAnexosColados(setErro);
  const enviar = async () => {
    const t = anexos.junto(texto);
    if (!t || enviando || anexos.subindo) return;
    setEnviando(true);
    setErro(null);
    try {
      const r = await customFetch<AssuntoCriado>("/api/admin/atendimento/assuntos", {
        method: "POST",
        body: JSON.stringify({ jid, mensagem: JSON.parse(referenciaDaMensagem(jid, m)), texto: t, ...(sessao ? { sessao } : {}), ...(editarMensagem ? { editarMensagem: true } : {}) }),
      });
      guardarTexto(chaveDoTexto, "");
      anexos.limpar();
      onCriou(r);
    } catch (err) {
      setErro(mensagemDoErro(err, `Não foi possível enviar à ${rotulo}.`));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <DialogPrimitive.Root open onOpenChange={(o) => !o && onFechar()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-background p-4 shadow-xl focus:outline-none"
          aria-describedby={undefined}
          data-testid="atendimento-robo-popup"
        >
          <div className="mb-3 flex items-center gap-2">
            <Bot className="h-5 w-5 shrink-0 text-primary" />
            <DialogPrimitive.Title className="flex min-w-0 items-center gap-1 text-base font-semibold" data-testid="atendimento-robo-titulo">
              <span className="shrink-0">Perguntar à</span>
              {sessoes.length > 1 ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="-mx-1 flex min-w-0 items-center gap-0.5 rounded-md px-1 hover:bg-muted"
                      title="Escolher a sessão"
                      data-testid="atendimento-robo-sessao"
                    >
                      <span className="truncate">
                        {rotulo}
                        {escolhida && !escolhida.ativa && <span className="font-normal text-muted-foreground"> (fechada)</span>}
                      </span>
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <MenuDeSessoes sessoes={sessoes} valor={escolhida?.nome ?? null} onEscolher={setSessao} testid="atendimento-robo-sessoes" />
                </DropdownMenu>
              ) : (
                <span className="truncate">{rotulo}</span>
              )}
            </DialogPrimitive.Title>
            <div className="group relative ml-auto">
              <button
                type="button"
                onClick={alternarEditar}
                aria-pressed={editarMensagem}
                aria-label="Editar mensagem"
                className={`flex h-7 w-7 items-center justify-center rounded-md border ${editarMensagem ? "border-primary bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                data-testid="atendimento-robo-editar-chave"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <span
                role="tooltip"
                className={`pointer-events-none absolute right-0 top-full z-10 mt-1 whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-[11px] text-background ${dicaDoLapis ? "block" : "hidden group-hover:block"}`}
                data-testid="atendimento-robo-editar-dica"
              >
                Editar mensagem
              </span>
            </div>
            <DialogPrimitive.Close className="flex h-8 w-8 items-center justify-center rounded-lg border text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Fechar">
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="mb-3 line-clamp-3 border-l-2 border-primary/60 bg-muted/60 py-1 pl-2.5 pr-2 text-xs text-muted-foreground" data-testid="atendimento-robo-citada">
            <span className="font-semibold">{ROTULO_DE_QUEM[m.quem] ?? "Cliente"} · {hora(m.em)}</span> — {resumoDaMensagem(m)}
          </div>
          <textarea
            ref={campoDaPergunta}
            autoFocus
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            {...correcaoDaPergunta}
            readOnly={ditado.ativo}
            onPaste={anexos.colar}
            onKeyDown={(e) => {
              correcaoDaPergunta.onKeyDown(e);
              if (e.defaultPrevented) return;
              if (ehCtrlEnter(e)) {
                e.preventDefault();
                if (!ditado.ativo) void enviar();
              }
            }}
            rows={4}
            placeholder={`O que você quer perguntar à ${rotulo} sobre esta mensagem?`}
            className="w-full resize-none rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            data-testid="atendimento-robo-texto"
          />
          <div className="mt-2 empty:mt-0">
            <MiniaturasColadas anexos={anexos.anexos} onTirar={anexos.tirar} />
          </div>
          {erro && (
            <p className="mt-2 text-sm text-destructive" role="alert">
              {erro}
            </p>
          )}
          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="text-[11px] text-muted-foreground">{ditado.ativo ? <AvisoDoDitado estado={ditado.estado} /> : "Ctrl + Enter envia"}</span>
            <div className="flex items-center gap-2">
            <BotaoDeImagem onEscolher={anexos.escolher} desabilitado={ditado.ativo} testid="atendimento-robo-imagem" />
            <BotaoDoDitado estado={ditado.estado} onClick={ditado.alternar} testid="atendimento-robo-ditar" />
            <button
              type="button"
              onClick={() => void enviar()}
              disabled={(!texto.trim() && !anexos.temImagem) || enviando || ditado.ativo || anexos.subindo}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              data-testid="atendimento-robo-enviar"
            >
              {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
              Enviar
            </button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// O painel à direita, como o lateral da extensão do Claude no Chrome; no celular, a tela inteira.
function PainelDoRobo({
  jid,
  abrir,
  abrirNaoLido,
  mensagemDe,
  robozinho,
  onResolveu,
  onEtiquetada,
  onContinuou,
  onLido,
  onAssunto,
  onFechar,
}: {
  jid: string;
  abrir: string | null;
  abrirNaoLido: boolean;
  mensagemDe: (messageid: string) => Mensagem | null;
  robozinho: "pendente" | "resolvido" | null;
  onResolveu: () => void;
  /** O «Continuar…» devolveu a conversa ao RobôZinho (`etiquetada` na resposta). */
  onEtiquetada: () => void;
  /** O «Continuar…» saiu: o card fica azul na hora. */
  onContinuou: () => void;
  onLido: () => void;
  /** O assunto aberto no painel (null = a lista), para voltar igual ao recarregar. */
  onAssunto: (id: string | null) => void;
  onFechar: () => void;
}) {
  const qc = useQueryClient();
  const [aberto, setAberto] = useState<string | null>(abrir);
  useEffect(() => {
    onAssunto(aberto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);
  // «Resolvido» (Pedro, 30/10/2026): a conversa sai da etiqueta «RobôZinho» e vai para a «RobôZinho Resolveu».
  const [resolvendo, setResolvendo] = useState(false);
  const [erroDoResolvido, setErroDoResolvido] = useState<string | null>(null);
  const resolver = async () => {
    setResolvendo(true);
    setErroDoResolvido(null);
    try {
      await customFetch("/api/admin/atendimento/robozinho-resolveu", { method: "POST", body: JSON.stringify({ jid }) });
      onResolveu();
      qc.invalidateQueries({ queryKey: ["atendimento-conversas"] });
      qc.invalidateQueries({ queryKey: ["atendimento-etiquetas"] });
    } catch (e) {
      setErroDoResolvido(mensagemDoErro(e, "Não foi possível marcar como resolvido."));
    } finally {
      setResolvendo(false);
    }
  };
  // «Resolvido» de um assunto só (Pedro, 31/10/2026, T-0027): cada linha da aba se resolve sozinha; o último aberto
  // resolvido leva a conversa para a «RobôZinho Resolveu», como o botão da conversa.
  const [resolvendoAssunto, setResolvendoAssunto] = useState<string | null>(null);
  const resolverAssunto = async (a: Assunto, resolvido: boolean) => {
    setResolvendoAssunto(a.id);
    setErroDoResolvido(null);
    try {
      const r = await customFetch<{ conversa: "resolvida" | "aberta" | "etiqueta-falhou" }>(`/api/admin/atendimento/assuntos/${encodeURIComponent(a.id)}/resolvido`, {
        method: "POST",
        body: JSON.stringify({ resolvido }),
      });
      if (r.conversa === "resolvida") onResolveu();
      if (r.conversa === "etiqueta-falhou") setErroDoResolvido("O assunto ficou resolvido, mas a etiqueta da conversa não trocou. Use o «Resolvido» da lista.");
      qc.invalidateQueries({ queryKey: ["atendimento-assuntos", jid] });
      qc.invalidateQueries({ queryKey: ["atendimento-conversas"] });
      qc.invalidateQueries({ queryKey: ["atendimento-etiquetas"] });
    } catch (e) {
      setErroDoResolvido(mensagemDoErro(e, "Não foi possível marcar o assunto como resolvido."));
    } finally {
      setResolvendoAssunto(null);
    }
  };
  useEffect(() => {
    if (abrir) setAberto(abrir);
  }, [abrir]);
  const assuntos = useQuery({
    queryKey: ["atendimento-assuntos", jid],
    queryFn: () => customFetch<{ assuntos: Assunto[] }>(`/api/admin/atendimento/assuntos?jid=${encodeURIComponent(jid)}`),
    refetchInterval: 5_000,
    retry: false,
  });
  const lista = assuntos.data?.assuntos ?? [];
  // Aberta pela resposta não lida: vai direto ao assunto dela. Só com a lista buscada agora: a do cache (de quando ele
  // perguntou, antes da resposta) não tem a não lida, e o painel ficava na lista com o robozinho verde (31/10/2026).
  const jaEscolheu = useRef(!!abrir);
  useEffect(() => {
    if (jaEscolheu.current || !abrirNaoLido || !assuntos.isFetchedAfterMount) return;
    jaEscolheu.current = true;
    const naoLido = lista.find((a) => a.nao_lidas > 0);
    if (naoLido) setAberto(naoLido.id);
  }, [assuntos.data, assuntos.isFetchedAfterMount, abrirNaoLido, lista]);
  const atual = lista.find((a) => a.id === aberto) ?? null;
  // Resposta à vista = lida: o robozinho sai da lista.
  const naoLidasDoAtual = atual?.nao_lidas ?? 0;
  useEffect(() => {
    if (!atual || naoLidasDoAtual === 0) return;
    customFetch(`/api/admin/atendimento/assuntos/${encodeURIComponent(atual.id)}/lido`, { method: "POST" })
      .then(() => {
        onLido();
        qc.invalidateQueries({ queryKey: ["atendimento-conversas"] });
        qc.invalidateQueries({ queryKey: ["atendimento-assuntos", jid] });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atual?.id, naoLidasDoAtual, jid, qc]);

  return (
    <aside className="fixed inset-0 z-40 flex flex-col bg-background md:static md:z-auto md:w-[380px] md:shrink-0 md:border-l lg:w-[420px]" data-testid="atendimento-robo-painel">
      <div className="flex items-center gap-2 border-b px-3 py-2.5">
        {/* No celular o voltar fica sempre à esquerda, como o da conversa (Pedro, 31/10/2026, T-0027): do assunto volta à
            lista; da lista, fecha o robô e volta à conversa. O X da direita é só do computador. */}
        {atual ? (
          <button type="button" onClick={() => setAberto(null)} className="-ml-1 rounded p-1 text-muted-foreground hover:text-foreground" aria-label="Assuntos" data-testid="atendimento-robo-assuntos">
            <ChevronLeft className="h-5 w-5" />
          </button>
        ) : (
          <button type="button" onClick={onFechar} className="-ml-1 rounded p-1 text-muted-foreground hover:text-foreground md:hidden" aria-label="Voltar à conversa" data-testid="atendimento-robo-voltar">
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        <Bot className="h-5 w-5 shrink-0 text-primary" />
        <span className="min-w-0 truncate text-sm font-semibold" data-testid="atendimento-robo-cabeca">
          {atual ? atual.id : "Atendente 2.0"}
          {atual?.sessao_rotulo && <span className="font-normal text-muted-foreground"> · {atual.sessao_rotulo}</span>}
        </span>
        {atual && <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${COR_DO_ESTADO[atual.status] ?? COR_DO_ESTADO.na_fila}`}>{ESTADO_DO_ASSUNTO[atual.status] ?? atual.status}</span>}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {atual && (
            <BotaoResolvidoDoAssunto a={atual} indo={resolvendoAssunto === atual.id} onMudar={(v) => void resolverAssunto(atual, v)} />
          )}
          {!atual && robozinho === "pendente" && (
            <button
              type="button"
              onClick={() => void resolver()}
              disabled={resolvendo}
              className="flex h-8 items-center gap-1 rounded-lg border px-2.5 text-xs font-semibold hover:bg-muted disabled:opacity-60"
              data-testid="atendimento-robo-resolvido"
            >
              {resolvendo && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Resolver
            </button>
          )}
          {!atual && robozinho === "resolvido" && (
            <span className="flex h-8 items-center gap-1 rounded-lg bg-emerald-50 px-2.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" data-testid="atendimento-robo-resolvido-feito">
              <Check className="h-3.5 w-3.5" />
              Resolvido
            </span>
          )}
          <button type="button" onClick={onFechar} className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg border text-muted-foreground hover:bg-muted hover:text-foreground md:flex" aria-label="Fechar a Atendente 2.0" data-testid="atendimento-robo-fechar">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      {erroDoResolvido && (
        <div className="border-b px-3 py-2">
          <div className="flex items-start gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert" data-testid="atendimento-robo-resolvido-erro">
            <span className="min-w-0 flex-1">{erroDoResolvido}</span>
            <button type="button" onClick={() => setErroDoResolvido(null)} aria-label="Fechar o aviso" className="shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {atual ? (
        <>
          <FioDoAssunto a={atual} mensagemDe={mensagemDe} />
          <ContinuarAssunto a={atual} jid={jid} onEtiquetada={onEtiquetada} onContinuou={onContinuou} />
        </>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto" data-testid="atendimento-robo-lista">
          {assuntos.isLoading || (assuntos.isError && !assuntos.data) ? (
            <p className="p-4 text-sm text-muted-foreground">Carregando…</p>
          ) : lista.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              <p className="font-medium text-foreground">Nenhum assunto nesta conversa</p>
              <p className="mt-1 text-xs">Toque no robozinho de uma mensagem para perguntar à Atendente 2.0.</p>
            </div>
          ) : (
            lista.map((a) => {
              const primeira = a.mensagens.find((f) => f.autor === "pedro");
              const quando = horaDaComentada(a, mensagemDe);
              return (
                <div key={a.id} className={`flex items-start gap-2 border-b pr-3 hover:bg-muted/60 ${a.resolvido_em ? "opacity-70" : ""}`} data-testid={`atendimento-robo-linha-${a.id}`}>
                <button type="button" onClick={() => setAberto(a.id)} className="flex min-w-0 flex-1 flex-col gap-1 py-3 pl-4 text-left" data-testid={`atendimento-robo-assunto-${a.id}`}>
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="min-w-0 truncate text-xs" data-testid="atendimento-robo-sessao-do-assunto">
                      <span className="font-mono font-semibold">{a.id}</span>
                      {a.sessao_rotulo && <span className="text-muted-foreground"> · {a.sessao_rotulo}</span>}
                    </span>
                    {quando && <span className="shrink-0 text-[11px] text-muted-foreground">mensagem das {quando}</span>}
                    {a.nao_lidas > 0 && <span className="ml-auto h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Resposta não lida" />}
                  </span>
                  {primeira && <span className="line-clamp-2 text-sm">{semLinhasDeImagem(primeira.texto)}</span>}
                  <span className={`self-start rounded-full px-2 py-0.5 text-[11px] font-medium ${COR_DO_ESTADO[a.status] ?? COR_DO_ESTADO.na_fila}`}>{ESTADO_DO_ASSUNTO[a.status] ?? a.status}</span>
                </button>
                <div className="shrink-0 pt-3">
                  <BotaoResolvidoDoAssunto a={a} indo={resolvendoAssunto === a.id} onMudar={(v) => void resolverAssunto(a, v)} />
                </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </aside>
  );
}

// O «Resolvido» de um assunto: aberto, o botão; resolvido, o selo verde, que desfaz no toque.
function BotaoResolvidoDoAssunto({ a, indo, onMudar }: { a: Assunto; indo: boolean; onMudar: (resolvido: boolean) => void }) {
  if (a.resolvido_em) {
    return (
      <button
        type="button"
        onClick={() => onMudar(false)}
        disabled={indo}
        title="Resolvido. Toque para reabrir"
        className="flex h-8 items-center gap-1 rounded-lg bg-emerald-50 px-2.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60 dark:bg-emerald-950/40 dark:text-emerald-300"
        data-testid={`atendimento-robo-assunto-resolvido-feito-${a.id}`}
      >
        {indo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        Resolvido
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onMudar(true)}
      disabled={indo}
      title="Marcar este assunto como resolvido"
      className="flex h-8 items-center gap-1 rounded-lg border px-2.5 text-xs font-semibold hover:bg-muted disabled:opacity-60"
      data-testid={`atendimento-robo-assunto-resolver-${a.id}`}
    >
      {indo && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      Resolver
    </button>
  );
}

function FioDoAssunto({ a, mensagemDe }: { a: Assunto; mensagemDe: (messageid: string) => Mensagem | null }) {
  const rolagem = useRef<HTMLDivElement>(null);
  // Ao abrir e a cada fala nova, desce ao fim.
  useLayoutEffect(() => {
    const el = rolagem.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [a.id, a.mensagens.length]);
  const comentada = a.mensagem ? mensagemDe(a.mensagem.messageid) : null;
  const quando = horaDaComentada(a, mensagemDe);
  return (
    <div ref={rolagem} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3" data-testid="atendimento-robo-fio">
      {a.mensagem && (
        <div className="border-l-2 border-primary/60 bg-muted/60 py-1 pl-2.5 pr-2 text-xs text-muted-foreground" data-testid="atendimento-robo-comentada">
          <span className="font-semibold">{quando ? `Mensagem das ${quando}` : "Mensagem comentada"}</span>
          {comentada && <> — <span className="line-clamp-3 inline">{resumoDaMensagem(comentada)}</span></>}
        </div>
      )}
      {a.mensagens.map((f) =>
        f.autor === "pedro" ? (
          <div key={f.id} className="flex justify-end" data-testid="atendimento-robo-pergunta">
            <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-tr-sm bg-primary/10 px-3 py-2 text-sm"><TextoComImagens texto={f.texto} /></div>
          </div>
        ) : (
          <div key={f.id} className="flex gap-2" data-testid="atendimento-robo-resposta">
            <Bot className="mt-1 h-4 w-4 shrink-0 text-primary" />
            <div className="min-w-0 flex-1 break-words">
              <Markdown texto={f.texto} />
              <div className="mt-0.5 text-[10px] text-muted-foreground">{hora(f.criado_em)}</div>
            </div>
          </div>
        ),
      )}
      {a.status !== "respondido" && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="atendimento-robo-esperando">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {a.status === "entregue" ? "A sessão está respondendo…" : "Na fila da sessão…"}
        </div>
      )}
    </div>
  );
}

function ContinuarAssunto({ a, jid, onEtiquetada, onContinuou }: { a: Assunto; jid: string; onEtiquetada: () => void; onContinuou: () => void }) {
  const qc = useQueryClient();
  // O rascunho do «Continuar…», por assunto: volta ao recarregar e some ao enviar.
  const chaveDoTexto = `central-continuar:${a.id}`;
  const [texto, setTexto] = useState(() => lerTexto(chaveDoTexto));
  useEffect(() => guardarTexto(chaveDoTexto, texto), [chaveDoTexto, texto]);
  const campoDoContinuar = useRef<HTMLTextAreaElement>(null);
  const correcaoDoContinuar = useCorrecao(texto, setTexto, campoDoContinuar);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const ditado = useDitadoNoCampo(texto, setTexto, campoDoContinuar, setErro);
  const anexos = useAnexosColados(setErro);
  const enviar = async () => {
    const t = anexos.junto(texto);
    if (!t || enviando || anexos.subindo) return;
    setEnviando(true);
    setErro(null);
    try {
      const r = await customFetch<{ etiquetada?: boolean }>(`/api/admin/atendimento/assuntos/${encodeURIComponent(a.id)}/mensagens`, {
        method: "POST",
        body: JSON.stringify({ texto: t }),
      });
      setTexto("");
      anexos.limpar();
      if (r?.etiquetada) onEtiquetada();
      onContinuou();
      await qc.invalidateQueries({ queryKey: ["atendimento-assuntos", jid] });
    } catch (err) {
      setErro(mensagemDoErro(err, `Não foi possível enviar à ${a.sessao_rotulo ?? "Atendente 2.0"}.`));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <div className="border-t px-3 py-2.5">
      {erro && (
        <p className="mb-2 text-sm text-destructive" role="alert">
          {erro}
        </p>
      )}
      <div className="mb-2 empty:mb-0">
        <MiniaturasColadas anexos={anexos.anexos} onTirar={anexos.tirar} />
      </div>
      <div className="flex items-end gap-2">
        <textarea
          ref={campoDoContinuar}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          {...correcaoDoContinuar}
          readOnly={ditado.ativo}
          onPaste={anexos.colar}
          onKeyDown={(e) => {
            correcaoDoContinuar.onKeyDown(e);
            if (e.defaultPrevented) return;
            if (ehCtrlEnter(e)) {
              e.preventDefault();
              if (!ditado.ativo) void enviar();
            }
          }}
          rows={2}
          placeholder={`Continuar com ${comASessao(a.sessao_rotulo)}…`}
          className="max-h-40 min-h-10 min-w-0 flex-1 resize-none rounded-2xl border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
          data-testid="atendimento-robo-continuar"
        />
        <BotaoDeImagem onEscolher={anexos.escolher} desabilitado={ditado.ativo} tamanho="redondo" testid="atendimento-robo-continuar-imagem" />
        <BotaoDoDitado estado={ditado.estado} onClick={ditado.alternar} redondo testid="atendimento-robo-continuar-ditar" />
        <button
          type="button"
          onClick={() => void enviar()}
          disabled={(!texto.trim() && !anexos.temImagem) || enviando || ditado.ativo || anexos.subindo}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          aria-label={`Enviar à ${a.sessao_rotulo ?? "Atendente 2.0"}`}
          data-testid="atendimento-robo-continuar-enviar"
        >
          {enviando ? <Loader2 className="h-5 w-5 animate-spin" /> : <SendHorizontal className="h-5 w-5" />}
        </button>
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">{ditado.ativo ? <AvisoDoDitado estado={ditado.estado} /> : "Ctrl + Enter envia"}</p>
    </div>
  );
}

// O microfone do ditado (Pedro, 31/10/2026): toca para falar, o texto vai entrando no campo com a pontuação; toca de novo
// para parar. Enquanto ouve, o campo fica só de leitura e o Enviar espera o último trecho.
function BotaoDoDitado({ estado, onClick, redondo = false, pequeno = false, testid }: { estado: EstadoDoDitado; onClick: () => void; redondo?: boolean; pequeno?: boolean; testid: string }) {
  const ouvindo = estado === "ouvindo";
  const ocupado = estado === "ligando" || estado === "terminando";
  const titulo = ouvindo ? "Parar o ditado" : "Ditar (a fala vira texto)";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={ocupado}
      title={titulo}
      aria-label={titulo}
      aria-pressed={ouvindo}
      className={`flex shrink-0 items-center justify-center border transition-colors disabled:opacity-60 ${redondo ? "h-10 w-10 rounded-full" : pequeno ? "h-8 w-8 rounded-lg" : "h-9 w-9 rounded-lg"} ${
        ouvindo ? "animate-pulse border-red-500 bg-red-500 text-white hover:bg-red-600" : "text-muted-foreground hover:bg-muted hover:text-foreground"
      }`}
      data-testid={testid}
      data-estado={estado}
    >
      {ocupado ? <Loader2 className="h-4 w-4 animate-spin" /> : ouvindo ? <Square className="h-4 w-4 fill-current" /> : <Mic className="h-[18px] w-[18px]" />}
    </button>
  );
}

function AvisoDoDitado({ estado }: { estado: EstadoDoDitado }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-medium text-red-600" data-testid="atendimento-ditado-aviso">
      {estado === "ouvindo" && <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" aria-hidden="true" />}
      {estado === "ligando" ? "Ligando o microfone…" : estado === "terminando" ? "Terminando…" : "Ouvindo… toque no quadrado para parar"}
    </span>
  );
}

// ─── O TO DO (Pedro, 31/10/2026 pela Geral OZ): tarefas para resolver depois, ligadas a uma conversa ou não ────────────
// As rotas /api/admin/atendimento/tarefas são da Backend; o título e a sessão sugerida chegam da ponte da Geral OZ em
// segundos, e a entrega à sessão e a resposta também são dela. A aba fica no lugar da do robozinho (uma das duas por vez).

type EstadoDaTarefa = "nova" | "na_fila" | "entregue" | "respondido" | "concluida";

interface Tarefa {
  id: string;
  /** null até a ponte sugerir. */
  titulo: string | null;
  titulo_editado: boolean;
  comentario: string;
  vinculada: boolean;
  /** A conversa em que nasceu: fica guardada com o vínculo desligado. */
  jid: string | null;
  numero: number | null;
  /** Só com o vínculo ligado. */
  conversa: { nome: string; loja: string | null; numero: { n: number; nome: string } | null } | null;
  sessao_sugerida: string | null;
  sessao_sugerida_rotulo: string | null;
  sessao: string | null;
  sessao_rotulo: string | null;
  status: EstadoDaTarefa;
  /** Algo ainda não visto por quem está na página: resposta da sessão, comentário do outro ou a marca de não lida. */
  nao_lida: boolean;
  /** Quem criou e para quem é (Marina, 31/10/2026: tarefas dela para o Pedro e dele para ela). */
  criada_por?: Pessoa;
  para?: Pessoa;
  /** Comentário do outro ainda não visto por quem está na página. */
  comentario_novo?: boolean;
  /** Fixada no topo e a posição que a pessoa arrastou (T-0027, 31/10/2026); ausentes na API de antes. */
  fixada?: boolean;
  posicao?: number | null;
  /** Minimizada na lista da pessoa, no servidor (T-0027, 01/11/2026: vale no computador e no celular). */
  minimizada?: boolean;
  criado_em: string;
  atualizado_em: string;
  enviada_em: string | null;
  concluida_em: string | null;
}

interface FalaDaTarefa {
  /** «pedro» é a fala de gente (para a sessão), de quem estiver na `pessoa`. */
  autor: "pedro" | "sessao";
  pessoa?: Pessoa | null;
  texto: string;
  criado_em: string;
  entregue_em: string | null;
  lido_em: string | null;
}

/** O comentário entre a Marina e o Pedro: fica na tarefa e não vai à sessão. */
interface ComentarioDaTarefa {
  id: number | string;
  pessoa: Pessoa;
  texto: string;
  criado_em: string;
}

/** A tarefa com a conversa dela com a sessão (o comentário é a primeira fala) e os comentários; toda mudança devolve assim. */
type TarefaInteira = Tarefa & { mensagens: FalaDaTarefa[]; comentarios?: ComentarioDaTarefa[] };

// Quem está na página (Marina, 31/10/2026: «Quem acessa /atendimento é Marina e quem acessa /pedro é Pedro»; as duas com
// a conta de suporte). Vai em `?pessoa=` em toda rota de tarefa; sem ele, a API entende a Marina.
type Pessoa = "marina" | "pedro";
const PESSOAS: Record<Pessoa, { nome: string; letra: string; paraEla: string; dela: string }> = {
  marina: { nome: "Marina", letra: "M", paraEla: "para a Marina", dela: "da Marina" },
  pedro: { nome: "Pedro", letra: "P", paraEla: "para o Pedro", dela: "do Pedro" },
};
const outraPessoa = (p: Pessoa): Pessoa => (p === "marina" ? "pedro" : "marina");
const maiuscula = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
// Tarefa entre os dois (uma criou para o outro): só nela há comentário.
const compartilhada = (t: Tarefa) => !!t.para && !!t.criada_por && t.para !== t.criada_por;
const PessoaDaPagina = createContext<Pessoa>("marina");
function useRotasDaTarefa() {
  const pessoa = useContext(PessoaDaPagina);
  const q = pessoa === "marina" ? "" : `?pessoa=${pessoa}`;
  return {
    pessoa,
    lista: `/api/admin/atendimento/tarefas${q}`,
    url: (id: string, resto = "") => `/api/admin/atendimento/tarefas/${encodeURIComponent(id)}${resto}${q}`,
    chaveLista: ["atendimento-tarefas", pessoa],
    chave: (id: string) => ["atendimento-tarefa", pessoa, id],
  };
}

const ESTADO_DA_TAREFA: Record<EstadoDaTarefa, string> = {
  nova: "Nova",
  na_fila: "Na fila",
  entregue: "A sessão está respondendo",
  respondido: "Respondida",
  concluida: "Concluída",
};
// As cores do robozinho do card: azul na fila, amarelo respondendo, verde na respondida não lida. O que mais estiver não
// visto (a tarefa que o outro deu, o comentário dele, a marca de não lida) fica no ponto ao lado do número.
const corDaTarefa = (t: Tarefa) =>
  t.status === "na_fila"
    ? ROBO_DO_CARD.fila.cor
    : t.status === "entregue"
      ? ROBO_DO_CARD.andamento.cor
      : t.status === "respondido" && t.nao_lida
        ? ROBO_DO_CARD.nao_lida.cor
        : "bg-muted text-muted-foreground";

// A lista, de 5 em 5 s com a aba aberta (o título e a sugestão chegam em segundos); fechada, só para o ponto do botão.
function useTarefas(abaAberta: boolean) {
  const rotas = useRotasDaTarefa();
  return useQuery({
    queryKey: rotas.chaveLista,
    queryFn: () => customFetch<{ tarefas: Tarefa[] }>(rotas.lista),
    refetchInterval: abaAberta ? 5_000 : 15_000,
    retry: false,
  });
}

// O que a mudança devolveu entra na hora (na lista e na tarefa aberta); a lista relê a ordem do servidor.
function useTarefaMudou() {
  const qc = useQueryClient();
  const rotas = useRotasDaTarefa();
  return (t: TarefaInteira) => {
    qc.setQueryData(rotas.chave(t.id), t);
    qc.setQueryData<{ tarefas: Tarefa[] }>(rotas.chaveLista, (antes) =>
      antes && { tarefas: antes.tarefas.some((x) => x.id === t.id) ? antes.tarefas.map((x) => (x.id === t.id ? t : x)) : [t, ...antes.tarefas] },
    );
    void qc.invalidateQueries({ queryKey: rotas.chaveLista });
  };
}

// Excluir, marcar como não lida e editar a observação (Pedro, 31/10/2026: «Tenho que poder excluir uma tarefa, e também
// marcar como não lida… E editar para adicionar mais coisas na observação»), as mesmas na aba e na /pedro.
function useAcoesDaTarefa(aberta: string | null, onAbrir: (id: string | null) => void): AcoesDaTarefa {
  const qc = useQueryClient();
  const { toast } = useToast();
  const rotas = useRotasDaTarefa();
  const mudou = useTarefaMudou();
  return {
    // A tarefa sai da lista e da contagem; aberta, a aba volta à lista. A compartilhada some para os dois.
    excluir: async (id) => {
      try {
        await customFetch(rotas.url(id), { method: "DELETE" });
      } catch (err) {
        toast({ title: mensagemDoErro(err, "Não foi possível excluir a tarefa."), variant: "destructive" });
        return false;
      }
      if (aberta === id) onAbrir(null);
      qc.setQueryData<{ tarefas: Tarefa[] }>(rotas.chaveLista, (antes) => antes && { tarefas: antes.tarefas.filter((x) => x.id !== id) });
      qc.removeQueries({ queryKey: rotas.chave(id) });
      void qc.invalidateQueries({ queryKey: rotas.chaveLista });
      return true;
    },
    // O ponto volta na tarefa e no botão. Aberta, ela fecha antes: senão a regra «aberta com resposta não lida marca
    // lida» desfaria na hora. Abrir de novo tira a marca.
    marcarNaoLida: async (id) => {
      if (aberta === id) onAbrir(null);
      qc.setQueryData<{ tarefas: Tarefa[] }>(rotas.chaveLista, (antes) => antes && { tarefas: antes.tarefas.map((x) => (x.id === id ? { ...x, nao_lida: true } : x)) });
      try {
        const r = await customFetch<TarefaInteira>(rotas.url(id, "/nao-lida"), { method: "POST" });
        if (r && typeof r === "object" && "status" in r) mudou(r);
        else void qc.invalidateQueries({ queryKey: rotas.chaveLista });
      } catch (err) {
        toast({ title: mensagemDoErro(err, "Não foi possível marcar como não lida."), variant: "destructive" });
        void qc.invalidateQueries({ queryKey: rotas.chaveLista });
      }
    },
    // Fixar no topo (T-0027): na hora na lista; a releitura traz a ordem do servidor.
    fixar: async (id, fixada) => {
      qc.setQueryData<{ tarefas: Tarefa[] }>(rotas.chaveLista, (antes) => antes && { tarefas: antes.tarefas.map((x) => (x.id === id ? { ...x, fixada } : x)) });
      try {
        mudou(await customFetch<TarefaInteira>(rotas.url(id, "/fixar"), { method: "POST", body: JSON.stringify({ fixada }) }));
      } catch (err) {
        toast({ title: mensagemDoErro(err, fixada ? "Não foi possível fixar a tarefa." : "Não foi possível soltar a tarefa."), variant: "destructive" });
        void qc.invalidateQueries({ queryKey: rotas.chaveLista });
      }
    },
    // Minimizar (T-0027): na hora na lista; o servidor guarda para os outros aparelhos da pessoa.
    minimizar: async (id, minimizada) => {
      qc.setQueryData<{ tarefas: Tarefa[] }>(rotas.chaveLista, (antes) => antes && { tarefas: antes.tarefas.map((x) => (x.id === id ? { ...x, minimizada } : x)) });
      try {
        mudou(await customFetch<TarefaInteira>(rotas.url(id, "/minimizar"), { method: "POST", body: JSON.stringify({ minimizada }) }));
      } catch (err) {
        toast({ title: mensagemDoErro(err, minimizada ? "Não foi possível minimizar a tarefa." : "Não foi possível mostrar a tarefa inteira."), variant: "destructive" });
        void qc.invalidateQueries({ queryKey: rotas.chaveLista });
      }
    },
    // A ordem arrastada (as pendentes, de cima para baixo): a lista muda na hora e o servidor guarda.
    ordenar: async (ids) => {
      ordemPelaMao = true;
      qc.setQueryData<{ tarefas: Tarefa[] }>(rotas.chaveLista, (antes) => {
        if (!antes) return antes;
        const pos = new Map(ids.map((id, i) => [id, i]));
        const nas = antes.tarefas.filter((x) => pos.has(x.id)).sort((a, b) => pos.get(a.id)! - pos.get(b.id)!).map((x) => ({ ...x, posicao: pos.get(x.id)! }));
        const fora = antes.tarefas.filter((x) => !pos.has(x.id));
        return { tarefas: [...nas, ...fora] };
      });
      try {
        await customFetch(rotas.url("ordem"), { method: "PUT", body: JSON.stringify({ ids }) });
      } catch (err) {
        toast({ title: mensagemDoErro(err, "Não foi possível salvar a ordem."), variant: "destructive" });
      } finally {
        void qc.invalidateQueries({ queryKey: rotas.chaveLista });
      }
    },
    // Já entregue, a Backend manda «Observação atualizada: …» à sessão (aparece na conversa da tarefa); ainda não
    // enviada, o título (se não foi editado à mão) e a sessão sugerida voltam a «sugerindo…». Trocar o destino (Marina,
    // 31/10/2026) pode tirar a tarefa da lista de quem está na página: a releitura da lista tira o cartão.
    editar: async (id, mudancas) => {
      try {
        mudou(await customFetch<TarefaInteira>(rotas.url(id), { method: "PATCH", body: JSON.stringify(mudancas) }));
        return true;
      } catch (err) {
        toast({ title: mensagemDoErro(err, "Não foi possível salvar a observação."), variant: "destructive" });
        return false;
      }
    },
  };
}

// A lista: em cima a tarefa nova; embaixo as pendentes inteiras e as concluídas numa linha. Clicar numa tarefa a abre.
// A tarefa enviada sobe para o topo, mas a aba NÃO rola junto (Pedro, 31/10/2026, T-0027: «A tarefa pode subir pro
// topo, mas a página continua onde eu estou»). O Chrome ancorava a rolagem no cartão do botão clicado e o perseguia até o
// topo; com a ancoragem dele desligada (overflow-anchor: none no contêiner), aqui o primeiro cartão à vista que NÃO subiu
// fica no mesmo lugar da tela. Vale também no Safari, que não tem ancoragem.
// A ordem que mudou por arrasto à mão: quem arrastou já vê o cartão onde soltou, então a compensação pula essa vez.
let ordemPelaMao = false;
function useManterPosicaoNaLista(marco: RefObject<HTMLElement | null>, ordem: string) {
  const vista = useRef<Array<{ id: string; topo: number; indice: number }>>([]);
  const caixa = () => marco.current?.closest<HTMLElement>('[data-testid="atendimento-todo-lista"]') ?? null;
  const cartoes = (el: HTMLElement) => [...el.querySelectorAll<HTMLElement>('[data-testid^="atendimento-tarefa-T-"]')];
  const anotar = () => {
    const el = caixa();
    if (!el) return;
    const r = el.getBoundingClientRect();
    vista.current = cartoes(el)
      .map((c, indice) => ({ id: c.dataset.testid!.replace("atendimento-tarefa-", ""), topo: c.getBoundingClientRect().top - r.top, indice, fim: c.getBoundingClientRect().bottom - r.top }))
      .filter((c) => c.fim > 0 && c.topo < r.height)
      .map(({ id, topo, indice }) => ({ id, topo, indice }));
  };
  useEffect(() => {
    const el = caixa();
    if (!el) return;
    el.addEventListener("scroll", anotar, { passive: true });
    return () => el.removeEventListener("scroll", anotar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useLayoutEffect(() => {
    const el = caixa();
    if (ordemPelaMao) {
      ordemPelaMao = false;
      anotar();
      return;
    }
    if (el && vista.current.length && el.scrollTop > 0) {
      const r = el.getBoundingClientRect();
      const agora = cartoes(el);
      const indiceDe = new Map(agora.map((c, i) => [c.dataset.testid!.replace("atendimento-tarefa-", ""), i]));
      // O primeiro que estava à vista e não pulou para cima (o enviado pulou; os outros ficam ou descem uma posição).
      const ancora = vista.current.find((v) => (indiceDe.get(v.id) ?? -1) >= v.indice);
      const no = ancora ? agora[indiceDe.get(ancora.id)!] : null;
      if (ancora && no) el.scrollTop += no.getBoundingClientRect().top - r.top - ancora.topo;
    }
    anotar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordem]);
}

function ListaDeTarefas({
  jid,
  tarefas,
  sessoes,
  escolhas,
  onEscolher,
  onAbrir,
  onConversa,
  acoes,
}: {
  /** A conversa aberta (a tarefa nova nasce vinculada a ela); null na /pedro. */
  jid: string | null;
  tarefas: { data?: { tarefas: Tarefa[] }; isLoading: boolean; isError: boolean };
  sessoes: Sessao[];
  escolhas: Record<string, string>;
  onEscolher: (id: string, nome: string) => void;
  onAbrir: (id: string) => void;
  onConversa: (t: Tarefa) => void;
  acoes: AcoesDaTarefa;
}) {
  const mudou = useTarefaMudou();
  // A busca (Marina, 31/10/2026: «Quero uma pesquisa nas tarefas»): no número, no título, na observação, na conversa e na
  // sessão, sem diferenciar maiúscula nem acento, como a das mensagens. Com algo escrito, a «Nova tarefa» sai da frente.
  const [busca, setBusca] = useState("");
  const termo = busca.trim();
  const casa = (t: Tarefa) =>
    comoNaBusca([t.id, t.titulo, t.comentario, t.conversa?.nome, t.conversa?.loja, t.sessao_rotulo, t.sessao_sugerida_rotulo].filter(Boolean).join("\n")).includes(comoNaBusca(termo));
  const todas = tarefas.data?.tarefas ?? [];
  useMinimizadasDoAparelho(tarefas.data?.tarefas, acoes);
  const lista = termo ? todas.filter(casa) : todas;
  const pendentes = lista.filter((t) => t.status !== "concluida");
  // A concluída com resposta não lida (a sessão respondeu depois de concluir) vem primeiro nas concluídas: é ela que acende
  // a bolinha do botão (Pedro, 01/11/2026, T-0027: «está com uma bolinha, mas todas as tarefas estão lidas»).
  const concluidas = lista.filter((t) => t.status === "concluida").sort((a, b) => Number(b.nao_lida) - Number(a.nao_lida));
  const marco = useRef<HTMLDivElement>(null);
  useManterPosicaoNaLista(marco, [...pendentes, ...concluidas].map((t) => t.id).join(","));
  return (
    <>
      <div ref={marco} className="border-b px-4 py-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setBusca("");
            }}
            placeholder="Buscar nas tarefas"
            inputMode="search"
            aria-label="Buscar nas tarefas"
            className="h-8 w-full rounded-lg border bg-background pl-8 pr-8 text-sm outline-none focus:ring-2 focus:ring-ring"
            data-testid="atendimento-todo-busca"
          />
          {busca && (
            <button
              type="button"
              onClick={() => setBusca("")}
              aria-label="Limpar a busca"
              className="absolute right-1 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              data-testid="atendimento-todo-busca-limpar"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      {!termo && <NovaTarefa jid={jid} onCriou={mudou} />}
      {tarefas.isLoading || (tarefas.isError && !tarefas.data) ? (
        <p className="p-4 text-sm text-muted-foreground">Carregando…</p>
      ) : termo && lista.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground" data-testid="atendimento-todo-busca-vazia">
          <p className="font-medium text-foreground">Nenhuma tarefa</p>
          <p className="mt-1 text-xs">Nada com «{termo}» nas tarefas.</p>
        </div>
      ) : lista.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">
          <p className="font-medium text-foreground">Nenhuma tarefa</p>
          <p className="mt-1 text-xs">Escreva acima o que precisa ser resolvido depois.</p>
        </div>
      ) : (
        <>
          {termo && (
            <p className="px-4 pb-1 pt-3 text-xs text-muted-foreground" data-testid="atendimento-todo-busca-contagem">
              {lista.length} {lista.length === 1 ? "tarefa" : "tarefas"} com «{termo}»
            </p>
          )}
          {(() => {
            const cartao = (t: Tarefa, alca?: DragHandleProps) => (
              <CartaoDaTarefa
                key={t.id}
                t={t}
                sessoes={sessoes}
                escolha={escolhas[t.id] ?? null}
                onEscolher={(nome) => onEscolher(t.id, nome)}
                onMudou={mudou}
                onAbrir={() => onAbrir(t.id)}
                onConversa={onConversa}
                acoes={acoes}
                termo={termo}
                alca={alca}
              />
            );
            // Com busca, a lista filtrada não arrasta (a ordem é da lista inteira).
            if (termo) return pendentes.map((t) => cartao(t));
            // As fixadas em cima; cada grupo arrasta dentro dele (fixar/soltar é pelo alfinete).
            const fixadas = pendentes.filter((t) => t.fixada);
            const outras = pendentes.filter((t) => !t.fixada);
            const salvar = (f: Tarefa[], o: Tarefa[]) => acoes.ordenar([...f, ...o].map((t) => t.id));
            return (
              <>
                {fixadas.length > 0 && (
                  <SortableList items={fixadas} handleInside onReorder={(novas) => salvar(novas, outras)} renderItem={(t, _i, alca) => cartao(t, alca)} />
                )}
                {fixadas.length > 0 && outras.length > 0 && <div className="h-1.5 border-b bg-muted/60" aria-hidden="true" data-testid="atendimento-todo-fixadas-fim" />}
                <SortableList items={outras} handleInside onReorder={(novas) => salvar(fixadas, novas)} renderItem={(t, _i, alca) => cartao(t, alca)} />
              </>
            );
          })()}
          {concluidas.length > 0 && (
            <div data-testid="atendimento-todo-concluidas">
              <p className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Concluídas</p>
              {concluidas.map((t) => (
                <LinhaConcluida key={t.id} t={t} onAbrir={() => onAbrir(t.id)} onExcluir={() => acoes.excluir(t.id)} termo={termo} />
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

// A aba, no lugar e no tamanho da do robozinho; no celular, a tela inteira. Em cima a tarefa nova; embaixo todas: as
// pendentes inteiras e as concluídas numa linha. Clicar numa tarefa abre a conversa dela com a sessão.
function PainelDeTarefas({
  jid,
  expandido,
  onExpandir,
  aberta,
  onAbrir,
  onConversa,
  onFechar,
}: {
  /** A conversa aberta; null = aberta pela lista de conversas (celular). */
  jid: string | null;
  expandido: boolean;
  onExpandir: (expandido: boolean) => void;
  /** A tarefa aberta (null = a lista). */
  aberta: string | null;
  onAbrir: (id: string | null) => void;
  /** Clicou na conversa de uma tarefa. */
  onConversa: (t: Tarefa) => void;
  onFechar: () => void;
}) {
  const tarefas = useTarefas(true);
  const sessoes = useSessoes().data ?? [];
  const mudou = useTarefaMudou();
  const acoes = useAcoesDaTarefa(aberta, onAbrir);
  // A sessão escolhida numa tarefa nova (a sugerida até escolher outra): a mesma no cartão e na tarefa aberta.
  const [escolhas, setEscolhas] = useState<Record<string, string>>({});
  const escolher = (id: string, nome: string) => setEscolhas((e) => ({ ...e, [id]: nome }));
  const lista = tarefas.data?.tarefas ?? [];
  const pendentes = lista.filter((t) => t.status !== "concluida");
  // Expandir cobre o chat (o botão só existe do tablet para cima: no celular a aba já é a tela inteira) e o X fecha a aba.
  const fechar = (
    <div className="ml-auto hidden shrink-0 items-center gap-1.5 md:flex">
      {jid !== null && <button
        type="button"
        onClick={() => onExpandir(!expandido)}
        title={expandido ? "Voltar ao lado da conversa" : "Expandir (cobre a conversa)"}
        aria-label={expandido ? "Voltar ao lado da conversa" : "Expandir as tarefas"}
        aria-pressed={expandido}
        className="flex h-8 w-8 items-center justify-center rounded-lg border text-muted-foreground hover:bg-muted hover:text-foreground"
        data-testid="atendimento-todo-expandir"
      >
        {expandido ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
      </button>}
      <button
        type="button"
        onClick={onFechar}
        className="flex h-8 w-8 items-center justify-center rounded-lg border text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label="Fechar o To Do"
        data-testid="atendimento-todo-fechar"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
  return (
    <aside
      className={`fixed inset-0 z-40 flex flex-col bg-background md:static md:z-auto md:border-l ${expandido ? "md:min-w-0 md:flex-1" : "md:w-[380px] md:shrink-0 lg:w-[420px]"}`}
      data-testid="atendimento-todo-painel"
      data-expandido={expandido ? "true" : undefined}
    >
      {aberta ? (
        <TarefaAberta
          key={aberta}
          id={aberta}
          resumo={lista.find((t) => t.id === aberta) ?? null}
          sessoes={sessoes}
          escolha={escolhas[aberta] ?? null}
          onEscolher={(nome) => escolher(aberta, nome)}
          onMudou={mudou}
          onConversa={onConversa}
          onVoltar={() => onAbrir(null)}
          fechar={fechar}
          acoes={acoes}
        />
      ) : (
        <>
          <div className="flex items-center gap-2 border-b px-3 py-2.5">
            {/* No celular, o voltar à esquerda como no robô e na conversa (T-0027): fecha a aba e volta à conversa. */}
            <button type="button" onClick={onFechar} className="-ml-1 rounded p-1 text-muted-foreground hover:text-foreground md:hidden" aria-label={jid ? "Voltar à conversa" : "Voltar às conversas"} data-testid="atendimento-todo-sair">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <ListChecks className="h-5 w-5 shrink-0 text-primary" />
            <span className="text-sm font-semibold">To Do</span>
            {pendentes.length > 0 && (
              <span className="text-xs text-muted-foreground" data-testid="atendimento-todo-pendentes">
                {pendentes.length} {pendentes.length === 1 ? "pendente" : "pendentes"}
              </span>
            )}
            {fechar}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto [overflow-anchor:none]" data-testid="atendimento-todo-lista">
            <ListaDeTarefas jid={jid} tarefas={tarefas} sessoes={sessoes} escolhas={escolhas} onEscolher={escolher} onAbrir={onAbrir} onConversa={onConversa} acoes={acoes} />
          </div>
        </>
      )}
    </aside>
  );
}

// ─── A página do Pedro, /pedro (Marina, 31/10/2026: «Pedro ter uma página só para ele /pedro onde ele veria apenas essas
// tarefas que eu designar à ele. Lá ele teria as funções que já tenho, inclusive de criar novas tarefas») ───────────────────
// As tarefas dele: as que a Marina deu, as que ele criou e as que ele deu a ela. Pede a conta de suporte, como a /results.
// No computador, a lista e a tarefa ficam lado a lado; no celular, uma por vez. A conversa da tarefa abre na Central.
const CHAVE_DA_TAREFA_DO_PEDRO = "central-pedro-tarefa";
export function PaginaDoPedro() {
  return (
    <PessoaDaPagina.Provider value="pedro">
      <TarefasDoPedro />
    </PessoaDaPagina.Provider>
  );
}

function TarefasDoPedro() {
  const [aberta, setAberta] = useState<string | null>(() => lerGuardado<string>(CHAVE_DA_TAREFA_DO_PEDRO));
  useEffect(() => guardar(CHAVE_DA_TAREFA_DO_PEDRO, aberta), [aberta]);
  const tarefas = useTarefas(true);
  const sessoes = useSessoes().data ?? [];
  const mudou = useTarefaMudou();
  const acoes = useAcoesDaTarefa(aberta, setAberta);
  const [escolhas, setEscolhas] = useState<Record<string, string>>({});
  const escolher = (id: string, nome: string) => setEscolhas((e) => ({ ...e, [id]: nome }));
  const lista = tarefas.data?.tarefas ?? [];
  const pendentes = lista.filter((t) => t.status !== "concluida").length;
  const naoVistas = lista.filter((t) => t.nao_lida).length;
  // A tarefa guardada que não está mais na lista (excluída) não fica aberta.
  useEffect(() => {
    if (aberta && tarefas.data && !tarefas.data.tarefas.some((t) => t.id === aberta)) setAberta(null);
  }, [aberta, tarefas.data]);
  // A conversa da tarefa abre na Central, noutra aba, já nela.
  const abrirConversa = (t: Tarefa) => {
    if (!t.jid) return;
    guardar(CHAVE_DA_ABERTA, t.jid);
    window.open(`${import.meta.env.BASE_URL}atendimento`, "_blank");
  };
  useEffect(() => {
    const antes = document.title;
    document.title = "Tarefas do Pedro";
    return () => {
      document.title = antes;
    };
  }, []);
  // A página ocupa a altura que sobra da tela (como a Central).
  const raiz = useRef<HTMLDivElement>(null);
  const [acima, setAcima] = useState(0);
  useLayoutEffect(() => {
    const medir = () => setAcima(Math.max(0, Math.round((raiz.current?.getBoundingClientRect().top ?? 0) + window.scrollY)));
    medir();
    window.addEventListener("resize", medir);
    return () => window.removeEventListener("resize", medir);
  }, []);
  const fechar = (
    <button
      type="button"
      onClick={() => setAberta(null)}
      className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg border text-muted-foreground hover:bg-muted hover:text-foreground md:flex"
      aria-label="Fechar a tarefa"
      data-testid="atendimento-todo-fechar"
    >
      <X className="h-4 w-4" />
    </button>
  );
  return (
    <div ref={raiz} className="flex flex-col bg-background" style={{ height: `calc(100svh - ${acima}px)` }} data-testid="pedro">
      <div className="flex items-center gap-3 border-b px-4 py-3">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${COR_DA_PESSOA.pedro}`} aria-hidden="true">
          P
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold">Tarefas do Pedro</h1>
          <p className="text-xs text-muted-foreground" data-testid="pedro-resumo">
            {pendentes} {pendentes === 1 ? "pendente" : "pendentes"}
            {naoVistas > 0 && ` · ${naoVistas} ${naoVistas === 1 ? "não vista" : "não vistas"}`}
          </p>
        </div>
      </div>
      <div className="mx-auto flex min-h-0 w-full max-w-6xl flex-1 md:gap-4 md:p-4">
        <div className={`min-h-0 flex-col overflow-hidden bg-background md:flex md:w-[420px] md:shrink-0 md:rounded-xl md:border ${aberta ? "hidden" : "flex flex-1 md:flex-none"}`}>
          <div className="min-h-0 flex-1 overflow-y-auto [overflow-anchor:none]" data-testid="atendimento-todo-lista">
            <ListaDeTarefas jid={null} tarefas={tarefas} sessoes={sessoes} escolhas={escolhas} onEscolher={escolher} onAbrir={setAberta} onConversa={abrirConversa} acoes={acoes} />
          </div>
        </div>
        <div className={`min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background md:flex md:rounded-xl md:border ${aberta ? "flex" : "hidden"}`} data-testid="atendimento-todo-painel">
          {aberta ? (
            <TarefaAberta
              key={aberta}
              id={aberta}
              resumo={lista.find((t) => t.id === aberta) ?? null}
              sessoes={sessoes}
              escolha={escolhas[aberta] ?? null}
              onEscolher={(nome) => escolher(aberta, nome)}
              onMudou={mudou}
              onConversa={abrirConversa}
              onVoltar={() => setAberta(null)}
              fechar={fechar}
              acoes={acoes}
            />
          ) : (
            <div className="m-auto p-6 text-center" data-testid="pedro-escolha">
              <p className="text-sm font-medium">Escolha uma tarefa</p>
              <p className="mt-1 text-xs text-muted-foreground">A conversa com a sessão e os comentários da Marina aparecem aqui.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// O campo da tarefa nova cresce com o texto (Pedro, 31/10/2026: «deve… ir crescendo ao digitar textos grandes») até 60%
// da altura visível da aba (no celular com o teclado aberto, da parte acima dele); passou disso, rola por dentro.
function crescerNaAba(el: HTMLTextAreaElement) {
  const aba = el.closest("aside");
  const limite = Math.round(0.6 * Math.min(aba?.clientHeight ?? window.innerHeight, window.visualViewport?.height ?? window.innerHeight));
  const topo = el.scrollTop;
  el.style.overflowY = "hidden";
  el.style.height = "auto";
  const inteiro = el.scrollHeight + el.offsetHeight - el.clientHeight;
  el.style.height = `${Math.min(inteiro, limite)}px`;
  el.style.overflowY = inteiro > limite ? "auto" : "hidden";
  el.scrollTop = topo;
}

// Para quem é a tarefa: quem está na página primeiro, depois o outro (na «Nova tarefa» e na edição).
function EscolherPara({ valor, onMudar, testid }: { valor: Pessoa; onMudar: (p: Pessoa) => void; testid: string }) {
  const { pessoa } = useRotasDaTarefa();
  return (
    <div className="flex items-center gap-1.5 text-xs" role="group" aria-label="Para quem é a tarefa" data-testid={testid}>
      <span className="text-muted-foreground">Para</span>
      {[pessoa, outraPessoa(pessoa)].map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onMudar(p)}
          aria-pressed={valor === p}
          className={`rounded-full border px-2.5 py-0.5 font-medium transition-colors ${valor === p ? "border-foreground bg-foreground text-background" : "bg-background text-muted-foreground hover:bg-muted"}`}
          data-testid={`${testid}-${p}`}
        >
          {PESSOAS[p].nome}
        </button>
      ))}
    </div>
  );
}

// «Nova tarefa»: o comentário (com a correção e o rascunho guardado, por conversa), a chave do vínculo (ligada, só com
// conversa aberta) e para quem é (Marina, 31/10/2026: ela cria para o Pedro e ele para ela; o padrão é quem está na página).
function NovaTarefa({ jid, onCriou }: { jid: string | null; onCriou: (t: TarefaInteira) => void }) {
  const rotas = useRotasDaTarefa();
  // O rascunho é um só por pessoa (Pedro, 03/11/2026, T-0027: «mantenha o rascunho mesmo que eu clique em outro lugar e
  // feche a tela do TO DO»): volta onde quer que a aba abra (na conversa, pela lista ou pelo topo). Antes era por
  // conversa, e abrir a aba por outro caminho parecia perder o texto. Guarda a conversa onde ele começou: aberto noutra,
  // «Vinculada à conversa» vem desligada (a tarefa não se liga calada à conversa errada).
  const chaveDoRascunho = `central-tarefa-nova-rascunho:${rotas.pessoa}`;
  const chaveAntiga = rotas.pessoa === "marina" ? `central-tarefa-nova:${jid}` : `central-tarefa-nova-${rotas.pessoa}:${jid ?? "sem-conversa"}`;
  const [inicio] = useState(() => {
    const g = lerGuardado<{ texto: string; jid: string | null }>(chaveDoRascunho);
    if (g?.texto?.trim()) return g;
    const antigo = lerTexto(chaveAntiga);
    return { texto: antigo, jid };
  });
  const [para, setPara] = useState<Pessoa>(rotas.pessoa);
  const [texto, setTexto] = useState(inicio.texto);
  useEffect(() => {
    if (!texto.trim()) {
      guardar(chaveDoRascunho, null);
      return;
    }
    // A conversa de origem fica a do começo do rascunho; texto novo começa aqui.
    const atual = lerGuardado<{ texto: string; jid: string | null }>(chaveDoRascunho);
    guardar(chaveDoRascunho, { texto, jid: atual?.texto?.trim() ? atual.jid : jid });
    guardarTexto(chaveAntiga, "");
  }, [chaveDoRascunho, chaveAntiga, texto, jid]);
  const campo = useRef<HTMLTextAreaElement>(null);
  const correcao = useCorrecao(texto, setTexto, campo);
  // Sem texto (depois do Salvar), volta ao tamanho de antes.
  useLayoutEffect(() => {
    if (campo.current) crescerNaAba(campo.current);
  }, [texto]);
  useEffect(() => {
    const medir = () => campo.current && crescerNaAba(campo.current);
    window.addEventListener("resize", medir);
    window.visualViewport?.addEventListener("resize", medir);
    return () => {
      window.removeEventListener("resize", medir);
      window.visualViewport?.removeEventListener("resize", medir);
    };
  }, []);
  const [vinculada, setVinculada] = useState(() => !inicio.texto.trim() || chaveDoJid(inicio.jid ?? "") === chaveDoJid(jid ?? ""));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const ditado = useDitadoNoCampo(texto, setTexto, campo, setErro);
  const anexos = useAnexosColados(setErro);
  const salvar = async () => {
    const t = anexos.junto(texto);
    if (!t || salvando || anexos.subindo) return;
    setSalvando(true);
    setErro(null);
    try {
      const r = await customFetch<TarefaInteira>(rotas.lista, {
        method: "POST",
        body: JSON.stringify({ comentario: t, ...(vinculada && jid ? { jid } : {}), ...(para !== rotas.pessoa ? { para } : {}) }),
      });
      setTexto("");
      anexos.limpar();
      setVinculada(true);
      setPara(rotas.pessoa);
      onCriou(r);
    } catch (err) {
      setErro(mensagemDoErro(err, "Não foi possível salvar a tarefa."));
    } finally {
      setSalvando(false);
    }
  };
  return (
    <div className="space-y-2 border-b bg-muted/30 px-4 py-3" data-testid="atendimento-todo-nova">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold">Nova tarefa</p>
        <EscolherPara valor={para} onMudar={setPara} testid="atendimento-todo-para" />
      </div>
      <textarea
        ref={campo}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        {...correcao}
        readOnly={ditado.ativo}
        onPaste={anexos.colar}
        onKeyDown={(e) => {
          correcao.onKeyDown(e);
          if (e.defaultPrevented) return;
          if (ehCtrlEnter(e)) {
            e.preventDefault();
            if (!ditado.ativo) void salvar();
          }
        }}
        rows={2}
        placeholder="O que precisa ser resolvido depois?"
        className="min-h-16 w-full resize-none rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
        data-testid="atendimento-todo-texto"
      />
      <MiniaturasColadas anexos={anexos.anexos} onTirar={anexos.tirar} />
      {erro && (
        <p className="text-sm text-destructive" role="alert">
          {erro}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        {jid && (
          <label className="flex items-center gap-2 text-xs">
            <Switch checked={vinculada} onCheckedChange={setVinculada} aria-label="Vinculada à conversa" data-testid="atendimento-todo-vinculada" />
            Vinculada à conversa
          </label>
        )}
        {ditado.ativo && <span className="text-[11px]"><AvisoDoDitado estado={ditado.estado} /></span>}
        <span className="ml-auto" />
        <BotaoDeImagem onEscolher={anexos.escolher} desabilitado={ditado.ativo} tamanho="pequeno" testid="atendimento-todo-imagem" />
        <BotaoDoDitado estado={ditado.estado} onClick={ditado.alternar} pequeno testid="atendimento-todo-ditar" />
        <button
          type="button"
          onClick={() => void salvar()}
          disabled={(!texto.trim() && !anexos.temImagem) || salvando || ditado.ativo || anexos.subindo}
          className="flex h-8 items-center gap-1.5 rounded-lg bg-primary px-4 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          data-testid="atendimento-todo-salvar"
        >
          {salvando && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Salvar
        </button>
      </div>
    </div>
  );
}

interface AcoesDaTarefa {
  excluir: (id: string) => Promise<boolean>;
  marcarNaoLida: (id: string) => void;
  fixar: (id: string, fixada: boolean) => void;
  minimizar: (id: string, minimizada: boolean) => void;
  ordenar: (ids: string[]) => void;
  editar: (id: string, mudancas: { comentario?: string; para?: Pessoa }) => Promise<boolean>;
}

// A tarefa pendente na lista. Clicar nela (ou no comentário) abre a conversa com a sessão; os controles ficam no cartão.
function CartaoDaTarefa({
  t,
  sessoes,
  escolha,
  onEscolher,
  onMudou,
  onAbrir,
  onConversa,
  acoes,
  termo = "",
  alca,
}: {
  t: Tarefa;
  sessoes: Sessao[];
  escolha: string | null;
  onEscolher: (nome: string) => void;
  onMudou: (t: TarefaInteira) => void;
  onAbrir: () => void;
  onConversa: (t: Tarefa) => void;
  acoes: AcoesDaTarefa;
  /** O que está na busca: fica em destaque no título e na observação. */
  termo?: string;
  /** A alça de arrastar (sem busca); ausente, o cartão não arrasta. */
  alca?: DragHandleProps;
}) {
  const [editando, setEditando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const minimizada = !!t.minimizada;
  const alternarMinimizada = () => acoes.minimizar(t.id, !minimizada);
  const botaoMinimizar = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        alternarMinimizada();
      }}
      title={minimizada ? "Mostrar a tarefa inteira" : "Minimizar (só o título e para quem é)"}
      aria-label={minimizada ? "Mostrar a tarefa inteira" : "Minimizar a tarefa"}
      aria-expanded={!minimizada}
      className="-mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
      data-testid="atendimento-tarefa-minimizar"
    >
      {minimizada ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
    </button>
  );
  // A alça de arrastar (T-0027: «reordenar as tarefas») e o alfinete da fixada, à esquerda do título.
  const inicio = (
    <>
      {alca && (
        <button
          type="button"
          ref={alca.ref}
          {...alca.listeners}
          {...alca.attributes}
          onClick={(e) => e.stopPropagation()}
          title="Arrastar para mudar a ordem"
          aria-label="Arrastar para mudar a ordem"
          className="-ml-2 flex h-6 w-5 shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted-foreground/70 hover:text-foreground active:cursor-grabbing"
          data-testid="atendimento-tarefa-arrastar"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      {t.fixada && <Pin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-label="Fixada" data-testid="atendimento-tarefa-fixada" />}
    </>
  );
  // Minimizada (Pedro, 31/10/2026, T-0027: «minimizar alguma tarefa e ficar aparecendo só o título e para quem é»): uma
  // linha com o título, para quem é, a bolinha de não lida (resposta nova não some) e o número. Tocar abre, como antes.
  if (minimizada && !editando) {
    return (
      <div onClick={onAbrir} className="flex min-w-0 cursor-pointer items-center gap-2 border-b bg-background px-4 py-2 hover:bg-muted/40" data-testid={`atendimento-tarefa-${t.id}`} data-minimizada="true">
        {inicio}
        <span className="min-w-0 flex-1 truncate text-sm font-medium" data-testid="atendimento-tarefa-minimizada-titulo">
          {t.titulo ? <Destaque texto={t.titulo} termo={termo} /> : <span className="italic text-muted-foreground">{semLinhasDeImagem(t.comentario)}</span>}
        </span>
        <span className="shrink-0 rounded-full border border-violet-300 px-1.5 py-px text-[10px] font-semibold text-violet-700 dark:border-violet-700 dark:text-violet-300" data-testid="atendimento-tarefa-minimizada-para">
          {maiuscula(PESSOAS[t.para ?? "marina"].paraEla)}
        </span>
        {t.nao_lida && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Não lida" data-testid="atendimento-tarefa-nao-lida" />}
        <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{t.id}</span>
        {botaoMinimizar}
      </div>
    );
  }
  return (
    <div
      onClick={editando ? undefined : onAbrir}
      className={`space-y-2 border-b bg-background px-4 py-3 ${editando ? "" : "cursor-pointer hover:bg-muted/40"}`}
      data-testid={`atendimento-tarefa-${t.id}`}
    >
      <div className="flex min-w-0 items-start gap-2">
        {inicio}
        <TituloDaTarefa t={t} onMudou={onMudou} termo={termo} />
        {t.nao_lida && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Não lida" data-testid="atendimento-tarefa-nao-lida" />}
        <span className="shrink-0 pt-0.5 font-mono text-[11px] text-muted-foreground">{t.id}</span>
        {botaoMinimizar}
      </div>
      <SelosDaTarefa t={t} />
      <ConversaDaTarefa t={t} onMudou={onMudou} onConversa={onConversa} />
      {editando ? (
        <EditarComentario
          t={t}
          onCancelar={() => setEditando(false)}
          onSalvar={async (mudancas) => {
            if (await acoes.editar(t.id, mudancas)) setEditando(false);
          }}
        />
      ) : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAbrir();
          }}
          className="block w-full text-left text-sm text-muted-foreground"
          data-testid="atendimento-tarefa-comentario"
        >
          <span className="line-clamp-2 whitespace-pre-wrap break-words">
            <Destaque texto={semLinhasDeImagem(t.comentario)} termo={termo} />
          </span>
        </button>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <ChipDaTarefa t={t} />
        <SessaoDaTarefa t={t} sessoes={sessoes} escolha={escolha} onEscolher={onEscolher} onMudou={onMudou} />
        <BotoesDaTarefa editando={editando} fixada={!!t.fixada} onFixar={() => acoes.fixar(t.id, !t.fixada)} onEditar={() => setEditando(true)} onNaoLida={() => acoes.marcarNaoLida(t.id)} onExcluir={() => setExcluindo(true)} />
      </div>
      {excluindo && <ConfirmarExclusao t={t} onCancelar={() => setExcluindo(false)} onConfirmar={() => acoes.excluir(t.id)} />}
    </div>
  );
}

// Até 01/11/2026 a tarefa minimizada ficava só no aparelho (localStorage, por pessoa). As que estavam guardadas aqui sobem
// uma vez para o servidor e a chave sai: o que foi minimizado no computador aparece minimizado no celular.
function useMinimizadasDoAparelho(lista: Tarefa[] | undefined, acoes: AcoesDaTarefa) {
  const { pessoa } = useRotasDaTarefa();
  const acoesRef = useRef(acoes);
  acoesRef.current = acoes;
  useEffect(() => {
    if (!lista) return;
    const chave = `central-tarefas-minimizadas-${pessoa}`;
    const ids = lerGuardado<string[]>(chave);
    if (!ids) return;
    guardar(chave, null);
    for (const t of lista) if (ids.includes(t.id) && !t.minimizada) acoesRef.current.minimizar(t.id, true);
  }, [lista, pessoa]);
}

// As ações da tarefa em ícones separados (como as dos balões): editar a observação, marcar como não lida e excluir.
function BotoesDaTarefa({
  editando,
  grande,
  fixada,
  onFixar,
  onEditar,
  onNaoLida,
  onExcluir,
}: {
  editando: boolean;
  grande?: boolean;
  fixada?: boolean;
  onFixar?: () => void;
  onEditar: () => void;
  onNaoLida: () => void;
  onExcluir: () => void;
}) {
  const tam = grande ? "h-8 w-8" : "h-7 w-7";
  const botao = `flex ${tam} shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground`;
  return (
    <div className={`flex items-center gap-0.5 ${grande ? "" : "ml-auto"}`} onClick={(e) => e.stopPropagation()}>
      {onFixar && (
        <button type="button" onClick={onFixar} aria-pressed={!!fixada} title={fixada ? "Soltar do topo" : "Fixar no topo"} aria-label={fixada ? "Soltar do topo" : "Fixar no topo"} className={`${botao} ${fixada ? "text-primary" : ""}`} data-testid="atendimento-tarefa-fixar">
          {fixada ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
        </button>
      )}
      <button type="button" onClick={onEditar} aria-pressed={editando} title="Editar a observação" aria-label="Editar a observação" className={`${botao} ${editando ? "bg-muted text-foreground" : ""}`} data-testid="atendimento-tarefa-editar">
        <Pencil className="h-3.5 w-3.5" />
      </button>
      <button type="button" onClick={onNaoLida} title="Marcar como não lida" aria-label="Marcar como não lida" className={botao} data-testid="atendimento-tarefa-marcar-nao-lida">
        <MessageSquareDot className="h-3.5 w-3.5" />
      </button>
      <BotaoExcluir grande={grande} onClick={onExcluir} />
    </div>
  );
}

function BotaoExcluir({ grande, onClick }: { grande?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title="Excluir a tarefa"
      aria-label="Excluir a tarefa"
      className={`flex ${grande ? "h-8 w-8" : "h-7 w-7"} shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive`}
      data-testid="atendimento-tarefa-excluir"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}

// Para quem é e de quem veio (só na tarefa entre os dois) e o comentário novo do outro.
function SelosDaTarefa({ t }: { t: Tarefa }) {
  const { pessoa } = useRotasDaTarefa();
  const outra = outraPessoa(pessoa);
  const entreOsDois = compartilhada(t);
  if (!entreOsDois && !t.comentario_novo) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="atendimento-tarefa-selos">
      {entreOsDois && (
        <span className="rounded-full border border-violet-300 px-1.5 py-px text-[10px] font-semibold text-violet-700 dark:border-violet-700 dark:text-violet-300" data-testid="atendimento-tarefa-de-para">
          {maiuscula(t.para === outra ? PESSOAS[outra].paraEla : PESSOAS[outra].dela)}
        </span>
      )}
      {t.comentario_novo && (
        <span className="inline-flex items-center gap-1 rounded-full bg-violet-600 px-2 py-0.5 text-[11px] font-semibold text-white" data-testid="atendimento-tarefa-comentario-novo">
          <MessageSquareText className="h-3 w-3" />
          comentário novo {PESSOAS[outra].dela}
        </span>
      )}
    </div>
  );
}

// «Excluir a tarefa?» no padrão do «Apagar para todos?» dos balões. Com a sessão trabalhando nela (o «entregue», o amarelo
// do robozinho), a pergunta diz qual (Pedro, 31/10/2026: «avisa que tem sessão trabalhando na confirmação de exclusão»).
function ConfirmarExclusao({ t, onCancelar, onConfirmar }: { t: Tarefa | null; onCancelar: () => void; onConfirmar: () => Promise<boolean> }) {
  const [indo, setIndo] = useState(false);
  const { pessoa } = useRotasDaTarefa();
  const trabalhando = t?.status === "entregue";
  // A tarefa entre os dois some para os dois.
  const tambem = t && compartilhada(t) ? PESSOAS[outraPessoa(pessoa)].paraEla : null;
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-1.5 text-xs" onClick={(e) => e.stopPropagation()} data-testid="atendimento-tarefa-excluir-pergunta">
      <span className="min-w-0">
        <span className="font-medium">Excluir a tarefa?</span>
        {trabalhando && (
          <span className="text-amber-800 dark:text-amber-300" data-testid="atendimento-tarefa-excluir-trabalhando">
            {" "}
            {t.sessao_rotulo ? `A sessão ${t.sessao_rotulo} está trabalhando nela agora.` : "Uma sessão está trabalhando nela agora."}
          </span>
        )}
        {tambem && <span data-testid="atendimento-tarefa-excluir-os-dois"> Ela some também {tambem}.</span>}
      </span>
      <button type="button" onClick={onCancelar} disabled={indo} className="rounded-md border bg-background px-2 py-0.5 hover:bg-muted" data-testid="atendimento-tarefa-excluir-cancelar">
        Cancelar
      </button>
      <button
        type="button"
        disabled={indo}
        onClick={async () => {
          setIndo(true);
          const ok = await onConfirmar();
          if (!ok) setIndo(false);
        }}
        className="inline-flex items-center gap-1 rounded-md bg-destructive px-2 py-0.5 font-semibold text-destructive-foreground hover:bg-destructive/90"
        data-testid="atendimento-tarefa-excluir-confirmar"
      >
        {indo && <Loader2 className="h-3 w-3 animate-spin" />}
        Excluir
      </button>
    </div>
  );
}

// A observação em edição: o destino (Marina, 31/10/2026: «na edicao apareca a escolha de Marina e Pedro tambem para poder
// alterar e salvar»), o campo com a correção e crescendo como o da «Nova tarefa», Salvar e Cancelar (Ctrl+Enter salva,
// Esc cancela). Vai só o que mudou.
function EditarComentario({ t, onSalvar, onCancelar }: { t: Tarefa; onSalvar: (mudancas: { comentario?: string; para?: Pessoa }) => Promise<void>; onCancelar: () => void }) {
  const { pessoa } = useRotasDaTarefa();
  const paraAntes = t.para ?? pessoa;
  const [para, setPara] = useState<Pessoa>(paraAntes);
  const [texto, setTexto] = useState(t.comentario);
  const campo = useRef<HTMLTextAreaElement>(null);
  const correcao = useCorrecao(texto, setTexto, campo);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const ditado = useDitadoNoCampo(texto, setTexto, campo, setErro);
  const anexos = useAnexosColados(setErro);
  useLayoutEffect(() => {
    if (campo.current) crescerNaAba(campo.current);
  }, [texto]);
  // Abre com o cursor no fim, para continuar escrevendo.
  useEffect(() => {
    const el = campo.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    el.scrollTop = el.scrollHeight;
  }, []);
  const mudouTexto = texto.trim() !== t.comentario.trim() || anexos.temImagem;
  const mudouPara = para !== paraAntes;
  const mudou = mudouTexto || mudouPara;
  const salvar = async () => {
    if (!anexos.junto(texto) || !mudou || salvando || anexos.subindo) return;
    setSalvando(true);
    await onSalvar({ ...(mudouTexto ? { comentario: anexos.junto(texto) } : {}), ...(mudouPara ? { para } : {}) });
    setSalvando(false);
  };
  return (
    <div className="space-y-2" onClick={(e) => e.stopPropagation()} data-testid="atendimento-tarefa-editando">
      <div className="flex justify-end">
        <EscolherPara valor={para} onMudar={setPara} testid="atendimento-tarefa-editar-para" />
      </div>
      <textarea
        ref={campo}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        {...correcao}
        readOnly={ditado.ativo}
        onPaste={anexos.colar}
        onKeyDown={(e) => {
          correcao.onKeyDown(e);
          if (e.defaultPrevented) return;
          if (ehCtrlEnter(e)) {
            e.preventDefault();
            if (!ditado.ativo) void salvar();
          } else if (e.key === "Escape") {
            e.preventDefault();
            onCancelar();
          }
        }}
        rows={2}
        className="min-h-16 w-full resize-none rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
        aria-label="Observação da tarefa"
        data-testid="atendimento-tarefa-comentario-campo"
      />
      <MiniaturasColadas anexos={anexos.anexos} onTirar={anexos.tirar} />
      {erro && (
        <p className="text-sm text-destructive" role="alert">
          {erro}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        {ditado.ativo && <span className="mr-auto text-[11px]"><AvisoDoDitado estado={ditado.estado} /></span>}
        <BotaoDeImagem onEscolher={anexos.escolher} desabilitado={ditado.ativo} tamanho="pequeno" testid="atendimento-tarefa-comentario-imagem" />
        <BotaoDoDitado estado={ditado.estado} onClick={ditado.alternar} pequeno testid="atendimento-tarefa-comentario-ditar" />
        <button type="button" onClick={onCancelar} disabled={salvando} className="h-8 rounded-lg border bg-background px-3 text-xs font-medium hover:bg-muted" data-testid="atendimento-tarefa-comentario-cancelar">
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => void salvar()}
          disabled={(!texto.trim() && !anexos.temImagem) || !mudou || salvando || ditado.ativo || anexos.subindo}
          className="flex h-8 items-center gap-1.5 rounded-lg bg-primary px-4 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          data-testid="atendimento-tarefa-comentario-salvar"
        >
          {salvando && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Salvar
        </button>
      </div>
    </div>
  );
}

// A concluída, numa linha (✓ e o título; clicar abre); a única ação é Excluir.
function LinhaConcluida({ t, onAbrir, onExcluir, termo = "" }: { t: Tarefa; onAbrir: () => void; onExcluir: () => Promise<boolean>; termo?: string }) {
  const [excluindo, setExcluindo] = useState(false);
  return (
    <div className="border-b" data-testid={`atendimento-tarefa-${t.id}`}>
      <div className="flex items-center gap-2 px-4 py-1.5 text-sm text-muted-foreground hover:bg-muted/60">
        <button type="button" onClick={onAbrir} className="flex min-w-0 flex-1 items-center gap-2 py-0.5 text-left" data-testid="atendimento-tarefa-abrir">
          <Check className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-label="Concluída" />
          <span className={`min-w-0 flex-1 truncate ${t.nao_lida ? "font-semibold text-foreground" : ""}`}>
            <Destaque texto={t.titulo ?? semLinhasDeImagem(t.comentario)} termo={termo} />
          </span>
          {t.nao_lida && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Não lida" data-testid="atendimento-tarefa-nao-lida" />}
        </button>
        <BotaoExcluir onClick={() => setExcluindo(true)} />
      </div>
      {excluindo && (
        <div className="px-4 pb-2">
          <ConfirmarExclusao t={t} onCancelar={() => setExcluindo(false)} onConfirmar={onExcluir} />
        </div>
      )}
    </div>
  );
}

// O título (Pedro: «que eu posso editar depois»): clicar edita ali; Enter ou sair do campo salva, Esc desiste.
function TituloDaTarefa({ t, grande, onMudou, termo = "" }: { t: Tarefa; grande?: boolean; onMudou: (t: TarefaInteira) => void; termo?: string }) {
  const { toast } = useToast();
  const rotas = useRotasDaTarefa();
  const [editando, setEditando] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  // O Esc tira o campo da tela, e o blur que pode vir junto não salva.
  const desistiu = useRef(false);
  const indo = useRef(false);
  const fonte = grande ? "text-base" : "text-sm";
  const salvar = async () => {
    const v = (editando ?? "").trim();
    if (indo.current) return;
    if (desistiu.current || !v || v === t.titulo) {
      setEditando(null);
      return;
    }
    indo.current = true;
    setSalvando(true);
    try {
      onMudou(await customFetch<TarefaInteira>(rotas.url(t.id), { method: "PATCH", body: JSON.stringify({ titulo: v }) }));
      setEditando(null);
    } catch (err) {
      toast({ title: mensagemDoErro(err, "Não foi possível salvar o título."), variant: "destructive" });
    } finally {
      indo.current = false;
      setSalvando(false);
    }
  };
  if (editando !== null) {
    return (
      <input
        autoFocus
        value={editando}
        maxLength={200}
        disabled={salvando}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setEditando(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void salvar();
          } else if (e.key === "Escape") {
            e.preventDefault();
            desistiu.current = true;
            setEditando(null);
          }
        }}
        onBlur={() => void salvar()}
        className={`min-w-0 flex-1 rounded-md border bg-background px-2 py-0.5 font-semibold outline-none focus:ring-2 focus:ring-ring ${fonte}`}
        aria-label="Título da tarefa"
        data-testid="atendimento-tarefa-titulo-campo"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        desistiu.current = false;
        setEditando(t.titulo ?? "");
      }}
      title="Editar o título"
      className={`min-w-0 flex-1 break-words text-left font-semibold decoration-dotted underline-offset-4 hover:underline ${fonte}`}
      data-testid="atendimento-tarefa-titulo"
    >
      {t.titulo ? <Destaque texto={t.titulo} termo={termo} /> : <span className="font-normal italic text-muted-foreground">sugerindo título…</span>}
    </button>
  );
}

// A conversa da tarefa (nome e Nº; clicar abre) e a chave do vínculo: desligada, a tarefa fica «sem conversa» e guarda o
// jid para religar.
function ConversaDaTarefa({ t, onMudou, onConversa }: { t: Tarefa; onMudou: (t: TarefaInteira) => void; onConversa: (t: Tarefa) => void }) {
  const { toast } = useToast();
  const rotas = useRotasDaTarefa();
  const [indo, setIndo] = useState<boolean | null>(null);
  const ligada = indo ?? t.vinculada;
  const mudar = async (v: boolean) => {
    setIndo(v);
    try {
      onMudou(await customFetch<TarefaInteira>(rotas.url(t.id), { method: "PATCH", body: JSON.stringify({ vinculada: v }) }));
    } catch (err) {
      toast({ title: mensagemDoErro(err, "Não foi possível mudar o vínculo."), variant: "destructive" });
    } finally {
      setIndo(null);
    }
  };
  return (
    <div className="flex items-center gap-3 text-xs" onClick={(e) => e.stopPropagation()}>
      {ligada && t.conversa ? (
        <button
          type="button"
          onClick={() => onConversa(t)}
          className="flex min-w-0 items-center gap-1.5 font-medium hover:underline"
          title="Abrir a conversa"
          data-testid="atendimento-tarefa-conversa"
        >
          <MessageSquareText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate">{t.conversa.loja || t.conversa.nome}</span>
          {t.conversa.numero && <Selo n={t.conversa.numero.n} />}
        </button>
      ) : (
        <span className="text-muted-foreground" data-testid="atendimento-tarefa-sem-conversa">
          sem conversa
        </span>
      )}
      <label className="ml-auto flex shrink-0 items-center gap-1.5 text-muted-foreground">
        <Switch
          checked={ligada}
          disabled={!t.jid || indo !== null}
          onCheckedChange={(v) => void mudar(v)}
          aria-label="Vinculada à conversa"
          data-testid="atendimento-tarefa-vinculada"
        />
        Vinculada à conversa
      </label>
    </div>
  );
}

function ChipDaTarefa({ t }: { t: Tarefa }) {
  return (
    <span className={`flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${corDaTarefa(t)}`} data-testid="atendimento-tarefa-estado" data-estado={t.status}>
      {t.status === "concluida" && <Check className="h-3 w-3" />}
      {ESTADO_DA_TAREFA[t.status] ?? t.status}
    </span>
  );
}

// Na tarefa nova, o dropdown da sessão (na sugerida pelo JEV até escolher outra) e o Enviar; depois, a sessão dela.
function SessaoDaTarefa({
  t,
  sessoes,
  escolha,
  onEscolher,
  onMudou,
}: {
  t: Tarefa;
  sessoes: Sessao[];
  escolha: string | null;
  onEscolher: (nome: string) => void;
  onMudou: (t: TarefaInteira) => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const rotas = useRotasDaTarefa();
  const [enviando, setEnviando] = useState(false);
  if (t.status !== "nova") {
    return t.sessao_rotulo ? (
      <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground" data-testid="atendimento-tarefa-sessao-enviada">
        <Bot className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{t.sessao_rotulo}</span>
      </span>
    ) : null;
  }
  const nome = escolha ?? t.sessao_sugerida;
  const s = sessoes.find((x) => x.nome === nome);
  const rotulo = s?.rotulo ?? (nome === t.sessao_sugerida ? t.sessao_sugerida_rotulo : nome);
  const enviar = async () => {
    if (!nome || enviando) return;
    setEnviando(true);
    try {
      onMudou(await customFetch<TarefaInteira>(rotas.url(t.id, "/enviar"), { method: "POST", body: JSON.stringify({ sessao: nome }) }));
    } catch (err) {
      toast({ title: mensagemDoErro(err, "Não foi possível enviar a tarefa."), variant: "destructive" });
      void qc.invalidateQueries({ queryKey: rotas.chaveLista });
    } finally {
      setEnviando(false);
    }
  };
  return (
    <div className="flex min-w-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="flex h-8 min-w-0 items-center gap-1.5 rounded-lg border bg-background px-2.5 text-xs hover:bg-muted" title="Escolher a sessão" data-testid="atendimento-tarefa-sessao">
            {rotulo ? (
              <span className="truncate">
                {rotulo}
                {s && !s.ativa && <span className="text-muted-foreground"> (fechada)</span>}
              </span>
            ) : (
              <span className="italic text-muted-foreground">sugerindo sessão…</span>
            )}
            {nome && nome === t.sessao_sugerida && (
              <span className="shrink-0 rounded bg-primary/10 px-1.5 py-px text-[10px] font-medium text-primary" data-testid="atendimento-tarefa-sugerida">
                sugerida
              </span>
            )}
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <MenuDeSessoes sessoes={sessoes} valor={nome} sugerida={t.sessao_sugerida} onEscolher={onEscolher} testid="atendimento-tarefa-sessoes" />
      </DropdownMenu>
      <button
        type="button"
        onClick={() => void enviar()}
        disabled={!nome || enviando}
        className="flex h-8 shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        data-testid="atendimento-tarefa-enviar"
      >
        {enviando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <SendHorizontal className="h-3.5 w-3.5" />}
        Enviar
      </button>
    </div>
  );
}

// A tarefa aberta: o que o cartão mostra, a conversa com a sessão (como no painel do robozinho), o «Continuar…» e o
// Concluir (Reabrir na concluída). Aberta com resposta não lida, marca lida.
function TarefaAberta({
  id,
  resumo,
  sessoes,
  escolha,
  onEscolher,
  onMudou,
  onConversa,
  onVoltar,
  fechar,
  acoes,
}: {
  id: string;
  /** A tarefa como a lista mostra, enquanto a conversa dela carrega. */
  resumo: Tarefa | null;
  sessoes: Sessao[];
  escolha: string | null;
  onEscolher: (nome: string) => void;
  onMudou: (t: TarefaInteira) => void;
  onConversa: (t: Tarefa) => void;
  onVoltar: () => void;
  fechar: ReactNode;
  acoes: AcoesDaTarefa;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const rotas = useRotasDaTarefa();
  const detalhe = useQuery({
    queryKey: rotas.chave(id),
    queryFn: () => customFetch<TarefaInteira>(rotas.url(id)),
    placeholderData: resumo ? { ...resumo, mensagens: [] } : undefined,
    refetchInterval: 5_000,
    retry: false,
  });
  const t = detalhe.data ?? null;
  const naoLida = !!t?.nao_lida;
  useEffect(() => {
    if (!naoLida) return;
    const lida = <T extends Tarefa>(x: T): T => (x.id === id ? { ...x, nao_lida: false } : x);
    qc.setQueryData<{ tarefas: Tarefa[] }>(rotas.chaveLista, (antes) => antes && { tarefas: antes.tarefas.map(lida) });
    qc.setQueryData<TarefaInteira>(rotas.chave(id), (antes) => antes && lida(antes));
    customFetch(rotas.url(id, "/lido"), { method: "POST" })
      .then(() => {
        qc.invalidateQueries({ queryKey: rotas.chaveLista });
        qc.invalidateQueries({ queryKey: rotas.chave(id) });
      })
      .catch(() => {});
  }, [id, naoLida, qc]);
  const [editando, setEditando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [fazendo, setFazendo] = useState(false);
  const fazer = async (acao: "concluir" | "reabrir") => {
    setFazendo(true);
    try {
      onMudou(await customFetch<TarefaInteira>(rotas.url(id, `/${acao}`), { method: "POST" }));
    } catch (err) {
      toast({ title: mensagemDoErro(err, acao === "concluir" ? "Não foi possível concluir a tarefa." : "Não foi possível reabrir a tarefa."), variant: "destructive" });
    } finally {
      setFazendo(false);
    }
  };
  return (
    <>
      <div className="flex items-center gap-2 border-b px-3 py-2.5">
        <button type="button" onClick={onVoltar} className="-ml-1 rounded p-1 text-muted-foreground hover:text-foreground" aria-label="Tarefas" data-testid="atendimento-todo-voltar">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="min-w-0 truncate font-mono text-sm font-semibold">{id}</span>
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {t &&
            (t.status === "concluida" ? (
              <BotaoExcluir grande onClick={() => setExcluindo(true)} />
            ) : (
              <BotoesDaTarefa grande editando={editando} fixada={!!t.fixada} onFixar={() => acoes.fixar(id, !t.fixada)} onEditar={() => setEditando(true)} onNaoLida={() => acoes.marcarNaoLida(id)} onExcluir={() => setExcluindo(true)} />
            ))}
          {t && (
            <button
              type="button"
              onClick={() => void fazer(t.status === "concluida" ? "reabrir" : "concluir")}
              disabled={fazendo}
              className="flex h-8 items-center gap-1 rounded-lg border px-2.5 text-xs font-semibold hover:bg-muted disabled:opacity-60"
              data-testid={t.status === "concluida" ? "atendimento-tarefa-reabrir" : "atendimento-tarefa-concluir"}
            >
              {fazendo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t.status === "concluida" ? <RotateCcw className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
              {t.status === "concluida" ? "Reabrir" : "Concluir"}
            </button>
          )}
          {fechar}
        </div>
      </div>
      {excluindo && (
        <div className="border-b px-3 pb-2">
          <ConfirmarExclusao t={t} onCancelar={() => setExcluindo(false)} onConfirmar={() => acoes.excluir(id)} />
        </div>
      )}
      {!t ? (
        <p className="p-4 text-sm text-muted-foreground">{detalhe.isError ? mensagemDoErro(detalhe.error, "Não foi possível abrir a tarefa.") : "Carregando…"}</p>
      ) : (
        <>
          <div className="space-y-2 border-b px-4 py-3" data-testid="atendimento-tarefa-aberta">
            <TituloDaTarefa t={t} grande onMudou={onMudou} />
            <SelosDaTarefa t={t} />
            <ConversaDaTarefa t={t} onMudou={onMudou} onConversa={onConversa} />
            {editando && t.status !== "concluida" && (
              <EditarComentario
                t={t}
                onCancelar={() => setEditando(false)}
                onSalvar={async (mudancas) => {
                  if (await acoes.editar(id, mudancas)) setEditando(false);
                }}
              />
            )}
            <div className="flex flex-wrap items-center gap-2">
              <ChipDaTarefa t={t} />
              <SessaoDaTarefa t={t} sessoes={sessoes} escolha={escolha} onEscolher={onEscolher} onMudou={onMudou} />
            </div>
          </div>
          <FioDaTarefa t={t} carregando={detalhe.isPlaceholderData} />
          <FundoDaTarefa key={t.id} t={t} onMudou={onMudou} />
        </>
      )}
    </>
  );
}

// A conversa da tarefa: as falas para a sessão e as respostas dela e, na tarefa entre os dois, os comentários, na ordem em
// que foram escritos. O que é de quem está na página fica à direita; o do outro, à esquerda com o nome.
const COR_DA_PESSOA: Record<Pessoa, string> = { marina: "bg-teal-700", pedro: "bg-purple-600" };
function FioDaTarefa({ t, carregando }: { t: TarefaInteira; carregando: boolean }) {
  const { pessoa } = useRotasDaTarefa();
  const rolagem = useRef<HTMLDivElement>(null);
  const comentarios = t.comentarios ?? [];
  // Ao abrir e a cada fala nova, desce ao fim.
  useLayoutEffect(() => {
    const el = rolagem.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [t.id, t.mensagens.length, comentarios.length]);
  const itens = [
    ...t.mensagens.map((f, i) => ({ chave: `m-${f.criado_em}-${i}`, em: f.criado_em, f, c: null })),
    ...comentarios.map((c) => ({ chave: `c-${c.id}`, em: c.criado_em, f: null, c })),
  ].sort((a, b) => (a.em < b.em ? -1 : a.em > b.em ? 1 : 0));
  const avatar = (p: Pessoa) => (
    <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${COR_DA_PESSOA[p]}`} aria-hidden="true">
      {PESSOAS[p].letra}
    </span>
  );
  return (
    <div ref={rolagem} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3" data-testid="atendimento-tarefa-fio">
      {carregando && t.mensagens.length === 0 && <p className="text-sm text-muted-foreground">Carregando…</p>}
      {itens.map(({ chave, f, c }) => {
        if (c) {
          const meu = c.pessoa === pessoa;
          return meu ? (
            <div key={chave} className="flex justify-end" data-testid="atendimento-tarefa-comentario-meu">
              <div className="max-w-[85%]">
                <div className="mb-0.5 text-right text-[10px] text-muted-foreground">Comentário {PESSOAS[outraPessoa(pessoa)].paraEla}</div>
                <div className="whitespace-pre-wrap break-words rounded-2xl rounded-tr-sm bg-violet-100 px-3 py-2 text-sm dark:bg-violet-900/50"><TextoComImagens texto={c.texto} /></div>
              </div>
            </div>
          ) : (
            <div key={chave} className="flex gap-2" data-testid="atendimento-tarefa-comentario-outro">
              {avatar(c.pessoa)}
              <div className="min-w-0 max-w-[85%]">
                <div className="mb-0.5 text-[10px] font-semibold text-muted-foreground">{PESSOAS[c.pessoa].nome} · comentário</div>
                <div className="whitespace-pre-wrap break-words rounded-2xl rounded-tl-sm bg-violet-100 px-3 py-2 text-sm dark:bg-violet-900/50"><TextoComImagens texto={c.texto} /></div>
                <div className="mt-0.5 text-[10px] text-muted-foreground">{hora(c.criado_em)}</div>
              </div>
            </div>
          );
        }
        if (!f) return null;
        if (f.autor === "sessao") {
          return (
            <div key={chave} className="flex gap-2" data-testid="atendimento-tarefa-resposta">
              <Bot className="mt-1 h-4 w-4 shrink-0 text-primary" />
              <div className="min-w-0 flex-1 break-words">
                <Markdown texto={f.texto} />
                <div className="mt-0.5 text-[10px] text-muted-foreground">{hora(f.criado_em)}</div>
              </div>
            </div>
          );
        }
        const quem = f.pessoa ?? "marina";
        return quem === pessoa ? (
          <div key={chave} className="flex justify-end" data-testid="atendimento-tarefa-pergunta">
            <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-tr-sm bg-primary/10 px-3 py-2 text-sm"><TextoComImagens texto={f.texto} /></div>
          </div>
        ) : (
          <div key={chave} className="flex gap-2" data-testid="atendimento-tarefa-pergunta-outro">
            {avatar(quem)}
            <div className="min-w-0 max-w-[85%]">
              <div className="mb-0.5 text-[10px] font-semibold text-muted-foreground">{PESSOAS[quem].nome}</div>
              <div className="whitespace-pre-wrap break-words rounded-2xl rounded-tl-sm bg-primary/10 px-3 py-2 text-sm"><TextoComImagens texto={f.texto} /></div>
            </div>
          </div>
        );
      })}
      {(t.status === "na_fila" || t.status === "entregue") && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="atendimento-tarefa-esperando">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {t.status === "entregue" ? "A sessão está respondendo…" : "Na fila da sessão…"}
        </div>
      )}
    </div>
  );
}

// O campo de baixo da tarefa aberta: o «Continuar…» para a sessão (depois de enviada) e, na tarefa entre os dois, o
// comentário para o outro, em duas abas (Marina, 31/10/2026: o comentário de um chega ao outro e não vai à sessão).
function FundoDaTarefa({ t, onMudou }: { t: TarefaInteira; onMudou: (t: TarefaInteira) => void }) {
  const { pessoa } = useRotasDaTarefa();
  const comSessao = !!t.enviada_em;
  const [aba, setAba] = useState<"sessao" | "comentario">(() => (comSessao && !t.comentario_novo ? "sessao" : "comentario"));
  if (!compartilhada(t)) return comSessao ? <CampoDaTarefa key="sessao" t={t} onMudou={onMudou} modo="sessao" /> : null;
  const efetiva = comSessao ? aba : "comentario";
  const estilo = (ativa: boolean, cor: string) => `rounded-full px-2.5 py-1 ${ativa ? cor : "border text-muted-foreground hover:bg-muted"}`;
  return (
    <div className="border-t" data-testid="atendimento-tarefa-fundo">
      <div className="flex flex-wrap gap-1.5 px-3 pt-2 text-xs font-semibold" role="tablist">
        {comSessao && (
          <button type="button" role="tab" aria-selected={efetiva === "sessao"} onClick={() => setAba("sessao")} className={estilo(efetiva === "sessao", "bg-foreground text-background")} data-testid="atendimento-tarefa-aba-sessao">
            Para a sessão
          </button>
        )}
        <button type="button" role="tab" aria-selected={efetiva === "comentario"} onClick={() => setAba("comentario")} className={estilo(efetiva === "comentario", "bg-violet-600 text-white")} data-testid="atendimento-tarefa-aba-comentario">
          Comentário {PESSOAS[outraPessoa(pessoa)].paraEla}
        </button>
      </div>
      <CampoDaTarefa key={efetiva} t={t} onMudou={onMudou} modo={efetiva} borda={false} />
    </div>
  );
}

// O campo do «Continuar…» (para a sessão; na concluída, reabre) ou do comentário (para o outro; não vai à sessão). O
// rascunho fica por tarefa.
function CampoDaTarefa({ t, onMudou, modo, borda = true }: { t: TarefaInteira; onMudou: (t: TarefaInteira) => void; modo: "sessao" | "comentario"; borda?: boolean }) {
  const rotas = useRotasDaTarefa();
  const outra = PESSOAS[outraPessoa(rotas.pessoa)];
  const comentario = modo === "comentario";
  const chaveDoTexto = comentario
    ? `central-tarefa-comentario-${rotas.pessoa}:${t.id}`
    : rotas.pessoa === "marina"
      ? `central-tarefa-continuar:${t.id}`
      : `central-tarefa-continuar-${rotas.pessoa}:${t.id}`;
  const [texto, setTexto] = useState(() => lerTexto(chaveDoTexto));
  useEffect(() => guardarTexto(chaveDoTexto, texto), [chaveDoTexto, texto]);
  const campo = useRef<HTMLTextAreaElement>(null);
  const correcao = useCorrecao(texto, setTexto, campo);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const ditado = useDitadoNoCampo(texto, setTexto, campo, setErro);
  const anexos = useAnexosColados(setErro);
  const destino = comentario ? outra.paraEla : `à ${t.sessao_rotulo ?? "sessão"}`;
  const enviar = async () => {
    const x = anexos.junto(texto);
    if (!x || enviando || anexos.subindo) return;
    setEnviando(true);
    setErro(null);
    try {
      onMudou(await customFetch<TarefaInteira>(rotas.url(t.id, comentario ? "/comentarios" : "/mensagens"), { method: "POST", body: JSON.stringify({ texto: x }) }));
      setTexto("");
      anexos.limpar();
    } catch (err) {
      setErro(mensagemDoErro(err, comentario ? "Não foi possível enviar o comentário." : `Não foi possível enviar ${destino}.`));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <div className={`${borda ? "border-t " : ""}px-3 py-2.5`}>
      {erro && (
        <p className="mb-2 text-sm text-destructive" role="alert">
          {erro}
        </p>
      )}
      <div className="mb-2 empty:mb-0">
        <MiniaturasColadas anexos={anexos.anexos} onTirar={anexos.tirar} />
      </div>
      <div className="flex items-end gap-2">
        <textarea
          ref={campo}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          {...correcao}
          readOnly={ditado.ativo}
          onPaste={anexos.colar}
          onKeyDown={(e) => {
            correcao.onKeyDown(e);
            if (e.defaultPrevented) return;
            if (ehCtrlEnter(e)) {
              e.preventDefault();
              if (!ditado.ativo) void enviar();
            }
          }}
          rows={2}
          placeholder={comentario ? `Comentário ${outra.paraEla}…` : `Continuar com ${comASessao(t.sessao_rotulo)}…${t.status === "concluida" ? " (reabre a tarefa)" : ""}`}
          className="max-h-40 min-h-10 min-w-0 flex-1 resize-none rounded-2xl border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
          data-testid={comentario ? "atendimento-tarefa-comentar" : "atendimento-tarefa-continuar"}
        />
        <BotaoDeImagem onEscolher={anexos.escolher} desabilitado={ditado.ativo} tamanho="redondo" testid={comentario ? "atendimento-tarefa-comentar-imagem" : "atendimento-tarefa-continuar-imagem"} />
        <BotaoDoDitado estado={ditado.estado} onClick={ditado.alternar} redondo testid={comentario ? "atendimento-tarefa-comentar-ditar" : "atendimento-tarefa-continuar-ditar"} />
        <button
          type="button"
          onClick={() => void enviar()}
          disabled={(!texto.trim() && !anexos.temImagem) || enviando || ditado.ativo || anexos.subindo}
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-primary-foreground disabled:opacity-50 ${comentario ? "bg-violet-600 hover:bg-violet-700" : "bg-primary hover:bg-primary/90"}`}
          aria-label={comentario ? `Enviar o comentário ${outra.paraEla}` : `Enviar ${destino}`}
          data-testid={comentario ? "atendimento-tarefa-comentar-enviar" : "atendimento-tarefa-continuar-enviar"}
        >
          {enviando ? <Loader2 className="h-5 w-5 animate-spin" /> : <SendHorizontal className="h-5 w-5" />}
        </button>
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">{ditado.ativo ? <AvisoDoDitado estado={ditado.estado} /> : "Ctrl + Enter envia"}</p>
    </div>
  );
}

// Markdown simples da resposta: parágrafos, listas, blocos de código, `código` e **negrito** (títulos viram negrito).
// Só elementos do React: nada do texto vira HTML.
const ITEM_DE_LISTA = /^\s*([-*•]|\d+[.)])\s+/;
function Markdown({ texto }: { texto: string }) {
  const linhas = texto.replace(/\r\n/g, "\n").split("\n");
  const blocos: ReactNode[] = [];
  let i = 0;
  while (i < linhas.length) {
    const l = linhas[i]!;
    if (l.trim().startsWith("```")) {
      const corpo: string[] = [];
      i++;
      while (i < linhas.length && !linhas[i]!.trim().startsWith("```")) corpo.push(linhas[i++]!);
      i++;
      blocos.push(
        <pre key={blocos.length} className="my-2 whitespace-pre-wrap break-words rounded-md bg-muted px-3 py-2 font-mono text-xs">
          {corpo.join("\n")}
        </pre>,
      );
    } else if (ITEM_DE_LISTA.test(l)) {
      const ordenada = /^\s*\d/.test(l);
      const itens: string[] = [];
      while (i < linhas.length && ITEM_DE_LISTA.test(linhas[i]!)) itens.push(linhas[i++]!.replace(ITEM_DE_LISTA, ""));
      const Lista = ordenada ? "ol" : "ul";
      blocos.push(
        <Lista key={blocos.length} className={`my-1.5 space-y-0.5 pl-5 ${ordenada ? "list-decimal" : "list-disc"}`}>
          {itens.map((t, j) => (
            <li key={j}>{emLinha(t)}</li>
          ))}
        </Lista>,
      );
    } else if (!l.trim()) {
      i++;
    } else {
      const par: string[] = [];
      while (i < linhas.length && linhas[i]!.trim() && !linhas[i]!.trim().startsWith("```") && !ITEM_DE_LISTA.test(linhas[i]!)) par.push(linhas[i++]!);
      const titulo = par.length === 1 && /^#{1,6}\s/.test(par[0]!);
      blocos.push(
        <p key={blocos.length} className={`my-1.5 whitespace-pre-wrap ${titulo ? "font-semibold" : ""}`}>
          {emLinha(titulo ? par[0]!.replace(/^#{1,6}\s+/, "") : par.join("\n"))}
        </p>,
      );
    }
  }
  return <div className="text-sm leading-relaxed">{blocos}</div>;
}

function emLinha(t: string): ReactNode[] {
  return t.split(/(`[^`\n]+`|\*\*[^*\n]+\*\*)/g).map((p, i) =>
    p.length > 2 && p.startsWith("`") && p.endsWith("`") ? (
      <code key={i} className="rounded bg-muted px-1 font-mono text-[0.85em]">
        {p.slice(1, -1)}
      </code>
    ) : p.length > 4 && p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i}>{p.slice(2, -2)}</strong>
    ) : (
      p
    ),
  );
}

// Foto, áudio, vídeo e documento pelo link da UAZAPI, que guarda o arquivo por 2 dias: depois, fica a transcrição ou a
// descrição que o balão já mostra.
function Midia({ jid, m }: { jid: string; m: Mensagem }) {
  const canal = useContext(CanalDaConversa);
  const [ref, visivel] = useVisivel<HTMLDivElement>();
  const [grande, setGrande] = useState(false);
  const arquivo = useQuery({
    queryKey: ["atendimento-midia", canal.chave + jid, m.messageid],
    queryFn: () => customFetch<{ url: string; mimetype: string | null }>(`${canal.base}/midia?jid=${encodeURIComponent(jid)}&messageid=${encodeURIComponent(m.messageid)}`),
    enabled: visivel,
    retry: false,
    staleTime: 30 * 60_000,
  });
  const url = arquivo.data?.url ?? null;
  return (
    <div ref={ref} data-testid="atendimento-midia">
      {!url ? (
        <div className="text-muted-foreground">{arquivo.isLoading || !visivel ? <Loader2 className="inline h-3.5 w-3.5 animate-spin" /> : null} {rotuloDaMidia(m.tipo)}</div>
      ) : ehImagem(m.tipo) ? (
        <>
          <button type="button" onClick={() => setGrande(true)} className="block" aria-label="Abrir a imagem">
            <img src={url} alt="" className={`rounded ${m.tipo === "StickerMessage" ? "h-28 w-28 object-contain" : "max-h-60 max-w-[220px] object-cover"}`} />
          </button>
          <DialogPrimitive.Root open={grande} onOpenChange={setGrande}>
            <DialogPrimitive.Portal>
              <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80" />
              <DialogPrimitive.Content className="fixed inset-4 z-50 flex items-center justify-center focus:outline-none" aria-describedby={undefined}>
                <DialogPrimitive.Title className="sr-only">Imagem</DialogPrimitive.Title>
                <img src={url} alt="" className="max-h-full max-w-full rounded object-contain" />
                <DialogPrimitive.Close className="absolute right-0 top-0 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white" aria-label="Fechar">
                  <X className="h-5 w-5" />
                </DialogPrimitive.Close>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          </DialogPrimitive.Root>
        </>
      ) : m.tipo === "AudioMessage" ? (
        <audio controls preload="metadata" src={url} className="h-10 w-64 max-w-full" />
      ) : m.tipo === "VideoMessage" ? (
        <video controls preload="metadata" src={url} className="max-h-72 max-w-[260px] rounded" />
      ) : (
        <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary underline">
          <FileText className="h-4 w-4" /> Abrir o documento
        </a>
      )}
    </div>
  );
}

function BalaoPendente({ p }: { p: Pendente }) {
  return (
    <div className="my-1 flex justify-end" data-testid="atendimento-enviando">
      <div className="max-w-[85%] rounded-lg rounded-tr-sm bg-emerald-50/70 px-3 py-1.5 text-sm shadow-sm dark:bg-emerald-950/30 md:max-w-[70%]">
        <div className="mb-0.5 text-[11px] font-semibold text-muted-foreground">Você</div>
        {p.previa && <img src={p.previa} alt="" className="mb-1 max-h-60 max-w-[220px] rounded object-cover opacity-80" />}
        {p.tipo === "audio" && <div>🎤 Áudio {p.duracaoMs !== null ? tempo(p.duracaoMs) : ""}</div>}
        {p.texto && <div className="whitespace-pre-wrap break-words">{p.texto}</div>}
        <div className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> enviando…
        </div>
      </div>
    </div>
  );
}

const FORMATOS_DE_AUDIO = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/webm"];

function mensagemDoMicrofone(err: unknown): string {
  const nome = (err as { name?: string })?.name;
  if (nome === "NotAllowedError" || nome === "SecurityError") {
    return "O navegador bloqueou o microfone. Libere o microfone deste site (no cadeado da barra de endereço) e tente de novo.";
  }
  if (nome === "NotFoundError" || nome === "OverconstrainedError") return "Nenhum microfone encontrado neste computador.";
  if (nome === "NotReadableError") return "O microfone está em uso por outro programa.";
  if (nome === "NotSupportedError") return "Este navegador não deixa gravar áudio nesta página.";
  return "Não foi possível usar o microfone.";
}

// Texto (Enter envia, Shift+Enter quebra a linha), áudio gravado (🎤, ✕ cancela, ➤ envia) e imagem colada (Ctrl+V; o
// texto do campo vira a legenda).
function Compositor({
  jid,
  erro,
  setErro,
  onEnviou,
  citando,
  nomeDoCliente,
  onSemCitacao,
}: {
  jid: string;
  erro: string | null;
  setErro: (e: string | null) => void;
  onEnviou: (p: Omit<Pendente, "id" | "desde" | "conhecidas">) => void;
  /** A mensagem que o próximo envio responde (a faixa em cima do campo). */
  citando: Citada | null;
  nomeDoCliente: string;
  /** Cancelou a citação (✕ ou Esc com o campo vazio) ou a resposta saiu. */
  onSemCitacao: () => void;
}) {
  const canal = useContext(CanalDaConversa);
  // O rascunho desta conversa volta no campo; cada letra fica guardada (só o texto: foto e áudio não).
  const guardada = canal.chave + jid;
  const [texto, setTexto] = useState(() => lerRascunho(guardada));
  useEffect(() => {
    guardarRascunho(guardada, texto);
    if (!texto.trim()) window.dispatchEvent(new Event(AVISO_DE_RASCUNHO));
  }, [guardada, texto]);
  useEffect(() => () => void window.dispatchEvent(new Event(AVISO_DE_RASCUNHO)), []);
  const [imagem, setImagem] = useState<{ arquivo: File; previa: string } | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [gravando, setGravando] = useState<{ inicio: number } | null>(null);
  const [agora, setAgora] = useState(Date.now());
  const campo = useRef<HTMLTextAreaElement>(null);
  // Correção automática de ortografia e acentos no campo (só no computador).
  const correcaoDoCampo = useCorrecao(texto, setTexto, campo);
  const gravador = useRef<{ rec: MediaRecorder; partes: Blob[]; stream: MediaStream; tipo: string } | null>(null);
  // Escolheu uma mensagem para responder: o foco vai para o campo (a citação que volta ao recarregar, não).
  const citadaAntes = useRef(citando?.messageid ?? null);
  useEffect(() => {
    const id = citando?.messageid ?? null;
    if (id && id !== citadaAntes.current) campo.current?.focus();
    citadaAntes.current = id;
  }, [citando?.messageid]);

  // O campo cresce com o texto, até umas 6 linhas.
  useLayoutEffect(() => {
    const el = campo.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
  }, [texto]);

  useEffect(() => {
    if (!gravando) return;
    const t = setInterval(() => setAgora(Date.now()), 250);
    return () => clearInterval(t);
  }, [gravando]);

  // Ao sair da conversa no meio de uma gravação, o microfone desliga.
  useEffect(
    () => () => {
      gravador.current?.stream.getTracks().forEach((t) => t.stop());
      if (imagem) URL.revokeObjectURL(imagem.previa);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const tirarImagem = () => {
    if (imagem) URL.revokeObjectURL(imagem.previa);
    setImagem(null);
  };

  const enviar = async () => {
    const t = texto.trim();
    if (enviando || (!t && !imagem)) return;
    setEnviando(true);
    setErro(null);
    try {
      if (imagem) {
        const fd = new FormData();
        fd.append("jid", jid);
        fd.append("imagem", imagem.arquivo, imagem.arquivo.name || "imagem.png");
        if (t) fd.append("legenda", t);
        if (citando) fd.append("responder_a", citando.messageid);
        const r = await customFetch<Enviada>(`${canal.base}/enviar-imagem`, { method: "POST", body: fd });
        onEnviou({ messageid: r.messageid, tipo: "imagem", texto: t || null, previa: imagem.previa, duracaoMs: null });
        setImagem(null);
      } else {
        const r = await customFetch<Enviada>(`${canal.base}/enviar`, {
          method: "POST",
          body: JSON.stringify({ jid, texto: t, ...(citando ? { responder_a: citando.messageid } : {}) }),
        });
        onEnviou({ messageid: r.messageid, tipo: "texto", texto: t, previa: null, duracaoMs: null });
      }
      if (citando) onSemCitacao();
      setTexto("");
      campo.current?.focus();
    } catch (err) {
      setErro(mensagemDoErro(err, "Não foi possível enviar."));
    } finally {
      setEnviando(false);
    }
  };

  const colar = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const item = [...e.clipboardData.items].find((i) => i.kind === "file" && i.type.startsWith("image/"));
    const arquivo = item?.getAsFile();
    if (!arquivo) return;
    e.preventDefault();
    if (imagem) URL.revokeObjectURL(imagem.previa);
    setImagem({ arquivo, previa: URL.createObjectURL(arquivo) });
    setErro(null);
  };

  const gravar = async () => {
    setErro(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const tipo = FORMATOS_DE_AUDIO.find((f) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(f)) ?? "";
      const rec = new MediaRecorder(stream, tipo ? { mimeType: tipo } : undefined);
      const partes: Blob[] = [];
      rec.ondataavailable = (ev) => {
        if (ev.data.size > 0) partes.push(ev.data);
      };
      rec.start();
      gravador.current = { rec, partes, stream, tipo: rec.mimeType || tipo || "audio/webm" };
      setAgora(Date.now());
      setGravando({ inicio: Date.now() });
    } catch (err) {
      setErro(mensagemDoMicrofone(err));
    }
  };

  const pararGravacao = () =>
    new Promise<{ blob: Blob; tipo: string } | null>((resolve) => {
      const g = gravador.current;
      gravador.current = null;
      if (!g) return resolve(null);
      g.rec.onstop = () => {
        g.stream.getTracks().forEach((t) => t.stop());
        resolve({ blob: new Blob(g.partes, { type: g.tipo }), tipo: g.tipo });
      };
      g.rec.stop();
    });

  const cancelarGravacao = async () => {
    setGravando(null);
    await pararGravacao();
  };

  const enviarAudio = async () => {
    if (!gravando) return;
    const duracaoMs = Date.now() - gravando.inicio;
    setGravando(null);
    const gravado = await pararGravacao();
    if (!gravado || gravado.blob.size === 0) {
      setErro("A gravação saiu vazia. Tente de novo.");
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      const ext = gravado.tipo.includes("ogg") ? "ogg" : "webm";
      const fd = new FormData();
      fd.append("jid", jid);
      fd.append("duracao_ms", String(duracaoMs));
      fd.append("arquivo", new File([gravado.blob], `audio.${ext}`, { type: gravado.tipo }));
      if (citando) fd.append("responder_a", citando.messageid);
      const r = await customFetch<Enviada>(`${canal.base}/enviar-audio`, { method: "POST", body: fd });
      onEnviou({ messageid: r.messageid, tipo: "audio", texto: null, previa: null, duracaoMs });
      if (citando) onSemCitacao();
    } catch (err) {
      setErro(mensagemDoErro(err, "Não foi possível enviar o áudio."));
    } finally {
      setEnviando(false);
    }
  };

  const podeEnviar = !enviando && (!!texto.trim() || !!imagem);
  return (
    <div className="space-y-2 border-t bg-background px-3 py-2.5">
      {erro && (
        <div className="flex items-start gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert" data-testid="atendimento-erro">
          <span className="min-w-0 flex-1">{erro}</span>
          <button type="button" onClick={() => setErro(null)} aria-label="Fechar o aviso" className="shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {citando && (
        <div className="flex items-start gap-2" data-testid="atendimento-citando">
          <div className="min-w-0 flex-1">
            <BlocoCitado citacao={citando} nomeDoCliente={nomeDoCliente} />
          </div>
          <button
            type="button"
            onClick={onSemCitacao}
            title="Cancelar a resposta"
            aria-label="Cancelar a resposta"
            className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            data-testid="atendimento-citando-cancelar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {imagem && (
        <div className="flex items-end gap-3" data-testid="atendimento-imagem-colada">
          <div className="relative">
            <img src={imagem.previa} alt="Imagem colada" className="h-24 max-w-[160px] rounded-lg border object-cover" />
            <button type="button" onClick={tirarImagem} className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white" aria-label="Tirar a imagem" data-testid="atendimento-tirar-imagem">
              <X className="h-3 w-3" />
            </button>
          </div>
          <p className="pb-1 text-xs text-muted-foreground">O texto do campo vai como legenda.</p>
        </div>
      )}
      {gravando ? (
        <div className="flex items-center gap-2" data-testid="atendimento-gravando">
          <button type="button" onClick={() => void cancelarGravacao()} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-foreground hover:bg-muted/70" aria-label="Cancelar a gravação" data-testid="atendimento-cancelar-audio">
            <X className="h-5 w-5" />
          </button>
          <div className="flex h-10 min-w-0 flex-1 items-center gap-3 rounded-full border bg-muted/30 px-4">
            <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-red-600" />
            <span className="text-sm font-semibold tabular-nums" data-testid="atendimento-tempo">
              {tempo(agora - gravando.inicio)}
            </span>
            <span className="truncate text-xs text-muted-foreground">Gravando…</span>
          </div>
          <button type="button" onClick={() => void enviarAudio()} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90" aria-label="Enviar o áudio" data-testid="atendimento-enviar-audio">
            <SendHorizontal className="h-5 w-5" />
          </button>
        </div>
      ) : (
        <div className="flex items-end gap-2">
          <textarea
            ref={campo}
            rows={1}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onPaste={colar}
            {...correcaoDoCampo}
            onKeyDown={(e) => {
              correcaoDoCampo.onKeyDown(e);
              if (e.defaultPrevented) return;
              if (e.key === "Escape" && citando && !texto.trim()) {
                e.preventDefault();
                onSemCitacao();
                return;
              }
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void enviar();
              }
            }}
            placeholder={imagem ? "Legenda (opcional)" : canal.pessoal && canal.rotulo ? `Mensagem pelo ${canal.rotulo}` : "Mensagem"}
            className="max-h-[150px] min-h-10 min-w-0 flex-1 resize-none rounded-2xl border bg-muted/30 px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            data-testid="atendimento-campo"
          />
          <BotaoDeImagem
            onEscolher={(arquivos) => {
              const arquivo = arquivos.find((f) => f.type.startsWith("image/"));
              if (!arquivo) return;
              if (imagem) URL.revokeObjectURL(imagem.previa);
              setImagem({ arquivo, previa: URL.createObjectURL(arquivo) });
              setErro(null);
              campo.current?.focus();
            }}
            varias={false}
            desabilitado={enviando}
            titulo="Enviar imagem"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-foreground hover:bg-muted/70 disabled:opacity-50"
            testid="atendimento-imagem"
          />
          <button type="button" onClick={() => void gravar()} disabled={enviando || !!imagem} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-foreground hover:bg-muted/70 disabled:opacity-50" aria-label="Gravar áudio" data-testid="atendimento-gravar">
            <Mic className="h-5 w-5" />
          </button>
          <button type="button" onClick={() => void enviar()} disabled={!podeEnviar} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50" aria-label="Enviar" data-testid="atendimento-enviar">
            {enviando ? <Loader2 className="h-5 w-5 animate-spin" /> : <SendHorizontal className="h-5 w-5" />}
          </button>
        </div>
      )}
    </div>
  );
}
